import { z } from 'zod';

// src/domain/roles.ts
var ROLE_ORDER = ["GUEST", "EMPLOYEE", "MANAGER", "ADMIN", "OWNER"];
var RoleSchema = z.enum(ROLE_ORDER);
function roleRank(role) {
  return ROLE_ORDER.indexOf(role);
}
function roleAtLeast(role, required) {
  return roleRank(role) >= roleRank(required);
}
var DUCK_COLORS = [
  // Warm.
  "yellow",
  "orange",
  "red",
  "peach",
  "pink",
  // Cool / jewel.
  "lavender",
  "purple",
  "blue",
  "teal",
  "mint",
  "green",
  // Neutrals (light → dark).
  "cream",
  "brown",
  "grey",
  "white",
  "black"
];
var DuckColorSchema = z.enum(DUCK_COLORS);
var DUCK_SPECIES = [
  "duck",
  "frog",
  "cat",
  "fox",
  "penguin",
  "pig",
  "axolotl",
  "capybara",
  "hamster",
  "dino",
  // ── Wave 2 (the unhinged & delightful) — same shared rig, same procedural tech (ADR-0036). ──
  "goose",
  "shiba",
  "sloth",
  "raccoon",
  "owl",
  "bee",
  "crab",
  "shark",
  "blobfish",
  "cthulhu"
];
var DuckSpeciesSchema = z.enum(DUCK_SPECIES);
var DEFAULT_DUCK_SPECIES = "duck";
var SPECIES_META = {
  duck: { id: "duck", label: "Duck", emoji: "\u{1F986}", defaultColor: "yellow", blurb: "The original. Waddles in like they own the place." },
  frog: { id: "frog", label: "Frog", emoji: "\u{1F438}", defaultColor: "green", blurb: "Wide grin, wider eyes. Ribbits encouragement." },
  cat: { id: "cat", label: "Cat", emoji: "\u{1F431}", defaultColor: "brown", blurb: "Tolerates you. Mostly. Naps on the keyboard." },
  fox: { id: "fox", label: "Fox", emoji: "\u{1F98A}", defaultColor: "brown", blurb: "Sly little grin, fluffy tail. Definitely up to something." },
  penguin: { id: "penguin", label: "Penguin", emoji: "\u{1F427}", defaultColor: "black", blurb: "Dressed for a meeting that is also a waddle." },
  pig: { id: "pig", label: "Pig", emoji: "\u{1F437}", defaultColor: "pink", blurb: "Snorts when it laughs. Laughs a lot." },
  axolotl: { id: "axolotl", label: "Axolotl", emoji: "\u{1F98E}", defaultColor: "pink", blurb: "Perma-smile, frilly gills. Eternally, gloriously delighted." },
  capybara: { id: "capybara", label: "Capybara", emoji: "\u{1F9AB}", defaultColor: "brown", blurb: "Unbothered. Zen. The vibe everyone aspires to." },
  hamster: { id: "hamster", label: "Hamster", emoji: "\u{1F439}", defaultColor: "yellow", blurb: "Cheeks full of snacks and big ambitions." },
  dino: { id: "dino", label: "Dino", emoji: "\u{1F996}", defaultColor: "green", blurb: "Tiny arms, huge grin. Rawr means hello." },
  // ── Wave 2 — the unhinged, the goofy, the deeply relatable (ADR-0036). ──
  goose: { id: "goose", label: "Goose", emoji: "\u{1FABF}", defaultColor: "white", blurb: "HONK. Chaos incarnate. Stole your stapler, no regrets." },
  shiba: { id: "shiba", label: "Shiba", emoji: "\u{1F415}", defaultColor: "brown", blurb: "Much work. Very office. So wow. Curly-tail menace." },
  sloth: { id: "sloth", label: "Sloth", emoji: "\u{1F9A5}", defaultColor: "brown", blurb: "Will reply to your message. Eventually. No rush, ever." },
  raccoon: { id: "raccoon", label: "Raccoon", emoji: "\u{1F99D}", defaultColor: "black", blurb: "Trash panda. Snack bandit. Found your secret cookies." },
  owl: { id: "owl", label: "Owl", emoji: "\u{1F989}", defaultColor: "brown", blurb: "Night-shift legend. Big eyes, bigger opinions. Whoo?" },
  bee: { id: "bee", label: "Bee", emoji: "\u{1F41D}", defaultColor: "yellow", blurb: "Busy busy busy. Pollinating good vibes. Bzzz." },
  crab: { id: "crab", label: "Crab", emoji: "\u{1F980}", defaultColor: "pink", blurb: "Sidles into standup. Mildly crabby. Pinches with love." },
  shark: { id: "shark", label: "Shark", emoji: "\u{1F988}", defaultColor: "blue", blurb: "Toothy derp. Just a friend-shaped cuddle predator." },
  blobfish: { id: "blobfish", label: "Blobfish", emoji: "\u{1FAE0}", defaultColor: "pink", blurb: "The living embodiment of Monday. Relatable. Droopy. Iconic." },
  cthulhu: { id: "cthulhu", label: "Cthulhu", emoji: "\u{1F419}", defaultColor: "green", blurb: "The intern from the deep. Tentacle face. Ph\u2019nglui mglw\u2019hello." }
};
var SPECIES_LIST = DUCK_SPECIES.map((id) => SPECIES_META[id]);
var DuckAppearanceSchema = z.object({
  color: DuckColorSchema,
  /** Cosmetic item ids by slot. Empty in M1; populated by the inventory/economy contexts later. */
  hat: z.string().nullable().default(null),
  glasses: z.string().nullable().default(null),
  outfit: z.string().nullable().default(null)
});
var DEFAULT_DUCK_APPEARANCE = {
  color: "yellow",
  hat: null,
  glasses: null,
  outfit: null
};
var ACTIVITY_STATES = [
  "IDLE",
  "WALKING",
  "WORKING",
  "COFFEE_BREAK",
  "MEETING",
  "AFK",
  "SLEEPING"
];
var ActivitySchema = z.enum(ACTIVITY_STATES);
var ANIMATION_STATES = [
  "idle",
  "walk",
  "sit",
  "sleep",
  "dance",
  "wave",
  "laugh",
  "coffee",
  "type",
  // Zany one-shot emotes (M7+) — each has a distinct motion signature so they read at a glance.
  "spin",
  "jump",
  "panic",
  "headbang",
  "wobble",
  "vibe"
];
var AnimationSchema = z.enum(ANIMATION_STATES);
var REPUTATION_DAILY_BUDGET = 100;
var REPUTATION_DEFAULT_GRANT = 10;
var REPUTATION_PER_PAIR_DAILY_CAP = 40;
var POSITIVE_ACTIONS = {
  HELP_TEAMMATE: { xp: 25, influence: 15, coins: 10, label: "Helped a teammate" },
  HOST_KNOWLEDGE_SESSION: { xp: 60, influence: 40, coins: 25, label: "Hosted a knowledge session" },
  SHARE_KNOWLEDGE: { xp: 20, influence: 12, coins: 8, label: "Shared useful knowledge" },
  JOIN_EVENT: { xp: 15, influence: 10, coins: 6, label: "Joined an office event" },
  // Influence-only since the gamified-XP redesign: celebrating a release lifts the GUILD, but a click
  // no longer mints the clicker personal XP/coins (those are earned by real participation + co-op quests).
  CELEBRATE_RELEASE: { xp: 0, influence: 50, coins: 0, label: "Celebrated a release" },
  GREET_NEWCOMER: { xp: 15, influence: 10, coins: 6, label: "Greeted a newcomer" },
  // Influence-only since the gamified-XP redesign (same reasoning as CELEBRATE_RELEASE). The achievable
  // co-op "quests" that grant personal XP now live in OFFICE_QUESTS (./xp-economy.ts), server-detected.
  COMPLETE_QUEST: { xp: 0, influence: 20, coins: 0, label: "Completed a quest" },
  PLAY_MINIGAME: { xp: 10, influence: 8, coins: 4, label: "Played a social mini-game" }
};
var LEGACY_FREE_BUTTON_ACTIONS = ["COMPLETE_QUEST", "CELEBRATE_RELEASE"];
var WELCOME_COINS = 1e4;
var COINS_DAILY_EARN_CAP = 500;
var POSITIVE_ACTION_XP_DAILY_CAP = 1e3;
var PositiveActionSchema = z.enum(
  Object.keys(POSITIVE_ACTIONS)
);
var GiveReputationSchema = z.object({
  toUserId: z.string().uuid(),
  amount: z.number().int().min(1).max(REPUTATION_DAILY_BUDGET),
  /** Optional thank-you note shown to the recipient. */
  reason: z.string().trim().max(140).optional()
});
var RecordActionSchema = z.object({
  action: PositiveActionSchema
});
var InternalGrantReputationSchema = z.object({
  fromUserId: z.string().uuid(),
  toUserId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  amount: z.number().int().min(1).max(REPUTATION_DAILY_BUDGET)
});
var InternalGrantActionSchema = z.object({
  userId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  action: PositiveActionSchema
});
function xpLevel(xp) {
  return Math.max(1, Math.floor(Math.sqrt(Math.max(0, xp) / 100)) + 1);
}
function guildLevel(influence) {
  return Math.max(1, Math.floor(Math.sqrt(Math.max(0, influence) / 250)) + 1);
}
var PARTICIPATION_XP_PER_MINUTE = 10;
var PARTICIPATION_FLUSH_MS = 6e4;
var PARTICIPATION_XP_DAILY_CAP = 1500;
var OFFICE_QUESTS = {
  /**
   * WORK — easy + small + repeatable. While seated at a WORKSTATION desk seat, a "work" tick grants a
   * tiny +1 XP on a server-enforced cooldown (see {@link WORK_COOLDOWN_MS}). Deliberately small: it's
   * the gentle "I'm heads-down at my desk" beat, not a grind. Solo-friendly (no quorum) but bounded.
   */
  WORK: {
    id: "WORK",
    name: "Heads-down work",
    goal: "Sit at a workstation desk and get some work done",
    tier: "easy",
    xp: 1,
    influence: 1,
    coins: 0,
    icon: "\u{1F4BB}"
  },
  /**
   * COFFEE_BREAK — 2+ ducks together in the kitchen/lounge, sustained briefly. Rewards the small,
   * human "let's grab a coffee together" moment. Server-detected from zone + proximity; distinct users.
   */
  COFFEE_BREAK: {
    id: "COFFEE_BREAK",
    name: "Coffee break",
    goal: "Hang out with a teammate in the kitchen or lounge",
    tier: "standard",
    xp: 20,
    influence: 12,
    coins: 6,
    icon: "\u2615"
  },
  /**
   * WELCOME_WAGON — be near a brand-new duck (joined moments ago) and greet them. Rewards making
   * newcomers feel they belong from minute one (Community Manager). Server-detected: proximity to a
   * freshly-joined teammate; the greeter and the newcomer are distinct users.
   */
  WELCOME_WAGON: {
    id: "WELCOME_WAGON",
    name: "Welcome wagon",
    goal: "Walk up and greet a brand-new teammate",
    tier: "standard",
    xp: 25,
    influence: 15,
    coins: 8,
    icon: "\u{1F44B}"
  },
  /**
   * TOUR_THE_OFFICE — visit all 10 rooms in one session. A gentle exploration quest that gets new
   * ducks moving through the whole world. Server-tracked from the authoritative zone the duck stands in.
   */
  TOUR_THE_OFFICE: {
    id: "TOUR_THE_OFFICE",
    name: "Tour the office",
    goal: "Explore every room in the office",
    tier: "standard",
    xp: 30,
    influence: 18,
    coins: 10,
    icon: "\u{1F5FA}\uFE0F"
  },
  /**
   * BRAINSTORM — 2+ ducks gathered at the whiteboard/TV in the meeting room (standing, not seated),
   * sustained briefly. The lighter, stand-up "let's sketch this out" cousin of a full meeting.
   */
  BRAINSTORM: {
    id: "BRAINSTORM",
    name: "Brainstorm",
    goal: "Gather with a teammate at the meeting-room whiteboard",
    tier: "standard",
    xp: 25,
    influence: 15,
    coins: 8,
    icon: "\u{1F9E0}"
  },
  /**
   * DAILY_STANDUP — the small-team co-op tier: {@link STANDUP_QUORUM}+ DISTINCT ducks seated in the
   * meeting room, sustained for {@link STANDUP_DURATION_MS}. So a 3-person team gets a real meeting
   * quest without needing all 8 seats. Fires once per completion; resets if the quorum drops.
   */
  DAILY_STANDUP: {
    id: "DAILY_STANDUP",
    name: "Daily standup",
    goal: "Get 3+ teammates seated together in the Meeting Room",
    tier: "group",
    xp: 40,
    influence: 30,
    coins: 12,
    icon: "\u{1F5E3}\uFE0F"
  },
  /**
   * HOLD_A_MEETING — THE HEADLINE RAID. All {@link import('../world/seats.js').MEETING_SEAT_COUNT}
   * meeting-room seats occupied by 8 DISTINCT live users starts a "meeting in session"; sustained for
   * {@link MEETING_DURATION_MS}, every seated duck earns {@link MEETING_XP_PER_SECOND} XP each second,
   * and completing the full duration fires a guild-wide celebration + this bonus to all 8 participants.
   * Server-authoritative throughout (distinct userIds, resets if any seat vacates) — the raid boss.
   */
  HOLD_A_MEETING: {
    id: "HOLD_A_MEETING",
    name: "Hold a Meeting",
    goal: "Fill all 8 Meeting Room seats and hold the meeting for a full minute",
    tier: "raid",
    xp: 120,
    influence: 100,
    coins: 30,
    icon: "\u{1F4CA}"
  }
};
var OfficeQuestIdSchema = z.enum(
  Object.keys(OFFICE_QUESTS)
);
function officeQuestById(id) {
  return OFFICE_QUESTS[id];
}
var WORK_COOLDOWN_MS = 5e3;
var STANDUP_QUORUM = 3;
var STANDUP_DURATION_MS = 3e4;
var MEETING_XP_PER_SECOND = 2;
var MEETING_DURATION_MS = 6e4;
var GATHER_QUORUM = 2;
var GATHER_DURATION_MS = 3e4;
var NEWCOMER_WINDOW_MS = 18e4;
var OFFICE_QUEST_DAILY_XP_CAP = 1e3;
var InternalGrantParticipationSchema = z.object({
  userId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  /** Engaged XP accrued since the last flush (positive int; the gateway re-clamps to the daily cap). */
  xp: z.number().int().min(1).max(PARTICIPATION_XP_DAILY_CAP)
});
var InternalGrantQuestSchema = z.object({
  userId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  quest: OfficeQuestIdSchema
});
var PRESENCE_FLAVORS = ["none", "working", "coffee_break", "gathering", "focus"];
var PresenceFlavorSchema = z.enum(PRESENCE_FLAVORS);
var PRESENCE_LABELS = {
  working: { icon: "\u{1F4BB}", label: "Working" },
  coffee_break: { icon: "\u2615", label: "Coffee break" },
  gathering: { icon: "\u{1F4AC}", label: "Gathering" },
  focus: { icon: "\u{1F3A7}", label: "Focus" }
};
var SetPresenceSchema = z.object({ flavor: PresenceFlavorSchema });
var QUEST_STATES = ["open", "done"];
var QuestStateSchema = z.enum(QUEST_STATES);
var CreateQuestSchema = z.object({
  title: z.string().trim().min(1).max(120)
});
var ShipReleaseSchema = z.object({
  name: z.string().trim().min(1).max(120)
});

// src/domain/achievements.ts
var ACHIEVEMENTS = [
  { id: "first-hand", name: "Lending a Hand", description: "Helped a teammate.", icon: "\u{1F91D}", rule: { kind: "action", action: "HELP_TEAMMATE", count: 1 } },
  { id: "mentor", name: "Mentor", description: "Hosted three knowledge sessions.", icon: "\u{1F9D1}\u200D\u{1F3EB}", rule: { kind: "action", action: "HOST_KNOWLEDGE_SESSION", count: 3 } },
  { id: "greeter", name: "Welcoming Committee", description: "Greeted five newcomers.", icon: "\u{1F44B}", rule: { kind: "action", action: "GREET_NEWCOMER", count: 5 } },
  { id: "quest-runner", name: "Quest Runner", description: "Completed ten quests.", icon: "\u2705", rule: { kind: "action", action: "COMPLETE_QUEST", count: 10 } },
  { id: "beloved", name: "Beloved", description: "Received fifty reputation from peers.", icon: "\u2B50", rule: { kind: "reputationReceived", count: 50 } },
  { id: "shipper", name: "Raid Leader", description: "Shipped a release.", icon: "\u{1F680}", rule: { kind: "releases", count: 1 } }
];
var LEADERBOARD_KINDS = ["top_reputation", "most_helpful"];
var LeaderboardKindSchema = z.enum(LEADERBOARD_KINDS);
var LEADERBOARD_LABELS = {
  top_reputation: { title: "Most Trusted", icon: "\u2B50", unit: "reputation" },
  most_helpful: { title: "Most Helpful", icon: "\u{1F91D}", unit: "helps" }
};
var SetLeaderboardOptInSchema = z.object({ optIn: z.boolean() });
var COSMETIC_SLOTS = ["hat", "glasses", "outfit", "pet"];
var CosmeticSlotSchema = z.enum(COSMETIC_SLOTS);
var COSMETICS = [
  // ── Hats ──────────────────────────────────────────────────────────────────
  { id: "hat-beanie", name: "Cozy Beanie", slot: "hat", price: 40, style: "beanie", description: "A warm knit beanie." },
  { id: "hat-party", name: "Party Hat", slot: "hat", price: 60, style: "party", description: "For every little win." },
  { id: "hat-tophat", name: "Top Hat", slot: "hat", price: 120, style: "tophat", description: "Dapper duck energy." },
  { id: "hat-crown", name: "Golden Crown", slot: "hat", price: 300, style: "crown", description: "Earned, never bought your way to." },
  { id: "hat-cowboy", name: "Cowboy Hat", slot: "hat", price: 90, style: "cowboy", description: "Howdy, partner. \u{1F920}" },
  { id: "hat-propeller", name: "Propeller Cap", slot: "hat", price: 70, style: "propeller", description: "Spins when you ship." },
  { id: "hat-chef", name: "Chef Toque", slot: "hat", price: 80, style: "chef", description: "Cooking up something great." },
  { id: "hat-wizard", name: "Wizard Hat", slot: "hat", price: 160, style: "wizard", description: "You are a code wizard, after all." },
  { id: "hat-headphones", name: "Studio Headphones", slot: "hat", price: 90, style: "headphones", description: "In the zone, but still here." },
  { id: "hat-flower", name: "Flower Crown", slot: "hat", price: 100, style: "flower", description: "Bloom where you waddle." },
  { id: "hat-pirate", name: "Pirate Hat", slot: "hat", price: 130, style: "pirate", description: "Yarr, sail the standup seas." },
  { id: "hat-halo", name: "Glowing Halo", slot: "hat", price: 220, style: "halo", description: "For the kindest of ducks." },
  // ── Glasses ───────────────────────────────────────────────────────────────
  { id: "glasses-round", name: "Round Specs", slot: "glasses", price: 40, style: "round", description: "Studious and sweet." },
  { id: "glasses-shades", name: "Cool Shades", slot: "glasses", price: 70, style: "shades", description: "Permanently unbothered." },
  { id: "glasses-heart", name: "Heart Glasses", slot: "glasses", price: 80, style: "heart", description: "Love the world." },
  { id: "glasses-monocle", name: "Monocle", slot: "glasses", price: 110, style: "monocle", description: "Quite distinguished, indeed." },
  { id: "glasses-3d", name: "3D Glasses", slot: "glasses", price: 60, style: "3d", description: "Everything feels more real." },
  { id: "glasses-goggles", name: "Ski Goggles", slot: "glasses", price: 90, style: "goggles", description: "Ready for the downhill sprint." },
  { id: "glasses-star", name: "Star Shades", slot: "glasses", price: 120, style: "star", description: "A little stardust on your face." },
  { id: "glasses-nerd", name: "Nerd Specs", slot: "glasses", price: 50, style: "nerd", description: "Taped bridge, big brain." },
  // ── Outfits & skins ────────────────────────────────────────────────────────
  { id: "outfit-scarf", name: "Friendly Scarf", slot: "outfit", price: 60, style: "scarf", description: "A scarf for chilly standups." },
  { id: "outfit-hoodie", name: "Guild Hoodie", slot: "outfit", price: 110, style: "hoodie", description: "Repping the guild." },
  { id: "outfit-cape", name: "Hero Cape", slot: "outfit", price: 180, style: "cape", description: "For shipping the raid boss." },
  { id: "outfit-tuxedo", name: "Tuxedo", slot: "outfit", price: 150, style: "tuxedo", description: "For very fancy retros." },
  { id: "outfit-hivis", name: "Hi-Vis Vest", slot: "outfit", price: 70, style: "hivis", description: "Under construction, in the best way." },
  { id: "outfit-overalls", name: "Overalls", slot: "outfit", price: 80, style: "overalls", description: "Roll up your sleeves (you have none)." },
  { id: "outfit-labcoat", name: "Lab Coat", slot: "outfit", price: 120, style: "labcoat", description: "For your finest experiments." },
  { id: "outfit-raincoat", name: "Raincoat", slot: "outfit", price: 90, style: "raincoat", description: "Stay cozy through any storm." },
  // Full-body "skins" — rendered as an outfit-style overlay on the body.
  { id: "outfit-galaxy", name: "Galaxy Skin", slot: "outfit", price: 280, style: "galaxy", description: "A whole cosmos in one duck." },
  { id: "outfit-neon", name: "Neon Skin", slot: "outfit", price: 240, style: "neon", description: "Glow with good vibes." },
  { id: "outfit-robot", name: "Robot Skin", slot: "outfit", price: 260, style: "robot", description: "Beep \u2014 a duck of the future." },
  { id: "outfit-gold", name: "Golden Sheen", slot: "outfit", price: 340, style: "gold", description: "Solid gold, pure heart." },
  // ── Companion pets (follow your duck) ───────────────────────────────────────
  { id: "pet-duckling", name: "Duckling", slot: "pet", price: 150, style: "duckling", description: "A tiny duckling that waddles after you." },
  { id: "pet-cat", name: "Office Cat", slot: "pet", price: 200, style: "cat", description: "It tolerates you. Mostly." },
  { id: "pet-bot", name: "Helper Bot", slot: "pet", price: 260, style: "bot", description: "Beep. Boop. Belonging." },
  { id: "pet-crab", name: "Pocket Crab", slot: "pet", price: 170, style: "crab", description: "Skitters sideways, loyal forever." },
  { id: "pet-frog", name: "Lily Frog", slot: "pet", price: 160, style: "frog", description: "Hops along, ribbits encouragement." },
  { id: "pet-butterfly", name: "Butterfly", slot: "pet", price: 190, style: "butterfly", description: "Flutters wherever you go." },
  { id: "pet-ghost", name: "Friendly Ghost", slot: "pet", price: 210, style: "ghost", description: "Boo! A very gentle haunting." }
];
var BY_ID = new Map(COSMETICS.map((c) => [c.id, c]));
function cosmeticById(id) {
  return BY_ID.get(id);
}
function cosmeticsBySlot(slot) {
  return COSMETICS.filter((c) => c.slot === slot);
}
var EquippedCosmeticsSchema = z.object({
  hat: z.string().optional(),
  glasses: z.string().optional(),
  outfit: z.string().optional(),
  pet: z.string().optional()
}).refine(
  (e) => COSMETIC_SLOTS.every((slot) => {
    const id = e[slot];
    if (id === void 0) return true;
    const c = cosmeticById(id);
    return c !== void 0 && c.slot === slot;
  }),
  { message: "Each equipped cosmetic must be a known cosmetic of that slot." }
);
var PurchaseCosmeticSchema = z.object({ cosmeticId: z.string().min(1).max(64) });
var EquipCosmeticSchema = z.object({
  slot: CosmeticSlotSchema,
  cosmeticId: z.string().min(1).max(64).nullable()
});
var RPS_CHOICES = ["rock", "paper", "scissors"];
var RpsChoiceSchema = z.enum(RPS_CHOICES);
var RPS_OUTCOMES = ["win", "lose", "draw", "declined", "expired"];
var RpsOutcomeSchema = z.enum(RPS_OUTCOMES);
var RPS_CHOICE_LABELS = {
  rock: { icon: "\u{1FAA8}", label: "Rock" },
  paper: { icon: "\u{1F4C4}", label: "Paper" },
  scissors: { icon: "\u2702\uFE0F", label: "Scissors" }
};
function rpsOutcome(mine, theirs) {
  if (mine === theirs) return "draw";
  const beats = { rock: "scissors", paper: "rock", scissors: "paper" };
  return beats[mine] === theirs ? "win" : "lose";
}
var BARK_DURATION_MS = 1e4;
var BARK_SAMPLE_INTERVAL_MS = 100;
var BARK_MAX_SAMPLES = BARK_DURATION_MS / BARK_SAMPLE_INTERVAL_MS;
var BARK_SAMPLE_MAX = 10;
var BARK_MAX_SCORE = BARK_MAX_SAMPLES * BARK_SAMPLE_MAX;
var BARK_COUNTDOWN_MS = 3e3;
var BARK_INVITE_TTL_MS = 3e4;
var BARK_LATE_GRACE_MS = 750;
var BARK_TICK_INTERVAL_MS = 100;
var BARK_OUTCOMES = ["win", "lose", "draw", "declined", "expired"];
var BarkOutcomeSchema = z.enum(BARK_OUTCOMES);
function clampBarkSample(v) {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(BARK_SAMPLE_MAX, Math.round(v)));
}
function barkPointsFromLevel(level) {
  if (!Number.isFinite(level)) return 0;
  return clampBarkSample(level * BARK_SAMPLE_MAX);
}
function barkOutcome(myScore, theirScore) {
  if (myScore === theirScore) return "draw";
  return myScore > theirScore ? "win" : "lose";
}
var WORLD_THEME_IDS = ["office", "space_station", "wizard_academy", "cozy_cafe", "medieval_castle", "medieval_office"];
var WorldThemeIdSchema = z.enum(WORLD_THEME_IDS);
var WORLD_THEMES = {
  office: {
    id: "office",
    name: "Office HQ",
    tagline: "The cozy home base where it all began.",
    palette: { floor: 2830138, wall: 3817808, accent: 16765514, sky: 9418751 }
  },
  space_station: {
    id: "space_station",
    name: "Orbital Station",
    tagline: "Float together among the stars.",
    palette: { floor: 1708080, wall: 2761040, accent: 5497087, sky: 656934 }
  },
  wizard_academy: {
    id: "wizard_academy",
    name: "Wizard Academy",
    tagline: "A guild of friends, and a little bit of magic.",
    palette: { floor: 2300474, wall: 3351898, accent: 12159999, sky: 4861818 }
  },
  cozy_cafe: {
    id: "cozy_cafe",
    name: "Cozy Caf\xE9",
    tagline: "Warm light, good company, no rush.",
    palette: { floor: 3812386, wall: 4864558, accent: 16757611, sky: 16767400 }
  },
  // Castle Hearthhold (ADR-0051): honey-grey flagstone + warm torchlit stone over a soft daylight-blue
  // sky. A storybook keep, never grim-dark — the honeyed warmth always dominates the stone.
  medieval_castle: {
    id: "medieval_castle",
    name: "Castle Hearthhold",
    tagline: "A guild of friends, a warm hearth, and a castle to call home.",
    palette: { floor: 7035461, wall: 8022613, accent: 16757322, sky: 10469600 }
  },
  // Castle Hearthhold v1 (the original single-hall 12-room keep, ADR-0051) — offered ALONGSIDE the grand
  // 5x castle. Shares the castle's warm honey-stone skin (same storybook look, cosier footprint).
  medieval_office: {
    id: "medieval_office",
    name: "Castle Hearthhold (Classic)",
    tagline: "The original cosy keep \u2014 one grand hall, and a fire at its heart.",
    palette: { floor: 7035461, wall: 8022613, accent: 16757322, sky: 10469600 }
  }
};
function worldTheme(id) {
  return WORLD_THEMES[id];
}
var SetWorldThemeSchema = z.object({ theme: WorldThemeIdSchema });

// src/auth/dto.ts
var EmailSchema = z.string().trim().toLowerCase().email().max(254);
var PasswordSchema = z.string().min(8, "Password must be at least 8 characters").max(128);
var DisplayNameSchema = z.string().trim().min(2).max(32);
var LoginNameSchema = z.string().trim().toLowerCase().min(3, "Login must be at least 3 characters").max(32).regex(/^[a-z0-9._-]+$/, "Use only letters, digits, dots, dashes or underscores");
var FingerprintSchema = z.string().trim().min(8).max(256);
var RoomPasswordSchema = z.string().min(4, "Password must be at least 4 characters").max(128);
var RegisterWorkspaceSchema = z.object({
  workspaceName: z.string().trim().min(2).max(60),
  email: EmailSchema,
  password: PasswordSchema,
  displayName: DisplayNameSchema
});
var RegisterAccountSchema = z.object({
  login: LoginNameSchema,
  displayName: DisplayNameSchema,
  password: PasswordSchema,
  fingerprint: FingerprintSchema.optional(),
  /**
   * The LEVEL the new home room is created in (ADR-0054 dual-level) — chosen in the register form's
   * room-type picker. Optional for back-compat; the server defaults new rooms to `medieval_castle`.
   * Only `office` + `medieval_castle` are real levels (other theme ids render as the office level).
   */
  theme: WorldThemeIdSchema.optional(),
  /**
   * The animal + tint the player picks in the register form's character preview (ADR-0024). Persisted on
   * the account so every room you enter shows this critter — and re-editable later from the lobby's
   * "Your character" panel. `.default()` keeps older clients (which omit them) rendering the yellow duck.
   */
  duckColor: DuckColorSchema.default("yellow"),
  species: DuckSpeciesSchema.default("duck")
});
var UpdateAppearanceSchema = z.object({
  duckColor: DuckColorSchema,
  species: DuckSpeciesSchema
});
var UpdateDisplayNameSchema = z.object({
  displayName: DisplayNameSchema,
  fingerprint: FingerprintSchema.optional()
});
var AcceptInviteSchema = z.object({
  inviteKey: z.string().min(8).max(128),
  email: EmailSchema,
  password: PasswordSchema,
  displayName: DisplayNameSchema,
  duckColor: DuckColorSchema.default("yellow"),
  species: DuckSpeciesSchema.default("duck")
});
var AcceptGuestInviteSchema = z.object({
  inviteKey: z.string().min(8).max(128),
  displayName: DisplayNameSchema,
  duckColor: DuckColorSchema.default("yellow"),
  species: DuckSpeciesSchema.default("duck")
});
var LoginSchema = z.object({
  login: z.string().trim().toLowerCase().min(1).max(254),
  password: PasswordSchema,
  fingerprint: FingerprintSchema.optional()
});
var RefreshSchema = z.object({
  refreshToken: z.string().min(16),
  /** Optional device fingerprint (ТЗ §6) — checked as a SECONDARY signal when the session was bound to
   *  one at issuance. A mismatch fails the refresh (re-login), but its absence never blocks refresh. */
  fingerprint: FingerprintSchema.optional()
});
var AccessTokenClaimsSchema = z.object({
  sub: z.string().uuid(),
  workspaceId: z.string().uuid(),
  role: RoleSchema,
  displayName: DisplayNameSchema
});
var WorkspaceSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  logoUrl: z.string().url().nullable(),
  ownerId: z.string().uuid(),
  /** The guild's world THEME (M8) — the same engine, re-skinned per guild. Defaults to 'office'. */
  theme: WorldThemeIdSchema.catch("office"),
  createdAt: z.string().datetime()
});
var UpdateWorkspaceSchema = z.object({
  name: z.string().trim().min(2).max(60).optional(),
  logoUrl: z.string().url().nullable().optional()
});
var CreateWorkspaceSchema = z.object({
  name: z.string().trim().min(2).max(60),
  theme: WorldThemeIdSchema.optional(),
  password: RoomPasswordSchema.optional(),
  /** Device fingerprint (ТЗ §6) bound to the new room's session for silent same-device extension. */
  fingerprint: FingerprintSchema.optional()
});
var CreateInviteSchema = z.object({
  role: RoleSchema.default("EMPLOYEE"),
  /** Null = unlimited uses. */
  maxUses: z.number().int().positive().max(1e4).nullable().default(null),
  /** ISO timestamp; null = never expires. */
  expiresAt: z.string().datetime().nullable().default(null)
});
var InviteCreatedSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  inviteUrl: z.string().url(),
  role: RoleSchema,
  maxUses: z.number().int().nullable(),
  expiresAt: z.string().datetime().nullable()
});
var InvitePreviewSchema = z.object({
  /**
   * The guild this invite leads to. The CLIENT uses this to make the URL authoritative: if the
   * visitor is already a member of THIS workspace it slips them straight in, otherwise it switches
   * their session to the invited world (never letting a stale local session win). Null when invalid.
   */
  workspaceId: z.string().uuid().nullable(),
  workspaceName: z.string(),
  workspaceLogoUrl: z.string().url().nullable(),
  role: RoleSchema,
  valid: z.boolean()
});
var LobbyJoinSchema = z.object({
  workspaceId: z.string().uuid(),
  /**
   * The visitor's attempt for a locked room (ignored for open ones). Length-bounded only (max 128) —
   * deliberately NOT min-constrained like RoomPasswordSchema: this is a GUESS verified against the
   * room's hash server-side, so a too-short guess simply fails as "Incorrect password", not a 400.
   */
  password: z.string().max(128).optional(),
  /** Optional device fingerprint (ТЗ §6) bound to the new room session for silent same-device extend. */
  fingerprint: FingerprintSchema.optional()
});

// src/world/geometry.ts
var WALL_THICKNESS = 12;
var DOOR_REACH = 90;
var ENTRY_DOORWAY_ID = "entry";
function isZoneEnclosed(zoneId, doorways, closedDoorIds) {
  if (!zoneId) return false;
  let hasDoorway = false;
  for (const dw of doorways) {
    if (dw.zoneIds[0] !== zoneId && dw.zoneIds[1] !== zoneId) continue;
    hasDoorway = true;
    if (!closedDoorIds.has(dw.id)) return false;
  }
  return hasDoorway;
}
function mayOpenHeldDoor(opts) {
  if (opts.isHost) return true;
  if (opts.heldBy === "" || opts.heldBy === opts.userId) return true;
  if (opts.zoneId === null || !opts.doorway.zoneIds.includes(opts.zoneId)) return false;
  return isZoneEnclosed(opts.zoneId, opts.doorways, opts.closedDoorIds);
}
function zoneAtIn(x, y, zones) {
  for (const z25 of zones) {
    if (x >= z25.x && x < z25.x + z25.width && y >= z25.y && y < z25.y + z25.height) return z25;
  }
  return null;
}
function subtractGaps(from, to, gaps) {
  const sorted = [...gaps].sort((a, b) => a.start - b.start);
  const runs = [];
  let cursor = from;
  for (const g of sorted) {
    const gs = Math.max(from, g.start);
    const ge = Math.min(to, g.end);
    if (ge <= cursor) continue;
    if (gs > cursor) runs.push({ start: cursor, end: gs });
    cursor = Math.max(cursor, ge);
  }
  if (cursor < to) runs.push({ start: cursor, end: to });
  return runs.filter((r) => r.end - r.start > 0.5);
}
function buildWalls(zones, doors) {
  const walls = [];
  const T = WALL_THICKNESS;
  for (const z25 of zones) {
    const doorsBySide = (side) => doors.filter((d) => d.zoneId === z25.id && d.side === side).map((d) => ({ start: d.pos - d.width / 2, end: d.pos + d.width / 2 }));
    const x0 = z25.x;
    const x1 = z25.x + z25.width;
    const y0 = z25.y;
    const y1 = z25.y + z25.height;
    for (const [side, slabY] of [
      ["N", y0],
      ["S", y1 - T]
    ]) {
      for (const run of subtractGaps(x0 + T, x1 - T, doorsBySide(side))) {
        walls.push({ id: `${z25.id}-${side}-${Math.round(run.start)}`, x: run.start, y: slabY, width: run.end - run.start, height: T });
      }
    }
    for (const [side, slabX] of [
      ["W", x0],
      ["E", x1 - T]
    ]) {
      for (const run of subtractGaps(y0, y1, doorsBySide(side))) {
        walls.push({ id: `${z25.id}-${side}-${Math.round(run.start)}`, x: slabX, y: run.start, width: T, height: run.end - run.start });
      }
    }
  }
  return walls;
}
function resolveWallCollisionIn(prevX, prevY, nextX, nextY, radius, walls) {
  let x = nextX;
  const y0 = prevY;
  const movingRight = nextX >= prevX;
  for (const w of walls) {
    if (overlaps(x, y0, radius, w)) {
      x = movingRight ? w.x - radius : w.x + w.width + radius;
    }
  }
  let y = nextY;
  const movingDown = nextY >= prevY;
  for (const w of walls) {
    if (overlaps(x, y, radius, w)) {
      y = movingDown ? w.y - radius : w.y + w.height + radius;
    }
  }
  return { x, y };
}
function overlaps(cx, cy, r, w) {
  const nx = clampNum(cx, w.x, w.x + w.width);
  const ny = clampNum(cy, w.y, w.y + w.height);
  const dx = cx - nx;
  const dy = cy - ny;
  return dx * dx + dy * dy < r * r;
}
function clampNum(v, lo, hi) {
  return v < lo ? lo : v > hi ? hi : v;
}
function canHearAcrossZones(listenerZone, speakerZone) {
  const sameZone = (listenerZone?.id ?? null) === (speakerZone?.id ?? null);
  if (!sameZone) return { audible: false, ignoreRange: false };
  const proximity = listenerZone === null || listenerZone.openAir === true;
  return { audible: true, ignoreRange: !proximity };
}
function abuttingSide(z25, other) {
  if (z25.x + z25.width === other.x) return "E";
  if (other.x + other.width === z25.x) return "W";
  if (z25.y + z25.height === other.y) return "S";
  if (other.y + other.height === z25.y) return "N";
  throw new Error(`rooms ${z25.id} and ${other.id} do not share an edge`);
}
function connectDoors(zones, links, entryGapId = () => void 0) {
  const byId = new Map(zones.map((z25) => [z25.id, z25]));
  const doors = [];
  const entryDoorIds = [];
  for (const link of links) {
    const a = byId.get(link.a);
    const b = byId.get(link.b);
    if (!a) throw new Error(`door link references unknown room ${link.a}`);
    if (!b) throw new Error(`door link references unknown room ${link.b}`);
    for (const z25 of [a, b]) {
      const other = z25 === a ? b : a;
      const side = abuttingSide(z25, other);
      const id = link.entry ? entryGapId(z25.id) ?? `${z25.id}-${side}-${Math.round(link.pos)}` : `${z25.id}-${side}-${Math.round(link.pos)}`;
      if (link.entry) entryDoorIds.push(id);
      doors.push({ id, zoneId: z25.id, side, pos: link.pos, width: link.width });
    }
  }
  return { doors, entryDoorIds };
}
function doorEdge(d, zoneById) {
  const z25 = zoneById.get(d.zoneId);
  if (!z25) throw new Error(`door ${d.id} references unknown zone ${d.zoneId}`);
  switch (d.side) {
    case "N":
      return { axis: "h", line: z25.y };
    case "S":
      return { axis: "h", line: z25.y + z25.height };
    case "W":
      return { axis: "v", line: z25.x };
    case "E":
      return { axis: "v", line: z25.x + z25.width };
  }
}
function buildDoorways(zones, doors, entryDoorIds) {
  const zoneById = new Map(zones.map((z25) => [z25.id, z25]));
  const zoneOrder = new Map(zones.map((z25, i) => [z25.id, i]));
  const groups = /* @__PURE__ */ new Map();
  for (const d of doors) {
    const { axis, line } = doorEdge(d, zoneById);
    const key = `${axis}|${line}|${d.pos}|${d.width}`;
    const g = groups.get(key);
    if (g) g.push(d);
    else groups.set(key, [d]);
  }
  const doorways = [];
  const T = WALL_THICKNESS;
  for (const g of groups.values()) {
    if (g.length !== 2) continue;
    if (g.some((d) => entryDoorIds.has(d.id))) continue;
    const sorted = [...g].sort((p, q) => p.id.localeCompare(q.id));
    const a = sorted[0];
    const b = sorted[1];
    const { axis, line } = doorEdge(a, zoneById);
    const { pos, width } = a;
    const id = `${a.id}__${b.id}`;
    const zoneIds = [a.zoneId, b.zoneId].sort(
      (p, q) => (zoneOrder.get(p) ?? 0) - (zoneOrder.get(q) ?? 0)
    );
    const name = zoneIds.map((zid) => zoneById.get(zid)?.name ?? zid).join(" \u2194 ");
    const threshold = axis === "h" ? { x: pos, y: line } : { x: line, y: pos };
    const slab = axis === "h" ? { id: `door-${id}`, x: pos - width / 2, y: line - T, width, height: 2 * T } : { id: `door-${id}`, x: line - T, y: pos - width / 2, width: 2 * T, height: width };
    doorways.push({ id, name, doorIds: [a.id, b.id], zoneIds, axis, threshold, slab });
  }
  return doorways.sort((p, q) => p.id.localeCompare(q.id));
}
function distSq(ax, ay, bx, by) {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}
function entryPredicates(zones, cfg) {
  const isInEntryway2 = (x, y) => zoneAtIn(x, y, zones)?.id === cfg.zoneId;
  const withinBellReach2 = (x, y) => distSq(x, y, cfg.bell.x, cfg.bell.y) <= cfg.bellReach * cfg.bellReach;
  const withinDoorReach2 = (x, y) => distSq(x, y, cfg.doorThreshold.x, cfg.doorThreshold.y) <= cfg.doorReach * cfg.doorReach;
  const canRingEntryBell2 = (x, y) => isInEntryway2(x, y) && withinBellReach2(x, y);
  const canKnockEntryDoor2 = (x, y) => isInEntryway2(x, y) && withinDoorReach2(x, y);
  const canOpenEntryDoor2 = (x, y, isHost) => {
    if (!withinDoorReach2(x, y)) return false;
    return isHost || !isInEntryway2(x, y);
  };
  const withinKnockHearing2 = (x, y) => distSq(x, y, cfg.doorThreshold.x, cfg.doorThreshold.y) <= cfg.knockHearing * cfg.knockHearing;
  return {
    isInEntryway: isInEntryway2,
    withinBellReach: withinBellReach2,
    withinDoorReach: withinDoorReach2,
    canRingEntryBell: canRingEntryBell2,
    canKnockEntryDoor: canKnockEntryDoor2,
    canOpenEntryDoor: canOpenEntryDoor2,
    withinKnockHearing: withinKnockHearing2
  };
}
function fireHazardAtIn(x, y, hazards) {
  for (const h of hazards) {
    if (distSq(x, y, h.x, h.y) <= h.r * h.r) return h;
  }
  return null;
}

// src/world/castle/zones.ts
var ROOMS = [
  // ── Central spine — the hero hall + the plaza it overlooks ──
  { id: "throne-hall", name: "\u0422\u0440\u043E\u043D\u043D\u044B\u0439 \u0437\u0430\u043B", x: 1180, y: 760, width: 1240, height: 560 },
  { id: "grand-court", name: "\u0411\u043E\u043B\u044C\u0448\u043E\u0439 \u0434\u0432\u043E\u0440", x: 1180, y: 1320, width: 1240, height: 720, openAir: true },
  // ── The four open-air arteries ringing the spine ──
  { id: "north-gallery", name: "\u041F\u0430\u0440\u0430\u0434\u043D\u0430\u044F \u0433\u0430\u043B\u0435\u0440\u0435\u044F", x: 1180, y: 360, width: 1240, height: 400, openAir: true },
  { id: "west-cloister", name: "\u0417\u0430\u043F\u0430\u0434\u043D\u0430\u044F \u0433\u0430\u043B\u0435\u0440\u0435\u044F", x: 800, y: 760, width: 380, height: 1280, openAir: true },
  { id: "east-cloister", name: "\u0412\u043E\u0441\u0442\u043E\u0447\u043D\u0430\u044F \u0433\u0430\u043B\u0435\u0440\u0435\u044F", x: 2420, y: 760, width: 380, height: 1280, openAir: true },
  { id: "lower-bailey", name: "\u041D\u0438\u0436\u043D\u0438\u0439 \u0434\u0432\u043E\u0440", x: 800, y: 2040, width: 2e3, height: 260, openAir: true },
  // ── Top band — the rampart wall-walk + the two north towers ──
  { id: "north-rampart", name: "\u041A\u0440\u0435\u043F\u043E\u0441\u0442\u043D\u0430\u044F \u0441\u0442\u0435\u043D\u0430", x: 360, y: 40, width: 2880, height: 320, openAir: true },
  { id: "watch-tower", name: "\u0414\u043E\u0437\u043E\u0440\u043D\u0430\u044F \u0431\u0430\u0448\u043D\u044F", x: 40, y: 40, width: 320, height: 720, isolated: true },
  { id: "astronomy-tower", name: "\u0410\u0441\u0442\u0440\u043E\u043D\u043E\u043C\u0438\u0447\u0435\u0441\u043A\u0430\u044F \u0431\u0430\u0448\u043D\u044F", x: 3240, y: 40, width: 320, height: 720, isolated: true },
  // ── West wing (martial) ──
  { id: "armoury", name: "\u041E\u0440\u0443\u0436\u0435\u0439\u043D\u0430\u044F", x: 360, y: 360, width: 440, height: 400 },
  { id: "barracks", name: "\u041A\u0430\u0437\u0430\u0440\u043C\u044B", x: 40, y: 760, width: 760, height: 420 },
  { id: "training-yard", name: "\u0422\u0440\u0435\u043D\u0438\u0440\u043E\u0432\u043E\u0447\u043D\u044B\u0439 \u0434\u0432\u043E\u0440", x: 40, y: 1180, width: 760, height: 520, openAir: true },
  { id: "guard-hall", name: "\u041A\u0430\u0440\u0430\u0443\u043B\u044C\u043D\u044B\u0439 \u0437\u0430\u043B", x: 40, y: 1700, width: 760, height: 340 },
  // ── North band rooms flanking the gallery ──
  { id: "chapel", name: "\u0427\u0430\u0441\u043E\u0432\u043D\u044F", x: 800, y: 360, width: 380, height: 400 },
  { id: "war-room", name: "\u0412\u043E\u0435\u043D\u043D\u044B\u0439 \u0441\u043E\u0432\u0435\u0442", x: 2420, y: 360, width: 380, height: 400, isolated: true },
  // ── East wing (scholarly + green) ──
  { id: "great-library", name: "\u0411\u043E\u043B\u044C\u0448\u0430\u044F \u0431\u0438\u0431\u043B\u0438\u043E\u0442\u0435\u043A\u0430", x: 2800, y: 360, width: 440, height: 400 },
  { id: "scriptorium", name: "\u0421\u043A\u0440\u0438\u043F\u0442\u043E\u0440\u0438\u0439", x: 2800, y: 760, width: 380, height: 420 },
  { id: "alchemy-lab", name: "\u0410\u043B\u0445\u0438\u043C\u0438\u0447\u0435\u0441\u043A\u0430\u044F \u043B\u0430\u0431\u043E\u0440\u0430\u0442\u043E\u0440\u0438\u044F", x: 3180, y: 760, width: 380, height: 420, isolated: true },
  { id: "royal-gardens", name: "\u041A\u043E\u0440\u043E\u043B\u0435\u0432\u0441\u043A\u0438\u0435 \u0441\u0430\u0434\u044B", x: 2800, y: 1180, width: 760, height: 520, openAir: true },
  { id: "orchard", name: "\u0424\u0440\u0443\u043A\u0442\u043E\u0432\u044B\u0439 \u0441\u0430\u0434", x: 2800, y: 1700, width: 760, height: 340, openAir: true },
  // ── The two south corner towers ──
  { id: "mage-tower", name: "\u0411\u0430\u0448\u043D\u044F \u043C\u0430\u0433\u0430", x: 40, y: 2040, width: 320, height: 600, isolated: true },
  { id: "royal-tower", name: "\u041A\u043E\u0440\u043E\u043B\u0435\u0432\u0441\u043A\u0430\u044F \u0431\u0430\u0448\u043D\u044F", x: 3240, y: 2040, width: 320, height: 600, isolated: true },
  // ── The undercroft (dark) + the welcoming gatehouse ──
  { id: "dungeon-jail", name: "\u0422\u0435\u043C\u043D\u0438\u0446\u0430", x: 360, y: 2040, width: 440, height: 600, isolated: true },
  { id: "wine-cellar", name: "\u0412\u0438\u043D\u043D\u044B\u0439 \u043F\u043E\u0433\u0440\u0435\u0431", x: 800, y: 2300, width: 560, height: 340, isolated: true },
  { id: "gatehouse", name: "\u041F\u0440\u0438\u0432\u0440\u0430\u0442\u043D\u0430\u044F \u0431\u0430\u0448\u043D\u044F", x: 1360, y: 2300, width: 880, height: 340, isolated: true },
  { id: "treasury-vault", name: "\u0421\u043E\u043A\u0440\u043E\u0432\u0438\u0449\u043D\u0438\u0446\u0430", x: 2240, y: 2300, width: 560, height: 340, isolated: true },
  { id: "catacombs", name: "\u041A\u0430\u0442\u0430\u043A\u043E\u043C\u0431\u044B", x: 2800, y: 2040, width: 440, height: 600, isolated: true }
];
var CASTLE_ZONES = ROOMS.map((r) => ({
  id: r.id,
  name: r.name,
  x: r.x,
  y: r.y,
  width: r.width,
  height: r.height,
  isolated: r.isolated ?? false,
  openAir: r.openAir ?? false
}));
var CASTLE_ROOM_LINKS = [
  // ── Top rampart wall-walk → towers + the north band ──
  { a: "watch-tower", b: "north-rampart", pos: 200, width: 140 },
  { a: "north-rampart", b: "astronomy-tower", pos: 200, width: 140 },
  { a: "north-rampart", b: "armoury", pos: 580, width: 140 },
  { a: "north-rampart", b: "chapel", pos: 990, width: 140 },
  { a: "north-rampart", b: "north-gallery", pos: 1800, width: 240 },
  { a: "north-rampart", b: "war-room", pos: 2610, width: 140 },
  { a: "north-rampart", b: "great-library", pos: 3020, width: 140 },
  // ── North band — gallery + flanking rooms + towers ──
  { a: "watch-tower", b: "armoury", pos: 560, width: 140 },
  { a: "armoury", b: "chapel", pos: 560, width: 140 },
  { a: "chapel", b: "north-gallery", pos: 560, width: 160 },
  { a: "north-gallery", b: "war-room", pos: 560, width: 160 },
  { a: "war-room", b: "great-library", pos: 560, width: 140 },
  { a: "great-library", b: "astronomy-tower", pos: 560, width: 140 },
  // ── North band → the central spine + west/east wings (y760 horizontals) ──
  { a: "watch-tower", b: "barracks", pos: 200, width: 140 },
  { a: "armoury", b: "barracks", pos: 580, width: 140 },
  { a: "chapel", b: "west-cloister", pos: 990, width: 160 },
  { a: "north-gallery", b: "throne-hall", pos: 1800, width: 260 },
  { a: "war-room", b: "east-cloister", pos: 2610, width: 160 },
  { a: "great-library", b: "scriptorium", pos: 2990, width: 140 },
  { a: "alchemy-lab", b: "astronomy-tower", pos: 3400, width: 140 },
  // ── West wing artery (the west cloister threads the martial rooms) ──
  { a: "barracks", b: "west-cloister", pos: 970, width: 160 },
  { a: "training-yard", b: "west-cloister", pos: 1440, width: 200 },
  { a: "guard-hall", b: "west-cloister", pos: 1870, width: 160 },
  { a: "barracks", b: "training-yard", pos: 420, width: 200 },
  { a: "training-yard", b: "guard-hall", pos: 420, width: 200 },
  // ── The throne + grand court, framed by the two cloisters (the central ring) ──
  { a: "west-cloister", b: "throne-hall", pos: 1040, width: 200 },
  { a: "west-cloister", b: "grand-court", pos: 1680, width: 240 },
  { a: "throne-hall", b: "east-cloister", pos: 1040, width: 200 },
  { a: "grand-court", b: "east-cloister", pos: 1680, width: 240 },
  { a: "throne-hall", b: "grand-court", pos: 1800, width: 300 },
  { a: "grand-court", b: "lower-bailey", pos: 1800, width: 300 },
  // ── East wing artery (the east cloister threads the scholarly + green rooms) ──
  { a: "east-cloister", b: "scriptorium", pos: 970, width: 160 },
  { a: "east-cloister", b: "royal-gardens", pos: 1440, width: 200 },
  { a: "east-cloister", b: "orchard", pos: 1870, width: 160 },
  { a: "scriptorium", b: "alchemy-lab", pos: 970, width: 140 },
  { a: "scriptorium", b: "royal-gardens", pos: 2990, width: 160 },
  { a: "alchemy-lab", b: "royal-gardens", pos: 3370, width: 140 },
  { a: "royal-gardens", b: "orchard", pos: 3180, width: 240 },
  // ── The lower bailey closes the ring (cloisters ↔ bailey) ──
  { a: "west-cloister", b: "lower-bailey", pos: 990, width: 200 },
  { a: "east-cloister", b: "lower-bailey", pos: 2610, width: 200 },
  // ── Descent into the undercroft (the dark depths + its hidden cross-loops) ──
  { a: "guard-hall", b: "mage-tower", pos: 200, width: 140 },
  { a: "guard-hall", b: "dungeon-jail", pos: 580, width: 160 },
  { a: "orchard", b: "catacombs", pos: 3020, width: 160 },
  { a: "orchard", b: "royal-tower", pos: 3400, width: 140 },
  { a: "dungeon-jail", b: "mage-tower", pos: 2340, width: 140 },
  { a: "dungeon-jail", b: "lower-bailey", pos: 2170, width: 160 },
  { a: "dungeon-jail", b: "wine-cellar", pos: 2470, width: 140 },
  { a: "lower-bailey", b: "wine-cellar", pos: 1080, width: 160 },
  { a: "lower-bailey", b: "treasury-vault", pos: 2520, width: 160 },
  { a: "lower-bailey", b: "catacombs", pos: 2170, width: 160 },
  { a: "wine-cellar", b: "gatehouse", pos: 2470, width: 140 },
  { a: "gatehouse", b: "treasury-vault", pos: 2470, width: 140 },
  { a: "treasury-vault", b: "catacombs", pos: 2470, width: 140 },
  { a: "catacombs", b: "royal-tower", pos: 2340, width: 140 },
  // ── The PORTCULLIS — the single controllable entry (bailey ↔ gatehouse spawn). ──
  { a: "lower-bailey", b: "gatehouse", pos: 1800, width: 200, entry: true }
];
function castleEntryGapId(zoneId) {
  if (zoneId === "gatehouse") return "gatehouse-portcullis";
  if (zoneId === "lower-bailey") return "bailey-portcullis";
  return void 0;
}
var connected = connectDoors(CASTLE_ZONES, CASTLE_ROOM_LINKS, castleEntryGapId);
var CASTLE_DOORS = connected.doors;
var CASTLE_ENTRY_DOOR_IDS = connected.entryDoorIds;

// src/world/castle/seats.ts
var CASTLE_MEETING_TABLE = { x: 2610, y: 560 };
var MEETING_RING = { rx: 120, ry: 130 };
var CASTLE_MEETING_SEAT_COUNT = 8;
function buildMeetingSeats() {
  const seats = [];
  for (let i = 0; i < CASTLE_MEETING_SEAT_COUNT; i++) {
    const angle = -Math.PI / 2 + i / CASTLE_MEETING_SEAT_COUNT * Math.PI * 2;
    const x = Math.round(CASTLE_MEETING_TABLE.x + Math.cos(angle) * MEETING_RING.rx);
    const y = Math.round(CASTLE_MEETING_TABLE.y + Math.sin(angle) * MEETING_RING.ry);
    const facing = Math.atan2(CASTLE_MEETING_TABLE.y - y, CASTLE_MEETING_TABLE.x - x);
    seats.push({ id: `meeting-seat-${i + 1}`, kind: "meeting-seat", icon: "\u{1FA91}", label: `\u041A\u0440\u0435\u0441\u043B\u043E \u0441\u043E\u0432\u0435\u0442\u0430 ${i + 1}`, x, y, facing });
  }
  return seats;
}
var CASTLE_MEETING_SEATS = buildMeetingSeats();
var CASTLE_WORKSTATION_SEATS = [
  { id: "desk-1", kind: "workstation", icon: "\u2712\uFE0F", label: "\u041F\u044E\u043F\u0438\u0442\u0440", x: 2880, y: 860, facing: -Math.PI / 2 },
  { id: "desk-2", kind: "workstation", icon: "\u2712\uFE0F", label: "\u041F\u044E\u043F\u0438\u0442\u0440", x: 2990, y: 860, facing: -Math.PI / 2 },
  { id: "desk-3", kind: "workstation", icon: "\u2712\uFE0F", label: "\u041F\u044E\u043F\u0438\u0442\u0440", x: 3100, y: 860, facing: -Math.PI / 2 },
  { id: "desk-4", kind: "workstation", icon: "\u2712\uFE0F", label: "\u041F\u044E\u043F\u0438\u0442\u0440", x: 2880, y: 1080, facing: -Math.PI / 2 },
  { id: "desk-5", kind: "workstation", icon: "\u2712\uFE0F", label: "\u041F\u044E\u043F\u0438\u0442\u0440", x: 2990, y: 1080, facing: -Math.PI / 2 },
  { id: "desk-6", kind: "workstation", icon: "\u2712\uFE0F", label: "\u041F\u044E\u043F\u0438\u0442\u0440", x: 3100, y: 1080, facing: -Math.PI / 2 }
];
var OTHER_SEATS = [
  // ── Throne Hall — the communal "Seat of the Guild" (centre dais) + feast tables flanking it. ──
  { id: "guild-throne", kind: "chair", icon: "\u{1F451}", label: "\u0422\u0440\u043E\u043D \u0433\u0438\u043B\u044C\u0434\u0438\u0438", x: 1800, y: 980, facing: Math.PI / 2 },
  { id: "feast-nw-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1380, y: 880, facing: Math.PI / 2 },
  { id: "feast-nw-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1480, y: 880, facing: Math.PI / 2 },
  { id: "feast-nw-3", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1380, y: 930, facing: -Math.PI / 2 },
  { id: "feast-nw-4", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1480, y: 930, facing: -Math.PI / 2 },
  { id: "feast-ne-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2120, y: 880, facing: Math.PI / 2 },
  { id: "feast-ne-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2220, y: 880, facing: Math.PI / 2 },
  { id: "feast-ne-3", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2120, y: 930, facing: -Math.PI / 2 },
  { id: "feast-ne-4", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2220, y: 930, facing: -Math.PI / 2 },
  // ── Grand Court — benches ringing the fountain + shaded corner benches (open-air gather). ──
  { id: "court-fountain-nw", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1560, y: 1520, facing: 0.59 },
  { id: "court-fountain-ne", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2040, y: 1520, facing: 2.55 },
  { id: "court-fountain-sw", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1560, y: 1840, facing: -0.59 },
  { id: "court-fountain-se", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2040, y: 1840, facing: -2.55 },
  { id: "court-bench-w1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1260, y: 1400, facing: 0 },
  { id: "court-bench-w2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1260, y: 1960, facing: 0 },
  { id: "court-bench-e1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2340, y: 1400, facing: Math.PI },
  { id: "court-bench-e2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2340, y: 1960, facing: Math.PI },
  // ── North Gallery — two processional benches along the colonnade. ──
  { id: "gallery-bench-w", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1340, y: 680, facing: -Math.PI / 2 },
  { id: "gallery-bench-e", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2260, y: 680, facing: -Math.PI / 2 },
  // ── Chapel — two short rows of prayer pews facing the altar. ──
  { id: "chapel-pew-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 940, y: 620, facing: -Math.PI / 2 },
  { id: "chapel-pew-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1040, y: 620, facing: -Math.PI / 2 },
  { id: "chapel-pew-3", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 940, y: 690, facing: -Math.PI / 2 },
  { id: "chapel-pew-4", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1040, y: 690, facing: -Math.PI / 2 },
  // ── Great Library — a reading table (2 chairs) + a window armchair. ──
  { id: "library-read-1", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 3140, y: 480, facing: Math.PI / 2 },
  { id: "library-read-2", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 3140, y: 650, facing: -Math.PI / 2 },
  { id: "library-armchair", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E \u0443 \u043E\u043A\u043D\u0430", x: 2880, y: 680, facing: 0 },
  // ── Alchemy Lab — a work-stool at the bench. ──
  { id: "alchemy-stool", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 3370, y: 1e3, facing: -Math.PI / 2 },
  // ── Armoury — a sharpening-bench stool. ──
  { id: "armoury-stool", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 580, y: 560, facing: -Math.PI / 2 },
  // ── Barracks (the mess) — a long trestle table + a fireside settle. ──
  { id: "barracks-bench-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 200, y: 900, facing: Math.PI / 2 },
  { id: "barracks-bench-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 320, y: 900, facing: Math.PI / 2 },
  { id: "barracks-bench-3", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 200, y: 1040, facing: -Math.PI / 2 },
  { id: "barracks-bench-4", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 320, y: 1040, facing: -Math.PI / 2 },
  { id: "barracks-settle", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "\u0421\u043A\u0430\u043C\u044C\u044F \u0443 \u043A\u0430\u043C\u0438\u043D\u0430", x: 650, y: 880, facing: -Math.PI / 2 },
  // ── Training Yard — two rest benches at the rail. ──
  { id: "yard-bench-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 120, y: 1620, facing: 0 },
  { id: "yard-bench-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 720, y: 1620, facing: Math.PI },
  // ── Guard Hall — a fireside rest. ──
  { id: "guard-bench-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 200, y: 1870, facing: 0 },
  { id: "guard-bench-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 600, y: 1870, facing: Math.PI },
  // ── West & East Cloisters — a quiet bench in each arcade. ──
  { id: "wcloister-bench", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 870, y: 1240, facing: 0 },
  { id: "ecloister-bench", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2730, y: 1240, facing: Math.PI },
  // ── Lower Bailey — the market/tavern square (open-air gather): trestle stools + ale benches. ──
  { id: "bailey-bench-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1100, y: 2170, facing: Math.PI / 2 },
  { id: "bailey-bench-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1300, y: 2170, facing: -Math.PI / 2 },
  { id: "bailey-bench-3", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1500, y: 2170, facing: Math.PI / 2 },
  { id: "bailey-bench-4", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2100, y: 2170, facing: -Math.PI / 2 },
  { id: "bailey-bench-5", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2300, y: 2170, facing: Math.PI / 2 },
  { id: "bailey-bench-6", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2500, y: 2170, facing: -Math.PI / 2 },
  // ── Royal Gardens — a gazebo cluster + a pondside bench. ──
  { id: "garden-gazebo-1", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 3300, y: 1300, facing: Math.PI / 2 },
  { id: "garden-gazebo-2", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 3300, y: 1580, facing: -Math.PI / 2 },
  { id: "garden-gazebo-3", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 3420, y: 1440, facing: Math.PI },
  { id: "garden-pond", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F \u0443 \u043F\u0440\u0443\u0434\u0430", x: 3e3, y: 1300, facing: 0 },
  // ── Orchard — two picnic benches under the boughs. ──
  { id: "orchard-bench-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 3e3, y: 1870, facing: 0 },
  { id: "orchard-bench-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 3360, y: 1870, facing: Math.PI },
  // ── Wine Cellar — the candlelit tasting table (intimate sealed nook). ──
  { id: "cellar-chair-1", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 1010, y: 2470, facing: 0 },
  { id: "cellar-chair-2", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 1150, y: 2470, facing: Math.PI },
  { id: "cellar-chair-3", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 1080, y: 2400, facing: Math.PI / 2 },
  { id: "cellar-chair-4", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 1080, y: 2540, facing: -Math.PI / 2 },
  // ── Treasury Vault — a velvet viewing bench facing the relic. ──
  { id: "treasury-bench", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2520, y: 2540, facing: -Math.PI / 2 },
  // ── Gatehouse — two stone arrival benches along the side walls (the welcome). ──
  { id: "gatehouse-bench-l", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1450, y: 2470, facing: 0 },
  { id: "gatehouse-bench-r", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2150, y: 2470, facing: Math.PI },
  // ── The four towers — a lookout / study seat in each crown room (verticality). ──
  { id: "watch-tower-stool", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442 \u0434\u043E\u0437\u043E\u0440\u043D\u043E\u0433\u043E", x: 200, y: 200, facing: 0 },
  { id: "astronomy-stool", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E \u0437\u0432\u0435\u0437\u0434\u043E\u0447\u0451\u0442\u0430", x: 3400, y: 200, facing: 0 },
  { id: "mage-tower-chair", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E \u043C\u0430\u0433\u0430", x: 200, y: 2340, facing: 0 },
  { id: "royal-tower-chair", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u043E\u0440\u043E\u043B\u0435\u0432\u0441\u043A\u043E\u0435 \u043A\u0440\u0435\u0441\u043B\u043E", x: 3400, y: 2340, facing: Math.PI },
  // ── The undercroft — a grim bench in the dungeon + the catacombs. ──
  { id: "dungeon-bench", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 580, y: 2340, facing: 0 },
  { id: "catacombs-bench", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 3020, y: 2340, facing: 0 },
  // ── North Rampart — two lookout benches on the wall-walk. ──
  { id: "rampart-bench-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 700, y: 200, facing: Math.PI / 2 },
  { id: "rampart-bench-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 2900, y: 200, facing: Math.PI / 2 }
];
var CASTLE_SEATS = [...CASTLE_MEETING_SEATS, ...CASTLE_WORKSTATION_SEATS, ...OTHER_SEATS];

// src/world/castle/objects.ts
var CASTLE_OBJECTS = [
  { id: "cauldron", kind: "coffee", icon: "\u{1F372}", label: "\u041A\u043E\u0442\u0451\u043B", x: 700, y: 960, effect: { presence: "coffee_break", animation: "coffee" } },
  { id: "mulled-cauldron", kind: "coffee", icon: "\u{1F372}", label: "\u0413\u043B\u0438\u043D\u0442\u0432\u0435\u0439\u043D", x: 2e3, y: 2120, effect: { presence: "coffee_break", animation: "coffee" } },
  { id: "scribe-lectern", kind: "desk", icon: "\u2712\uFE0F", label: "\u041F\u0435\u0440\u0435\u043F\u0438\u0441\u043A\u0430", x: 2860, y: 1e3, effect: { presence: "working", animation: "type" } },
  { id: "barracks-settle", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "\u0421\u043A\u0430\u043C\u044C\u044F \u0443 \u043A\u0430\u043C\u0438\u043D\u0430", x: 650, y: 880, effect: { presence: "gathering", animation: "sit" } },
  { id: "campaign-map", kind: "tv", icon: "\u{1F5FA}\uFE0F", label: "\u041A\u0430\u0440\u0442\u0430 \u043F\u043E\u0445\u043E\u0434\u0430", x: 2470, y: 420, effect: { presence: "focus", animation: "wave" } },
  { id: "games-table", kind: "arcade", icon: "\u{1F3B2}", label: "\u0418\u0433\u0440\u044B", x: 1150, y: 2200, effect: { presence: "gathering", animation: "laugh" } },
  // 🪕 Bard's stage — interact to TOGGLE the Bailey-market disco (player-driven, room-local sing-along).
  { id: "bard-stage", kind: "jukebox", icon: "\u{1FA95}", label: "\u0421\u0446\u0435\u043D\u0430 \u0431\u0430\u0440\u0434\u0430", x: 2450, y: 2200, effect: { animation: "dance", disco: true } }
];

// src/world/castle/hazards.ts
var CASTLE_FIRE_HAZARDS = [
  { id: "great-hearth", x: 1500, y: 1150, r: 58 },
  // the Throne Hall's great hearth (SW of the dais)
  { id: "forge", x: 700, y: 460, r: 38 },
  // the Armoury forge (off the sharpening stool)
  { id: "mess-fire", x: 680, y: 1080, r: 34 }
  // the Barracks mess cooking-fire
];

// src/realtime/constants.ts
var SIM = {
  /** Authoritative simulation + state-broadcast rate (Hz). */
  TICK_RATE: 20,
  /** Fixed timestep in seconds, derived from TICK_RATE. */
  DT: 1 / 20,
  /** Walking speed in world units per second. */
  WALK_SPEED: 220,
  /** Default world map size in world units (pixels at zoom 1). The map is bounded. This is the OFFICE
   *  (default level) envelope, 1600×1200; per-level bounds live on each {@link Level} (the castle is
   *  3600×2680) and are threaded through validation/flood-fill. */
  WORLD_WIDTH: 1600,
  WORLD_HEIGHT: 1200,
  /** Radius of a duck for bounds/collision in world units. */
  DUCK_RADIUS: 18,
  /**
   * If the local predicted position drifts further than this (world units) from the
   * authoritative position, snap instead of smoothing. Below it, ease toward authority.
   */
  RECONCILE_SNAP_THRESHOLD: 64,
  /** Per-frame easing factor used when reconciling small prediction errors (0..1). */
  RECONCILE_EASE: 0.2,
  /**
   * Render-side interpolation delay (ms). Remote players are drawn this far in the past so
   * there are always two authoritative snapshots to interpolate between. ~2 ticks.
   */
  INTERPOLATION_DELAY_MS: 100
};
var LIMITS = {
  /** Max movement-intent messages accepted per second per client (excess is dropped). */
  MAX_INTENTS_PER_SEC: 30,
  /** Max emote messages per second per client. */
  MAX_EMOTES_PER_SEC: 4,
  /** Max reputation-give attempts per second per client (giving is a deliberate, rare gesture). */
  MAX_REPUTATION_GRANTS_PER_SEC: 2,
  /** Hard cap of concurrent players per office room (matches the first-release target). */
  MAX_PLAYERS_PER_ROOM: 100
};
var SOCIAL = {
  /**
   * Max distance (world units) between two ducks for one to give the other reputation. The realtime
   * server owns positions, so this is enforced server-side — proximity can't be forged by a client.
   */
  REPUTATION_GIVE_RANGE: 220,
  /** Max distance (world units) to challenge a nearby duck to a mini-game (M7). Server-enforced. */
  MINIGAME_RANGE: 220
};
var VOICE = {
  /**
   * Proximity radius (world units) for SOUND REACTIONS (soundboard clips) — a clip is heard only by
   * ducks within this distance of the player. This is NOT the voice gate: voice itself is no longer
   * distance-gated (flat/global-in-zone, ADR-0057); this constant now governs sound-reaction reach only.
   */
  HEARING_RANGE: 320,
  /**
   * The model (ADR-0043, refined): in a room you ALWAYS hear EVERY active speaker — the speaker set is
   * UNCAPPED (`computeAudibleSet` forwards every eligible peer who is speaking, however many that is). The
   * nearest currently-SILENT peers then TOP UP the audible set to a TOTAL of `MAX_AUDIBLE` — purely so an
   * immediate neighbour starts INSTANTLY (no first-word clip), and for a little spatial ambiance. So the
   * silent pre-warm is `max(0, MAX_AUDIBLE − speakerCount)` — it shrinks as more people talk and is zero
   * once speakers already reach `MAX_AUDIBLE` (leanest exactly when the room is busiest). The far silent
   * crowd is NOT streamed at all (a silent producer carries no audio, so not forwarding it costs the
   * listener nothing but saves real SFU + decode load):
   *
   *     audible(listener) = { all eligible SPEAKERS, uncapped } + nearest silent peers up to MAX_AUDIBLE TOTAL
   *
   * This is how large calls actually work (Zoom/Discord forward the talkers, not the silent crowd) and it
   * makes a 100-person SESSION cheap: people spread across zones, and even 100 in ONE room only forwards
   * the handful actually talking + a few nearest silent — never 100 full streams to everyone. `MAX_AUDIBLE`
   * is therefore a small "presence / instant-start" floor, NOT a limit on who you can hear talk. Opus is
   * cheap (~32 kbps); DTX quiets idle producers. DEFAULT both ends agree on; the SERVER may override via
   * `VOICE_MAX_AUDIBLE` (env, clamped) — the client renders exactly what the server authorizes.
   *
   * Independent budgets, deliberately decoupled:
   *   • MAX_AUDIBLE            — TOTAL presence floor (speakers + nearest-silent top-up); not a speaker cap.
   *   • ACTIVE_SPEAKER_ENTRIES — how many speakers the level observer reports → bounds the UNCAPPED
   *     speaker set, so it must be ≥ the max simultaneous speakers we promise to forward (room-sized).
   *   • MAX_VIDEO_AUDIBLE      — webcam faces forwarded per listener (the heavy, tightest budget).
   */
  MAX_AUDIBLE: 12,
  /**
   * How many simultaneous speakers the room's AudioLevelObserver reports (ADR-0043). This BOUNDS the
   * "all speakers are forwarded" promise: a speaker not in the observer's report is treated as silent, so
   * to guarantee EVERY talker in a full room is heard this must be ≥ the room capacity. Sized to
   * `LIMITS.MAX_PLAYERS_PER_ROOM` (100). The cost is negligible — the observer measures every producer's
   * level regardless; `maxEntries` only sizes the reported array. Also drives the speaking-ring cue.
   * Ephemeral, never persisted (Constitution Art. 2). Server-overridable via `VOICE_ACTIVE_SPEAKER_ENTRIES`.
   */
  ACTIVE_SPEAKER_ENTRIES: 100,
  /**
   * How long (ms) a peer who has stopped speaking stays in the forwarded SPEAKER set (ADR-0043). The
   * level observer fires every ~400 ms; natural between-sentence pauses exceed that, so without a hold a
   * far speaker would drop + cold-start re-acquire each sentence (choppy). A short linger keeps a recent
   * speaker "warm" across pauses, so only the FIRST word after a long silence pays the acquire latency.
   * Server-side timing only (the realtime manager applies it before calling the pure gate).
   */
  SPEAKER_LINGER_MS: 2500,
  /**
   * Cap on simultaneous VIDEO faces forwarded per listener (ADR-0030) — the NEAREST few of the audible
   * set. Video decode/bandwidth is far heavier than audio, so faces get a tighter cap than voices; beyond
   * it, a nearby duck is still HEARD and still drawn, just without a live face bubble. Audio is unaffected.
   * Server-overridable via `VOICE_MAX_VIDEO_AUDIBLE`.
   */
  MAX_VIDEO_AUDIBLE: 6,
  /** How often (Hz) the room recomputes each listener's audible set from positions/zones. */
  SUBSCRIPTION_HZ: 5
};
var HEALTH = {
  /** Full hit points. */
  MAX_HP: 100,
  /** HP lost per second while standing in fire (≈4s from full HP to a pop — punchy, not instant). */
  FIRE_DAMAGE_PER_SEC: 25
};
function clamp(value, min, max) {
  return value < min ? min : value > max ? max : value;
}
var PERSONAL_VOICE_MIN = -100;
var PERSONAL_VOICE_MAX = 100;
function clampPersonalVoice(offset) {
  if (!Number.isFinite(offset)) return 0;
  return Math.round(clamp(offset, PERSONAL_VOICE_MIN, PERSONAL_VOICE_MAX));
}
function personalVoiceGain(offset) {
  const v = clampPersonalVoice(offset);
  return v <= 0 ? (100 + v) / 100 : 1 + v / 100;
}
function clampInputVector(x, y) {
  const len = Math.hypot(x, y);
  if (!Number.isFinite(len) || len === 0) return { x: 0, y: 0 };
  if (len <= 1) return { x, y };
  return { x: x / len, y: y / len };
}

// src/world/castle/entry.ts
var CASTLE_ENTRY = {
  zoneId: "gatehouse",
  doorThreshold: { x: 1800, y: 2300 },
  bell: { x: 2120, y: 2380 },
  doorSlab: { id: "entry-door", x: 1700, y: 2288, width: 200, height: 24 },
  doorIds: ["gatehouse-portcullis", "bailey-portcullis"],
  doorReach: 100,
  bellReach: 95,
  knockHearing: 280,
  openMs: 9e3
};
function castleSpawn(seed) {
  const spread = seed * 53 % 320 - 160;
  return {
    x: clamp(1800 + spread, 1420, 2180),
    y: 2520,
    facing: -Math.PI / 2
  };
}

// src/world/office/zones.ts
var OFFICE_ZONES_DATA = [
  // ── Top band (y 40..280) ──
  { id: "terrace", name: "Terrace", x: 40, y: 40, width: 400, height: 240, isolated: false },
  { id: "kitchen", name: "Kitchen", x: 440, y: 40, width: 680, height: 240, isolated: false },
  { id: "meeting-room", name: "Meeting Room", x: 1120, y: 40, width: 440, height: 240, isolated: true },
  // ── Middle bands (y 280..840) — the hub spans both, flanked by the wings ──
  { id: "lounge", name: "Lounge", x: 40, y: 280, width: 400, height: 300, isolated: false },
  { id: "open-space", name: "Open Space", x: 440, y: 280, width: 680, height: 560, isolated: false },
  { id: "ceo-office", name: "CEO Office", x: 1120, y: 280, width: 440, height: 300, isolated: true },
  { id: "server-room", name: "Server Room", x: 40, y: 580, width: 400, height: 260, isolated: false },
  { id: "studio", name: "Studio", x: 1120, y: 580, width: 440, height: 260, isolated: false },
  // ── Bottom band (y 840..1160) — front of house + the entry vestibule + the restroom annex ──
  { id: "game-room", name: "Game Room", x: 40, y: 840, width: 400, height: 320, isolated: false },
  { id: "reception", name: "Reception", x: 440, y: 840, width: 1120, height: 160, isolated: false },
  { id: "entryway", name: "Entryway", x: 440, y: 1e3, width: 600, height: 160, isolated: true },
  { id: "toilet", name: "Toilet", x: 1040, y: 1e3, width: 520, height: 160, isolated: false }
];
var OFFICE_DOORS_DATA = [
  // ENTRYWAY ↔ RECEPTION — the single controllable front door (OPEN by default, ADR-0035; see entry.ts).
  { id: "entryway-N", zoneId: "entryway", side: "N", pos: 800, width: 120 },
  { id: "reception-S", zoneId: "reception", side: "S", pos: 800, width: 120 },
  // RECEPTION ↔ OPEN-SPACE hub (front of house → commons).
  { id: "reception-N", zoneId: "reception", side: "N", pos: 780, width: 200 },
  { id: "open-space-S", zoneId: "open-space", side: "S", pos: 780, width: 200 },
  // RECEPTION ↔ GAME-ROOM (shared edge x440, y840..1000).
  { id: "reception-W", zoneId: "reception", side: "W", pos: 920, width: 120 },
  { id: "game-room-E", zoneId: "game-room", side: "E", pos: 920, width: 120 },
  // GAME-ROOM ↔ SERVER-ROOM (left wing, shared edge y840).
  { id: "game-room-N", zoneId: "game-room", side: "N", pos: 240, width: 160 },
  { id: "server-room-S", zoneId: "server-room", side: "S", pos: 240, width: 160 },
  // SERVER-ROOM ↔ LOUNGE (left wing, shared edge y580).
  { id: "server-room-N", zoneId: "server-room", side: "N", pos: 240, width: 160 },
  { id: "lounge-S", zoneId: "lounge", side: "S", pos: 240, width: 160 },
  // LOUNGE ↔ OPEN-SPACE hub (shared edge x440).
  { id: "lounge-E", zoneId: "lounge", side: "E", pos: 430, width: 160 },
  { id: "open-space-W", zoneId: "open-space", side: "W", pos: 430, width: 160 },
  // OPEN-SPACE hub ↔ KITCHEN (shared edge y280).
  { id: "open-space-N", zoneId: "open-space", side: "N", pos: 780, width: 200 },
  { id: "kitchen-S", zoneId: "kitchen", side: "S", pos: 780, width: 200 },
  // OPEN-SPACE hub ↔ CEO-OFFICE (shared edge x1120, upper).
  { id: "open-space-E", zoneId: "open-space", side: "E", pos: 430, width: 160 },
  { id: "ceo-office-W", zoneId: "ceo-office", side: "W", pos: 430, width: 160 },
  // OPEN-SPACE hub ↔ STUDIO (shared edge x1120, lower).
  { id: "open-space-E2", zoneId: "open-space", side: "E", pos: 710, width: 140 },
  { id: "studio-W", zoneId: "studio", side: "W", pos: 710, width: 140 },
  // RECEPTION ↔ TOILET — the restroom annex is entered from the front of house (shared edge y1000).
  { id: "reception-S2", zoneId: "reception", side: "S", pos: 1300, width: 120 },
  { id: "toilet-N", zoneId: "toilet", side: "N", pos: 1300, width: 120 },
  // KITCHEN ↔ TERRACE (shared edge x440).
  { id: "kitchen-W", zoneId: "kitchen", side: "W", pos: 160, width: 140 },
  { id: "terrace-E", zoneId: "terrace", side: "E", pos: 160, width: 140 },
  // KITCHEN ↔ MEETING-ROOM (shared edge x1120).
  { id: "kitchen-E", zoneId: "kitchen", side: "E", pos: 160, width: 140 },
  { id: "meeting-room-W", zoneId: "meeting-room", side: "W", pos: 160, width: 140 }
];
var OFFICE_ENTRY_DOOR_IDS = ["entryway-N", "reception-S"];

// src/world/office/seats.ts
var OFFICE_MEETING_TABLE = { x: 1300, y: 165 };
var MEETING_RING2 = { rx: 110, ry: 70 };
var OFFICE_MEETING_SEAT_COUNT = 8;
function buildMeetingSeats2() {
  const seats = [];
  for (let i = 0; i < OFFICE_MEETING_SEAT_COUNT; i++) {
    const angle = -Math.PI / 2 + i / OFFICE_MEETING_SEAT_COUNT * Math.PI * 2;
    const x = Math.round(OFFICE_MEETING_TABLE.x + Math.cos(angle) * MEETING_RING2.rx);
    const y = Math.round(OFFICE_MEETING_TABLE.y + Math.sin(angle) * MEETING_RING2.ry);
    const facing = Math.atan2(OFFICE_MEETING_TABLE.y - y, OFFICE_MEETING_TABLE.x - x);
    seats.push({ id: `meeting-seat-${i + 1}`, kind: "meeting-seat", icon: "\u{1F4BA}", label: `Meeting seat ${i + 1}`, x, y, facing });
  }
  return seats;
}
var OFFICE_MEETING_SEATS = buildMeetingSeats2();
var OFFICE_WORKSTATION_SEATS = [
  { id: "desk-1", kind: "workstation", icon: "\u{1F4BB}", label: "Desk", x: 620, y: 540, facing: -Math.PI / 2 },
  { id: "desk-2", kind: "workstation", icon: "\u{1F4BB}", label: "Desk", x: 740, y: 540, facing: -Math.PI / 2 },
  { id: "desk-3", kind: "workstation", icon: "\u{1F4BB}", label: "Desk", x: 860, y: 540, facing: -Math.PI / 2 },
  { id: "desk-4", kind: "workstation", icon: "\u{1F4BB}", label: "Desk", x: 980, y: 540, facing: -Math.PI / 2 }
];
var OTHER_SEATS2 = [
  // ── Lounge — conversation nook (sofa + armchair across a coffee table). ──
  { id: "lounge-sofa", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Sofa", x: 150, y: 400, facing: 0 },
  { id: "lounge-armchair", kind: "chair", icon: "\u{1FA91}", label: "Armchair", x: 330, y: 360, facing: Math.PI },
  // ── Reception — minimalist waiting lounge. ──
  { id: "reception-sofa", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Couch", x: 690, y: 920, facing: -Math.PI / 2 },
  { id: "reception-armchair", kind: "chair", icon: "\u{1FA91}", label: "Armchair", x: 590, y: 920, facing: -Math.PI / 2 },
  // ── Entryway — the waiting bench (two seats). ──
  { id: "entryway-bench-l", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Bench", x: 498, y: 1085, facing: Math.PI / 2 },
  { id: "entryway-bench-r", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Bench", x: 542, y: 1085, facing: Math.PI / 2 },
  // ── Kitchen — bar stools at the island + the dining nook. ──
  { id: "kitchen-bar-1", kind: "chair", icon: "\u{1FA91}", label: "Stool", x: 602, y: 210, facing: -Math.PI / 2 },
  { id: "kitchen-bar-2", kind: "chair", icon: "\u{1FA91}", label: "Stool", x: 632, y: 210, facing: -Math.PI / 2 },
  { id: "kitchen-bar-3", kind: "chair", icon: "\u{1FA91}", label: "Stool", x: 662, y: 210, facing: -Math.PI / 2 },
  { id: "kitchen-dine-1", kind: "chair", icon: "\u{1FA91}", label: "Chair", x: 922, y: 176, facing: 0 },
  { id: "kitchen-dine-2", kind: "chair", icon: "\u{1FA91}", label: "Chair", x: 1018, y: 176, facing: Math.PI },
  // ── CEO office — exec chair + guest lounge. ──
  { id: "ceo-chair", kind: "chair", icon: "\u{1FA91}", label: "CEO chair", x: 1300, y: 470, facing: -Math.PI / 2 },
  { id: "ceo-sofa", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Sofa", x: 1518, y: 470, facing: Math.PI },
  { id: "ceo-armchair", kind: "chair", icon: "\u{1FA91}", label: "Armchair", x: 1428, y: 470, facing: 0 },
  // ── Game room — gaming chair + TV-lounge sofa + beanbags. ──
  { id: "game-room-seat", kind: "chair", icon: "\u{1FA91}", label: "Game chair", x: 320, y: 1e3, facing: Math.PI },
  { id: "game-sofa", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Sofa", x: 104, y: 1e3, facing: Math.PI },
  { id: "game-beanbag-n", kind: "chair", icon: "\u{1FA91}", label: "Beanbag", x: 112, y: 952, facing: Math.PI },
  { id: "game-beanbag-s", kind: "chair", icon: "\u{1FA91}", label: "Beanbag", x: 112, y: 1048, facing: Math.PI },
  // ── Terrace — sun-loungers + outdoor sofa set. ──
  { id: "terrace-lounger-l", kind: "chair", icon: "\u{1FA91}", label: "Lounger", x: 92, y: 226, facing: Math.PI / 2 },
  { id: "terrace-lounger-r", kind: "chair", icon: "\u{1FA91}", label: "Lounger", x: 204, y: 226, facing: Math.PI / 2 },
  { id: "terrace-sofa", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Sofa", x: 356, y: 248, facing: -Math.PI / 2 },
  { id: "terrace-chair-l", kind: "chair", icon: "\u{1FA91}", label: "Chair", x: 242, y: 234, facing: Math.PI / 2 },
  { id: "terrace-chair-r", kind: "chair", icon: "\u{1FA91}", label: "Chair", x: 330, y: 234, facing: Math.PI / 2 },
  // ── Studio — design-desk chair + the ideation nook. ──
  { id: "studio-desk-chair", kind: "chair", icon: "\u{1FA91}", label: "Desk chair", x: 1360, y: 706, facing: -Math.PI / 2 },
  { id: "studio-nook-chair", kind: "chair", icon: "\u{1FA91}", label: "Armchair", x: 1502, y: 798, facing: -Math.PI / 2 },
  // ── Open Space — the CENTRAL biophilic island perch ring (4 benches) + the breakout lounge. ──
  { id: "openspace-island-n", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Bench", x: 600, y: 646, facing: Math.PI / 2 },
  { id: "openspace-island-s", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Bench", x: 600, y: 740, facing: -Math.PI / 2 },
  { id: "openspace-island-w", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Bench", x: 532, y: 692, facing: 0 },
  { id: "openspace-island-e", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Bench", x: 668, y: 692, facing: Math.PI },
  { id: "openspace-sofa", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Sofa", x: 968, y: 770, facing: -Math.PI / 2 },
  { id: "openspace-armchair-w", kind: "chair", icon: "\u{1FA91}", label: "Armchair", x: 902, y: 700, facing: 0 },
  { id: "openspace-armchair-e", kind: "chair", icon: "\u{1FA91}", label: "Armchair", x: 1022, y: 700, facing: Math.PI },
  // ── Toilet (restroom annex) — the cheeky private seat. ──
  { id: "toilet-seat", kind: "toilet", icon: "\u{1F6BD}", label: "Toilet", x: 1480, y: 1080, facing: Math.PI }
];
var OFFICE_SEATS_DATA = [...OFFICE_MEETING_SEATS, ...OFFICE_WORKSTATION_SEATS, ...OTHER_SEATS2];

// src/world/office/objects.ts
var OFFICE_OBJECTS_DATA = [
  { id: "coffee-machine", kind: "coffee", icon: "\u2615", label: "Coffee machine", x: 560, y: 170, effect: { presence: "coffee_break", animation: "coffee" } },
  { id: "workstation", kind: "desk", icon: "\u{1F4BB}", label: "Workstation", x: 780, y: 540, effect: { presence: "working", animation: "type" } },
  { id: "sofa", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "Sofa", x: 700, y: 920, effect: { presence: "focus", animation: "sit" } },
  { id: "tv", kind: "tv", icon: "\u{1F4FA}", label: "TV", x: 1490, y: 165, effect: { presence: "gathering", animation: "wave" } },
  { id: "arcade", kind: "arcade", icon: "\u{1F579}\uFE0F", label: "Arcade", x: 240, y: 1e3, effect: { presence: "gathering", animation: "dance" } },
  // 🎵 Jukebox — interact to TOGGLE the Lounge disco (player-driven, room-local).
  { id: "jukebox", kind: "jukebox", icon: "\u{1F3B5}", label: "Jukebox", x: 340, y: 340, effect: { animation: "dance", disco: true } }
];

// src/world/office/entry.ts
var OFFICE_ENTRY = {
  zoneId: "entryway",
  doorThreshold: { x: 800, y: 1e3 },
  bell: { x: 920, y: 1040 },
  doorSlab: { id: "entry-door", x: 740, y: 988, width: 120, height: 24 },
  doorIds: ["entryway-N", "reception-S"],
  doorReach: 100,
  bellReach: 95,
  knockHearing: 280,
  openMs: 9e3
};
function officeSpawn(seed) {
  const spread = seed * 53 % 200 - 100;
  return {
    x: clamp(800 + spread, 600, 1e3),
    y: 1080,
    facing: -Math.PI / 2
  };
}

// src/world/medieval-office/zones.ts
var MEDIEVAL_OFFICE_ZONES = [
  // ── Top band (y40..400) — craft & quiet spokes, each opens SOUTH onto the Hearth Hall ──
  { id: "library", name: "\u0411\u0438\u0431\u043B\u0438\u043E\u0442\u0435\u043A\u0430", x: 40, y: 40, width: 260, height: 360, isolated: false, openAir: false },
  { id: "smithy", name: "\u041A\u0443\u0437\u043D\u0438\u0446\u0430", x: 300, y: 40, width: 260, height: 360, isolated: false, openAir: false },
  { id: "scriptorium", name: "\u0421\u043A\u0440\u0438\u043F\u0442\u043E\u0440\u0438\u0439", x: 560, y: 40, width: 260, height: 360, isolated: false, openAir: false },
  { id: "war-room", name: "\u0412\u043E\u0435\u043D\u043D\u044B\u0439 \u0441\u043E\u0432\u0435\u0442", x: 820, y: 40, width: 260, height: 360, isolated: true, openAir: false },
  { id: "chapel", name: "\u0427\u0430\u0441\u043E\u0432\u043D\u044F", x: 1080, y: 40, width: 260, height: 360, isolated: false, openAir: false },
  // ── Hearth Hall — the central dominant concourse (feast hall · throne · trophy wall) ──
  { id: "hearth-hall", name: "\u041E\u0447\u0430\u0436\u043D\u044B\u0439 \u0437\u0430\u043B", x: 40, y: 400, width: 1300, height: 440, isolated: false, openAir: false },
  // ── Bottom band (y840..1160) — social spokes, each opens NORTH onto the Hearth Hall ──
  { id: "kitchen", name: "\u041A\u0443\u0445\u043D\u044F", x: 40, y: 840, width: 320, height: 320, isolated: false, openAir: false },
  { id: "tavern", name: "\u0422\u0430\u0432\u0435\u0440\u043D\u0430", x: 360, y: 840, width: 320, height: 320, isolated: false, openAir: false },
  { id: "barracks", name: "\u041A\u0430\u0437\u0430\u0440\u043C\u044B", x: 680, y: 840, width: 300, height: 320, isolated: false, openAir: false },
  { id: "wine-cellar", name: "\u0412\u0438\u043D\u043D\u044B\u0439 \u043F\u043E\u0433\u0440\u0435\u0431", x: 980, y: 840, width: 360, height: 320, isolated: true, openAir: false },
  // ── East wing (x1340..1560) — the discovery Vault over the Gatehouse (spawn) ──
  { id: "treasure-vault", name: "\u0421\u043E\u043A\u0440\u043E\u0432\u0438\u0449\u043D\u0438\u0446\u0430", x: 1340, y: 40, width: 220, height: 580, isolated: true, openAir: false },
  { id: "gatehouse", name: "\u0421\u0442\u043E\u0440\u043E\u0436\u0435\u0432\u0430\u044F \u0431\u0430\u0448\u043D\u044F", x: 1340, y: 620, width: 220, height: 540, isolated: true, openAir: false }
];
var MEDIEVAL_OFFICE_DOORS = [
  // ── Hearth Hall ↔ TOP-band spokes (hall N edge y400 ↔ each spoke's S edge) ──
  { id: "library-S", zoneId: "library", side: "S", pos: 170, width: 120 },
  { id: "hall-N-library", zoneId: "hearth-hall", side: "N", pos: 170, width: 120 },
  { id: "smithy-S", zoneId: "smithy", side: "S", pos: 430, width: 120 },
  { id: "hall-N-smithy", zoneId: "hearth-hall", side: "N", pos: 430, width: 120 },
  { id: "scriptorium-S", zoneId: "scriptorium", side: "S", pos: 690, width: 120 },
  { id: "hall-N-scriptorium", zoneId: "hearth-hall", side: "N", pos: 690, width: 120 },
  { id: "war-room-S", zoneId: "war-room", side: "S", pos: 950, width: 120 },
  { id: "hall-N-war-room", zoneId: "hearth-hall", side: "N", pos: 950, width: 120 },
  { id: "chapel-S", zoneId: "chapel", side: "S", pos: 1210, width: 120 },
  { id: "hall-N-chapel", zoneId: "hearth-hall", side: "N", pos: 1210, width: 120 },
  // ── Hearth Hall ↔ BOTTOM-band spokes (hall S edge y840 ↔ each spoke's N edge) ──
  { id: "kitchen-N", zoneId: "kitchen", side: "N", pos: 200, width: 120 },
  { id: "hall-S-kitchen", zoneId: "hearth-hall", side: "S", pos: 200, width: 120 },
  { id: "tavern-N", zoneId: "tavern", side: "N", pos: 520, width: 120 },
  { id: "hall-S-tavern", zoneId: "hearth-hall", side: "S", pos: 520, width: 120 },
  { id: "barracks-N", zoneId: "barracks", side: "N", pos: 830, width: 120 },
  { id: "hall-S-barracks", zoneId: "hearth-hall", side: "S", pos: 830, width: 120 },
  { id: "wine-cellar-N", zoneId: "wine-cellar", side: "N", pos: 1160, width: 120 },
  { id: "hall-S-wine-cellar", zoneId: "hearth-hall", side: "S", pos: 1160, width: 120 },
  // ── Hearth Hall ↔ Treasure Vault (hall E edge x1340 ↔ vault W edge), upper segment ──
  { id: "treasure-vault-W", zoneId: "treasure-vault", side: "W", pos: 510, width: 120 },
  { id: "hall-E-vault", zoneId: "hearth-hall", side: "E", pos: 510, width: 120 },
  // ── PORTCULLIS — the single controllable entry (gatehouse W ↔ hall E), lower segment.
  //    OPEN by default; host-sealable for the welcome ritual (see entry.ts). ──
  { id: "gatehouse-W", zoneId: "gatehouse", side: "W", pos: 730, width: 140 },
  { id: "hall-E-gatehouse", zoneId: "hearth-hall", side: "E", pos: 730, width: 140 }
];
var MEDIEVAL_OFFICE_ENTRY_DOOR_IDS = ["gatehouse-W", "hall-E-gatehouse"];

// src/world/medieval-office/seats.ts
var MEDIEVAL_OFFICE_MEETING_TABLE = { x: 950, y: 220 };
var MEETING_RING3 = { rx: 95, ry: 80 };
var MEDIEVAL_OFFICE_MEETING_SEAT_COUNT = 8;
function buildMeetingSeats3() {
  const seats = [];
  for (let i = 0; i < MEDIEVAL_OFFICE_MEETING_SEAT_COUNT; i++) {
    const angle = -Math.PI / 2 + i / MEDIEVAL_OFFICE_MEETING_SEAT_COUNT * Math.PI * 2;
    const x = Math.round(MEDIEVAL_OFFICE_MEETING_TABLE.x + Math.cos(angle) * MEETING_RING3.rx);
    const y = Math.round(MEDIEVAL_OFFICE_MEETING_TABLE.y + Math.sin(angle) * MEETING_RING3.ry);
    const facing = Math.atan2(MEDIEVAL_OFFICE_MEETING_TABLE.y - y, MEDIEVAL_OFFICE_MEETING_TABLE.x - x);
    seats.push({ id: `meeting-seat-${i + 1}`, kind: "meeting-seat", icon: "\u{1FA91}", label: `\u041A\u0440\u0435\u0441\u043B\u043E \u0441\u043E\u0432\u0435\u0442\u0430 ${i + 1}`, x, y, facing });
  }
  return seats;
}
var MEDIEVAL_OFFICE_MEETING_SEATS = buildMeetingSeats3();
var MEDIEVAL_OFFICE_WORKSTATION_SEATS = [
  { id: "desk-1", kind: "workstation", icon: "\u2712\uFE0F", label: "\u041F\u044E\u043F\u0438\u0442\u0440", x: 630, y: 175, facing: -Math.PI / 2 },
  { id: "desk-2", kind: "workstation", icon: "\u2712\uFE0F", label: "\u041F\u044E\u043F\u0438\u0442\u0440", x: 750, y: 175, facing: -Math.PI / 2 },
  { id: "desk-3", kind: "workstation", icon: "\u2712\uFE0F", label: "\u041F\u044E\u043F\u0438\u0442\u0440", x: 630, y: 320, facing: -Math.PI / 2 },
  { id: "desk-4", kind: "workstation", icon: "\u2712\uFE0F", label: "\u041F\u044E\u043F\u0438\u0442\u0440", x: 750, y: 320, facing: -Math.PI / 2 }
];
var OTHER_SEATS3 = [
  // ── Hearth Hall — the communal "Seat of the Guild" + two trestle feast tables flanking the hearth. ──
  { id: "guild-throne", kind: "chair", icon: "\u{1F451}", label: "\u0422\u0440\u043E\u043D \u0433\u0438\u043B\u044C\u0434\u0438\u0438", x: 120, y: 620, facing: 0 },
  { id: "feast-n-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 640, y: 472, facing: Math.PI / 2 },
  { id: "feast-n-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 740, y: 472, facing: Math.PI / 2 },
  { id: "feast-n-3", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 640, y: 528, facing: -Math.PI / 2 },
  { id: "feast-n-4", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 740, y: 528, facing: -Math.PI / 2 },
  { id: "feast-s-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 640, y: 712, facing: Math.PI / 2 },
  { id: "feast-s-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 740, y: 712, facing: Math.PI / 2 },
  { id: "feast-s-3", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 640, y: 768, facing: -Math.PI / 2 },
  { id: "feast-s-4", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 740, y: 768, facing: -Math.PI / 2 },
  // ── Library — reading table (2 chairs) + a cosy window armchair. ──
  { id: "library-read-l", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 140, y: 230, facing: 0 },
  { id: "library-read-r", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 210, y: 230, facing: Math.PI },
  { id: "library-armchair", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E \u0443 \u043E\u043A\u043D\u0430", x: 90, y: 340, facing: -Math.PI / 2 },
  // ── Smithy — work-stools around the shared crafting bench. ──
  { id: "smithy-stool-1", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 390, y: 300, facing: 0 },
  { id: "smithy-stool-2", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 470, y: 300, facing: Math.PI },
  { id: "smithy-stool-3", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 430, y: 340, facing: -Math.PI / 2 },
  // ── Chapel — two short rows of prayer pews facing the altar. ──
  { id: "chapel-pew-1", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1160, y: 260, facing: -Math.PI / 2 },
  { id: "chapel-pew-2", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1260, y: 260, facing: -Math.PI / 2 },
  { id: "chapel-pew-3", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1160, y: 320, facing: -Math.PI / 2 },
  { id: "chapel-pew-4", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1260, y: 320, facing: -Math.PI / 2 },
  // ── Kitchen — stools around the central prep island. ──
  { id: "kitchen-stool-1", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 200, y: 1045, facing: -Math.PI / 2 },
  { id: "kitchen-stool-2", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 240, y: 1052, facing: -Math.PI / 2 },
  { id: "kitchen-stool-3", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 280, y: 1045, facing: -Math.PI / 2 },
  // ── Tavern — barstools at the ale-bar + a fireside armchair. ──
  { id: "tavern-bar-1", kind: "chair", icon: "\u{1FA91}", label: "\u0411\u0430\u0440\u043D\u044B\u0439 \u0442\u0430\u0431\u0443\u0440\u0435\u0442", x: 430, y: 1095, facing: Math.PI / 2 },
  { id: "tavern-bar-2", kind: "chair", icon: "\u{1FA91}", label: "\u0411\u0430\u0440\u043D\u044B\u0439 \u0442\u0430\u0431\u0443\u0440\u0435\u0442", x: 480, y: 1095, facing: Math.PI / 2 },
  { id: "tavern-bar-3", kind: "chair", icon: "\u{1FA91}", label: "\u0411\u0430\u0440\u043D\u044B\u0439 \u0442\u0430\u0431\u0443\u0440\u0435\u0442", x: 530, y: 1095, facing: Math.PI / 2 },
  { id: "tavern-armchair", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E \u0443 \u043A\u0430\u043C\u0438\u043D\u0430", x: 615, y: 1075, facing: Math.PI },
  // ── Barracks — stools around the dicing/cards table by the fire. ──
  { id: "barracks-stool-1", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 790, y: 1e3, facing: 0 },
  { id: "barracks-stool-2", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 870, y: 1e3, facing: Math.PI },
  { id: "barracks-stool-3", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 830, y: 960, facing: Math.PI / 2 },
  { id: "barracks-stool-4", kind: "chair", icon: "\u{1FA91}", label: "\u0422\u0430\u0431\u0443\u0440\u0435\u0442", x: 830, y: 1040, facing: -Math.PI / 2 },
  // ── Wine Cellar — the candlelit tasting table (intimate sealed nook). ──
  { id: "cellar-chair-1", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 1110, y: 1e3, facing: 0 },
  { id: "cellar-chair-2", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 1210, y: 1e3, facing: Math.PI },
  { id: "cellar-chair-3", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 1160, y: 955, facing: Math.PI / 2 },
  { id: "cellar-chair-4", kind: "chair", icon: "\u{1FA91}", label: "\u041A\u0440\u0435\u0441\u043B\u043E", x: 1160, y: 1045, facing: -Math.PI / 2 },
  // ── Treasure Vault — a velvet viewing bench facing the relic pedestal. ──
  { id: "vault-bench", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1450, y: 470, facing: -Math.PI / 2 },
  // ── Gatehouse — two stone arrival benches along the side walls. ──
  { id: "gatehouse-bench-l", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1400, y: 1e3, facing: 0 },
  { id: "gatehouse-bench-r", kind: "sofa", icon: "\u{1FA91}", label: "\u0421\u043A\u0430\u043C\u044C\u044F", x: 1500, y: 1e3, facing: Math.PI }
];
var MEDIEVAL_OFFICE_SEATS = [
  ...MEDIEVAL_OFFICE_MEETING_SEATS,
  ...MEDIEVAL_OFFICE_WORKSTATION_SEATS,
  ...OTHER_SEATS3
];

// src/world/medieval-office/objects.ts
var MEDIEVAL_OFFICE_OBJECTS = [
  { id: "cauldron", kind: "coffee", icon: "\u{1F372}", label: "\u041A\u043E\u0442\u0451\u043B", x: 90, y: 970, effect: { presence: "coffee_break", animation: "coffee" } },
  { id: "scribe-lectern", kind: "desk", icon: "\u2712\uFE0F", label: "\u041F\u0435\u0440\u0435\u043F\u0438\u0441\u043A\u0430", x: 690, y: 95, effect: { presence: "working", animation: "type" } },
  { id: "barracks-settle", kind: "sofa", icon: "\u{1F6CB}\uFE0F", label: "\u0421\u043A\u0430\u043C\u044C\u044F \u0443 \u043A\u0430\u043C\u0438\u043D\u0430", x: 740, y: 1042, effect: { presence: "gathering", animation: "sit" } },
  { id: "campaign-map", kind: "tv", icon: "\u{1F5FA}\uFE0F", label: "\u041A\u0430\u0440\u0442\u0430 \u043F\u043E\u0445\u043E\u0434\u0430", x: 950, y: 90, effect: { presence: "focus", animation: "wave" } },
  { id: "games-table", kind: "arcade", icon: "\u{1F3B2}", label: "\u0418\u0433\u0440\u044B", x: 560, y: 1010, effect: { presence: "gathering", animation: "laugh" } },
  // 🪕 Bard's stage — interact to TOGGLE the Tavern disco (player-driven, room-local sing-along).
  { id: "bard-stage", kind: "jukebox", icon: "\u{1FA95}", label: "\u0421\u0446\u0435\u043D\u0430 \u0431\u0430\u0440\u0434\u0430", x: 440, y: 920, effect: { animation: "dance", disco: true } }
];

// src/world/medieval-office/hazards.ts
var MEDIEVAL_OFFICE_FIRE_HAZARDS = [
  { id: "great-hearth", x: 690, y: 620, r: 58 },
  // the hall's central hearth (dead-centre)
  { id: "forge", x: 430, y: 104, r: 38 },
  // the Smithy forge (north wall)
  { id: "kitchen-fire", x: 100, y: 902, r: 34 }
  // the Kitchen cooking-fire (NW corner)
];

// src/world/medieval-office/entry.ts
var MEDIEVAL_OFFICE_ENTRY = {
  zoneId: "gatehouse",
  doorThreshold: { x: 1340, y: 730 },
  bell: { x: 1420, y: 760 },
  doorSlab: { id: "entry-door", x: 1328, y: 660, width: 24, height: 140 },
  doorIds: ["gatehouse-W", "hall-E-gatehouse"],
  doorReach: 100,
  bellReach: 95,
  knockHearing: 280,
  openMs: 9e3
};
function medievalOfficeSpawn(seed) {
  const spread = seed * 53 % 160 - 80;
  return {
    x: 1460,
    y: clamp(770 + spread, 690, 1080),
    facing: Math.PI
  };
}

// src/world/levels.ts
function pairKey(a, b) {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}
function buildLevel(data) {
  const doors = data.doors;
  const entryDoorIds = /* @__PURE__ */ new Set([...data.entryDoorIds, ...data.entry.doorIds]);
  const walls = buildWalls(data.zones, doors);
  const wallsDoorClosed = [...walls, data.entry.doorSlab];
  const doorways = buildDoorways(data.zones, doors, entryDoorIds);
  const neighbors = /* @__PURE__ */ new Map();
  const joiningDoorway = /* @__PURE__ */ new Map();
  const link = (a, b, doorwayId) => {
    joiningDoorway.set(pairKey(a, b), doorwayId);
    (neighbors.get(a) ?? neighbors.set(a, /* @__PURE__ */ new Set()).get(a)).add(b);
    (neighbors.get(b) ?? neighbors.set(b, /* @__PURE__ */ new Set()).get(b)).add(a);
  };
  for (const dw of doorways) link(dw.zoneIds[0], dw.zoneIds[1], dw.id);
  const entryZones = [...new Set(doors.filter((d) => entryDoorIds.has(d.id)).map((d) => d.zoneId))];
  if (entryZones.length === 2) link(entryZones[0], entryZones[1], ENTRY_DOORWAY_ID);
  return {
    id: data.id,
    worldWidth: data.worldWidth,
    worldHeight: data.worldHeight,
    zones: data.zones,
    doors,
    walls,
    wallsDoorClosed,
    doorways,
    objects: data.objects,
    seats: data.seats,
    fireHazards: data.fireHazards,
    entry: data.entry,
    spawn: data.spawn,
    meetingTable: data.meetingTable,
    meetingSeatIds: Array.from({ length: data.meetingSeatCount }, (_, i) => `meeting-seat-${i + 1}`),
    meetingSeatCount: data.meetingSeatCount,
    workstationSeatIds: data.workstationSeatIds,
    neighbors,
    joiningDoorway
  };
}
var officeData = {
  id: "office",
  worldWidth: 1600,
  worldHeight: 1200,
  zones: OFFICE_ZONES_DATA,
  doors: OFFICE_DOORS_DATA,
  entryDoorIds: OFFICE_ENTRY_DOOR_IDS,
  objects: OFFICE_OBJECTS_DATA,
  seats: OFFICE_SEATS_DATA,
  fireHazards: [],
  entry: OFFICE_ENTRY,
  spawn: officeSpawn,
  meetingTable: OFFICE_MEETING_TABLE,
  meetingSeatCount: OFFICE_MEETING_SEAT_COUNT,
  workstationSeatIds: OFFICE_WORKSTATION_SEATS.map((s) => s.id)
};
var castleData = {
  id: "medieval_castle",
  worldWidth: 3600,
  worldHeight: 2680,
  zones: CASTLE_ZONES,
  doors: CASTLE_DOORS,
  entryDoorIds: CASTLE_ENTRY_DOOR_IDS,
  objects: CASTLE_OBJECTS,
  seats: CASTLE_SEATS,
  fireHazards: CASTLE_FIRE_HAZARDS,
  entry: CASTLE_ENTRY,
  spawn: castleSpawn,
  meetingTable: CASTLE_MEETING_TABLE,
  meetingSeatCount: CASTLE_MEETING_SEAT_COUNT,
  workstationSeatIds: CASTLE_WORKSTATION_SEATS.map((s) => s.id)
};
var medievalOfficeData = {
  id: "medieval_office",
  worldWidth: 1600,
  worldHeight: 1200,
  zones: MEDIEVAL_OFFICE_ZONES,
  doors: MEDIEVAL_OFFICE_DOORS,
  entryDoorIds: MEDIEVAL_OFFICE_ENTRY_DOOR_IDS,
  objects: MEDIEVAL_OFFICE_OBJECTS,
  seats: MEDIEVAL_OFFICE_SEATS,
  fireHazards: MEDIEVAL_OFFICE_FIRE_HAZARDS,
  entry: MEDIEVAL_OFFICE_ENTRY,
  spawn: medievalOfficeSpawn,
  meetingTable: MEDIEVAL_OFFICE_MEETING_TABLE,
  meetingSeatCount: MEDIEVAL_OFFICE_MEETING_SEAT_COUNT,
  workstationSeatIds: MEDIEVAL_OFFICE_WORKSTATION_SEATS.map((s) => s.id)
};
var LEVELS = {
  office: buildLevel(officeData),
  medieval_castle: buildLevel(castleData),
  medieval_office: buildLevel(medievalOfficeData)
};
function levelForTheme(theme) {
  if (theme === "medieval_castle") return LEVELS.medieval_castle;
  if (theme === "medieval_office") return LEVELS.medieval_office;
  return LEVELS.office;
}

// src/world/zones.ts
var OFFICE_ZONES = LEVELS.office.zones;
var OFFICE_DOORS = LEVELS.office.doors;
var OFFICE_WALLS = LEVELS.office.walls;
function zoneAt(x, y, zones = OFFICE_ZONES) {
  return zoneAtIn(x, y, zones);
}
function resolveWallCollision(prevX, prevY, nextX, nextY, radius, walls = OFFICE_WALLS) {
  return resolveWallCollisionIn(prevX, prevY, nextX, nextY, radius, walls);
}

// src/world/roster.ts
var OPEN_FLOOR_NAME = "Open floor";
function groupOccupantsByRoom(occupants, selfId, zones = OFFICE_ZONES) {
  const SELF_ZONE = selfId ? occupants.find((o) => o.id === selfId) ?? null : null;
  const selfZoneId = SELF_ZONE ? zoneAt(SELF_ZONE.x, SELF_ZONE.y, zones)?.id ?? null : void 0;
  const OPEN = "\0open-floor";
  const buckets = /* @__PURE__ */ new Map();
  for (const o of occupants) {
    const zone = zoneAt(o.x, o.y, zones);
    const key = zone ? zone.id : OPEN;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { zoneId: zone?.id ?? null, zoneName: zone?.name ?? OPEN_FLOOR_NAME, occupants: [] };
      buckets.set(key, bucket);
    }
    if (o.id === selfId) bucket.occupants.unshift(o);
    else bucket.occupants.push(o);
  }
  const groups = [...buckets.values()].map((b) => ({
    zoneId: b.zoneId,
    zoneName: b.zoneName,
    isCurrent: selfZoneId !== void 0 && b.zoneId === selfZoneId,
    occupants: b.occupants
  }));
  groups.sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    const aOpen = a.zoneId === null;
    const bOpen = b.zoneId === null;
    if (aOpen !== bOpen) return aOpen ? 1 : -1;
    if (a.occupants.length !== b.occupants.length) return b.occupants.length - a.occupants.length;
    return a.zoneName.localeCompare(b.zoneName);
  });
  return groups;
}

// src/world/entryway.ts
var OFFICE = LEVELS.office;
var ENTRY = OFFICE.entry;
var ENTRYWAY_ZONE_ID = ENTRY.zoneId;
var ENTRY_DOOR_THRESHOLD = ENTRY.doorThreshold;
var ENTRY_BELL = ENTRY.bell;
var ENTRY_DOOR_REACH = ENTRY.doorReach;
var ENTRY_BELL_REACH = ENTRY.bellReach;
var ENTRY_KNOCK_HEARING_RANGE = ENTRY.knockHearing;
var ENTRY_DOOR_OPEN_MS = ENTRY.openMs;
var ENTRY_DOOR_SLAB = ENTRY.doorSlab;
function officeWalls(doorOpen) {
  return doorOpen ? OFFICE.walls : OFFICE.wallsDoorClosed;
}
var P = entryPredicates(OFFICE.zones, ENTRY);
function isInEntryway(x, y) {
  return P.isInEntryway(x, y);
}
function withinBellReach(x, y) {
  return P.withinBellReach(x, y);
}
function withinDoorReach(x, y) {
  return P.withinDoorReach(x, y);
}
function canRingEntryBell(x, y) {
  return P.canRingEntryBell(x, y);
}
function canKnockEntryDoor(x, y) {
  return P.canKnockEntryDoor(x, y);
}
function canOpenEntryDoor(x, y, isHost) {
  return P.canOpenEntryDoor(x, y, isHost);
}
function withinKnockHearing(x, y) {
  return P.withinKnockHearing(x, y);
}

// src/world/doors.ts
var OFFICE_DOORWAYS = LEVELS.office.doorways;
var DOORWAY_BY_ID = new Map(OFFICE_DOORWAYS.map((d) => [d.id, d]));
function doorwayById(id) {
  return DOORWAY_BY_ID.get(id);
}
function distSq2(ax, ay, bx, by) {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}
function nearestDoorway(x, y) {
  let best = null;
  let bestD = DOOR_REACH * DOOR_REACH;
  for (const dw of OFFICE_DOORWAYS) {
    const d = distSq2(x, y, dw.threshold.x, dw.threshold.y);
    if (d <= bestD) {
      bestD = d;
      best = dw;
    }
  }
  return best;
}
function canToggleDoor(x, y, doorway, isHost) {
  if (isHost) return true;
  return distSq2(x, y, doorway.threshold.x, doorway.threshold.y) <= DOOR_REACH * DOOR_REACH;
}
function closedDoorSlabs(closedIds) {
  const slabs = [];
  for (const id of closedIds) {
    const dw = DOORWAY_BY_ID.get(id);
    if (dw) slabs.push(dw.slab);
  }
  return slabs;
}
function composeWalls(entryDoorOpen, closedDoorwayIds) {
  const base = officeWalls(entryDoorOpen);
  const slabs = closedDoorSlabs(closedDoorwayIds);
  return slabs.length === 0 ? base : [...base, ...slabs];
}

// src/world/adjacency.ts
var NEIGHBOR_VOICE_GAIN = 0.5;
var NEIGHBOR_PRESENCE_ALPHA = 0.5;
function pairKey2(a, b) {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}
var OFFICE2 = LEVELS.office;
function zonesAreNeighborsIn(level, a, b) {
  if (a === null || b === null || a === b) return false;
  return level.neighbors.get(a)?.has(b) ?? false;
}
function zonesConnectedOpenIn(level, a, b, doors) {
  if (a === null || b === null || a === b) return false;
  const doorwayId = level.joiningDoorway.get(pairKey2(a, b));
  if (doorwayId === void 0) return false;
  return doorwayId === ENTRY_DOORWAY_ID ? doors.entryDoorOpen : !doors.closedDoorwayIds.has(doorwayId);
}
function zoneVoiceGainIn(level, listenerZone, speakerZone, doors) {
  const base = canHearAcrossZones(listenerZone, speakerZone);
  if (base.audible) return { ...base, gain: 1 };
  if (zonesConnectedOpenIn(level, listenerZone?.id ?? null, speakerZone?.id ?? null, doors)) {
    const bothOpenAir = listenerZone?.openAir === true && speakerZone?.openAir === true;
    return { audible: true, ignoreRange: !bothOpenAir, gain: NEIGHBOR_VOICE_GAIN };
  }
  return { audible: false, ignoreRange: false, gain: 0 };
}
function zonesAreNeighbors(a, b) {
  return zonesAreNeighborsIn(OFFICE2, a, b);
}
function zonesConnectedOpen(a, b, doors) {
  return zonesConnectedOpenIn(OFFICE2, a, b, doors);
}
function zoneVoiceGain(listenerZone, speakerZone, doors) {
  return zoneVoiceGainIn(OFFICE2, listenerZone, speakerZone, doors);
}

// src/world/objects-types.ts
var OBJECT_INTERACT_RANGE = 90;

// src/world/objects.ts
var OFFICE_OBJECTS = LEVELS.office.objects;
var OBJECT_BY_ID = new Map(OFFICE_OBJECTS.map((o) => [o.id, o]));
function objectById(id) {
  return OBJECT_BY_ID.get(id) ?? null;
}
var EFFECT_BY_KIND = OFFICE_OBJECTS.reduce(
  (acc, o) => {
    if (!acc[o.kind]) acc[o.kind] = o.effect;
    return acc;
  },
  {}
);
function objectEffectForKind(kind) {
  return EFFECT_BY_KIND[kind] ?? null;
}
function nearestObject(x, y, range = OBJECT_INTERACT_RANGE) {
  let best = null;
  let bestSq = range * range;
  for (const o of OFFICE_OBJECTS) {
    const dx = o.x - x;
    const dy = o.y - y;
    const sq = dx * dx + dy * dy;
    if (sq <= bestSq) {
      bestSq = sq;
      best = o;
    }
  }
  return best;
}
function withinInteractRange(id, x, y) {
  const o = objectById(id);
  if (!o) return false;
  const dx = o.x - x;
  const dy = o.y - y;
  return dx * dx + dy * dy <= OBJECT_INTERACT_RANGE * OBJECT_INTERACT_RANGE;
}

// src/world/seats-types.ts
var SEAT_INTERACT_RANGE = 70;

// src/world/seats.ts
var MEETING_TABLE = OFFICE_MEETING_TABLE;
var MEETING_SEAT_COUNT = OFFICE_MEETING_SEAT_COUNT;
var MEETING_SEATS = OFFICE_MEETING_SEATS;
var MEETING_SEAT_IDS = MEETING_SEATS.map((s) => s.id);
var WORKSTATION_SEAT_IDS = OFFICE_WORKSTATION_SEATS.map((s) => s.id);
var OFFICE_SEATS = LEVELS.office.seats;
var SEAT_BY_ID = new Map(OFFICE_SEATS.map((s) => [s.id, s]));
function seatById(id) {
  return SEAT_BY_ID.get(id) ?? null;
}
function isWorkstationSeat(seatId) {
  const s = seatById(seatId);
  return s !== null && s.kind === "workstation";
}
function nearestSeat(x, y, range = SEAT_INTERACT_RANGE) {
  let best = null;
  let bestSq = range * range;
  for (const s of OFFICE_SEATS) {
    const dx = s.x - x;
    const dy = s.y - y;
    const sq = dx * dx + dy * dy;
    if (sq <= bestSq) {
      bestSq = sq;
      best = s;
    }
  }
  return best;
}
function withinSeatRange(id, x, y) {
  const s = seatById(id);
  if (!s) return false;
  const dx = s.x - x;
  const dy = s.y - y;
  return dx * dx + dy * dy <= SEAT_INTERACT_RANGE * SEAT_INTERACT_RANGE;
}

// src/world/hazards.ts
var FIRE_HAZARDS = LEVELS.office.fireHazards;
function fireHazardAt(x, y, hazards = FIRE_HAZARDS) {
  return fireHazardAtIn(x, y, hazards);
}
function isInFire(x, y, hazards = FIRE_HAZARDS) {
  return fireHazardAt(x, y, hazards) !== null;
}
var HELD_ITEMS = [
  { id: "guitar", name: "Guitar", emoji: "\u{1F3B8}", style: "guitar" },
  { id: "laptop", name: "Laptop", emoji: "\u{1F4BB}", style: "laptop" },
  { id: "coffee", name: "Coffee", emoji: "\u2615", style: "coffee" },
  { id: "pizza", name: "Pizza slice", emoji: "\u{1F355}", style: "pizza" },
  { id: "bat", name: "Baseball bat", emoji: "\u{1F3CF}", style: "bat" },
  { id: "flyswatter", name: "Flyswatter", emoji: "\u{1FAB0}", style: "flyswatter" },
  { id: "clipboard", name: "Clipboard", emoji: "\u{1F4CB}", style: "clipboard" },
  { id: "phone", name: "Phone", emoji: "\u{1F4F1}", style: "phone" },
  { id: "balloon", name: "Balloon", emoji: "\u{1F388}", style: "balloon" },
  { id: "mug", name: "Beer mug", emoji: "\u{1F37A}", style: "mug" }
];
function heldItemById(id) {
  if (!id) return null;
  return HELD_ITEMS.find((i) => i.id === id) ?? null;
}
var HELD_ITEM_IDS = HELD_ITEMS.map((i) => i.id);
var HeldItemIdSchema = z.enum(HELD_ITEM_IDS);
var HeldItemValueSchema = z.union([z.literal(""), HeldItemIdSchema]);
function isHeldItemValue(id) {
  return id === "" || HELD_ITEMS.some((i) => i.id === id);
}
var WEATHER_KINDS = ["clear", "cloudy", "rain"];
var WeatherSchema = z.enum(WEATHER_KINDS);
function dayPhase(t) {
  const x = (t % 1 + 1) % 1;
  if (x < 0.22 || x >= 0.85) return "night";
  if (x < 0.3) return "dawn";
  if (x < 0.75) return "day";
  return "dusk";
}
function daylight(t) {
  const v = (Math.sin((t - 0.25) * Math.PI * 2) + 1) / 2;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
var WORLD_EVENT_KINDS = ["none", "pizza", "fire_drill"];
var WorldEventKindSchema = z.enum(WORLD_EVENT_KINDS);
var WORLD_EVENT_LABELS = {
  pizza: { icon: "\u{1F355}", title: "Pizza Party!", blurb: "Fresh pizza in the lounge \u2014 grab a slice together." },
  fire_drill: { icon: "\u{1F9EF}", title: "Fire Drill!", blurb: "Everyone to the meeting point \u2014 together, quick!" }
};

// src/world/room-route.ts
function buildRoomPath(levelType, roomKey) {
  return `/${levelType}/${encodeURIComponent(roomKey)}`;
}

// src/world/office-decor.generated.ts
var OFFICE_DECOR_DATA = [
  { id: "office-decor-1", kind: "planter-box-wood", x: 96, y: 84 },
  { id: "office-decor-2", kind: "plant-fern", x: 96, y: 84 },
  { id: "office-decor-3", kind: "planter-box-wood", x: 174, y: 84 },
  { id: "office-decor-4", kind: "plant-fern", x: 174, y: 84 },
  { id: "office-decor-5", kind: "planter-box-wood", x: 252, y: 84 },
  { id: "office-decor-6", kind: "plant-fern", x: 252, y: 84 },
  { id: "office-decor-7", kind: "planter-box-wood", x: 330, y: 84 },
  { id: "office-decor-8", kind: "plant-fern", x: 330, y: 84 },
  { id: "office-decor-9", kind: "planter-box-wood", x: 80, y: 150 },
  { id: "office-decor-10", kind: "plant-leafy", x: 80, y: 148 },
  { id: "office-decor-11", kind: "rug", x: 148, y: 218, w: 164, h: 80 },
  { id: "office-decor-12", kind: "coffee-table", x: 148, y: 216 },
  { id: "office-decor-13", kind: "sun-lounger-l", x: 92, y: 226 },
  { id: "office-decor-14", kind: "sun-lounger-r", x: 204, y: 226 },
  { id: "office-decor-15", kind: "parasol-striped", x: 168, y: 208 },
  { id: "office-decor-16", kind: "rug", x: 286, y: 234, w: 128, h: 58 },
  { id: "office-decor-17", kind: "outdoor-sofa-patio", x: 356, y: 248 },
  { id: "office-decor-18", kind: "coffee-table", x: 286, y: 238 },
  { id: "office-decor-19", kind: "armchair", x: 242, y: 234 },
  { id: "office-decor-20", kind: "armchair", x: 330, y: 234 },
  { id: "office-decor-21", kind: "coffee-station", x: 560, y: 170 },
  { id: "office-decor-22", kind: "fridge-tall", x: 1022, y: 110 },
  { id: "office-decor-23", kind: "prep-island", x: 632, y: 158 },
  { id: "office-decor-24", kind: "fruit-bowl", x: 652, y: 146 },
  { id: "office-decor-25", kind: "bar-stool", x: 602, y: 210 },
  { id: "office-decor-26", kind: "bar-stool", x: 632, y: 210 },
  { id: "office-decor-27", kind: "bar-stool", x: 662, y: 210 },
  { id: "office-decor-28", kind: "desk-chair-down", x: 922, y: 176 },
  { id: "office-decor-29", kind: "desk-chair-down", x: 1018, y: 176 },
  { id: "office-decor-30", kind: "island-table", x: 970, y: 176 },
  { id: "office-decor-31", kind: "plant-tall", x: 466, y: 250 },
  { id: "office-decor-32", kind: "plant-leafy", x: 1092, y: 248 },
  { id: "office-decor-33", kind: "rug", x: 1300, y: 165, w: 260, h: 180 },
  { id: "office-decor-34", kind: "conference-table", x: 1300, y: 165 },
  { id: "office-decor-35", kind: "board-chair-1", x: 1300, y: 95 },
  { id: "office-decor-36", kind: "board-chair-2", x: 1377.78, y: 115.5 },
  { id: "office-decor-37", kind: "board-chair-3", x: 1410, y: 165 },
  { id: "office-decor-38", kind: "board-chair-4", x: 1377.78, y: 214.5 },
  { id: "office-decor-39", kind: "board-chair-5", x: 1300, y: 235 },
  { id: "office-decor-40", kind: "board-chair-6", x: 1222.22, y: 214.5 },
  { id: "office-decor-41", kind: "board-chair-7", x: 1190, y: 165 },
  { id: "office-decor-42", kind: "board-chair-8", x: 1222.22, y: 115.5 },
  { id: "office-decor-43", kind: "whiteboard", x: 1396, y: 70 },
  { id: "office-decor-44", kind: "cabinet", x: 1430, y: 256 },
  { id: "office-decor-45", kind: "plant-tall", x: 1532, y: 70 },
  { id: "office-decor-46", kind: "plant-leafy", x: 1148, y: 250 },
  { id: "office-decor-47", kind: "rug", x: 240, y: 388, w: 250, h: 150 },
  { id: "office-decor-48", kind: "sofa", x: 150, y: 400, rotation: -1.5708 },
  { id: "office-decor-49", kind: "armchair", x: 330, y: 360, rotation: 1.5708 },
  { id: "office-decor-50", kind: "coffee-table", x: 240, y: 388 },
  { id: "office-decor-51", kind: "bookshelf", x: 200, y: 328 },
  { id: "office-decor-52", kind: "wall-painting", x: 300, y: 300 },
  { id: "office-decor-53", kind: "lamp", x: 78, y: 470 },
  { id: "office-decor-54", kind: "lounge-side-table", x: 130, y: 478 },
  { id: "office-decor-55", kind: "hookah", x: 92, y: 540 },
  { id: "office-decor-56", kind: "plant-tall", x: 70, y: 334 },
  { id: "office-decor-57", kind: "plant-leafy", x: 408, y: 540 },
  { id: "office-decor-58", kind: "rug", x: 800, y: 540, w: 500, h: 132 },
  { id: "office-decor-59", kind: "monitor", x: 620, y: 528 },
  { id: "office-decor-60", kind: "long-desk", x: 620, y: 542, w: 96 },
  { id: "office-decor-61", kind: "desk-chair-up", x: 620, y: 566 },
  { id: "office-decor-62", kind: "desk-mug", x: 650, y: 538 },
  { id: "office-decor-63", kind: "computer", x: 740, y: 528 },
  { id: "office-decor-64", kind: "long-desk", x: 740, y: 542, w: 96 },
  { id: "office-decor-65", kind: "desk-chair-up", x: 740, y: 566 },
  { id: "office-decor-66", kind: "desk-papers", x: 770, y: 538 },
  { id: "office-decor-67", kind: "monitor", x: 860, y: 528 },
  { id: "office-decor-68", kind: "long-desk", x: 860, y: 542, w: 96 },
  { id: "office-decor-69", kind: "desk-chair-up", x: 860, y: 566 },
  { id: "office-decor-70", kind: "desk-plant", x: 890, y: 538 },
  { id: "office-decor-71", kind: "computer", x: 980, y: 528 },
  { id: "office-decor-72", kind: "long-desk", x: 980, y: 542, w: 96 },
  { id: "office-decor-73", kind: "desk-chair-up", x: 980, y: 566 },
  { id: "office-decor-74", kind: "desk-mug", x: 1010, y: 538 },
  { id: "office-decor-75", kind: "rug", x: 600, y: 700, w: 210, h: 186 },
  { id: "office-decor-76", kind: "biophilic-island", x: 600, y: 692 },
  { id: "office-decor-77", kind: "rug", x: 968, y: 724, w: 218, h: 176 },
  { id: "office-decor-78", kind: "sofa", x: 968, y: 770 },
  { id: "office-decor-79", kind: "coffee-table", x: 968, y: 718 },
  { id: "office-decor-80", kind: "armchair", x: 902, y: 700 },
  { id: "office-decor-81", kind: "armchair", x: 1022, y: 700 },
  { id: "office-decor-82", kind: "whiteboard", x: 575, y: 308 },
  { id: "office-decor-83", kind: "wall-abstract", x: 658, y: 308 },
  { id: "office-decor-84", kind: "wall-poster", x: 902, y: 308 },
  { id: "office-decor-85", kind: "coffee-counter", x: 965, y: 440 },
  { id: "office-decor-86", kind: "water-cooler", x: 1035, y: 426 },
  { id: "office-decor-87", kind: "bookshelf", x: 476, y: 575 },
  { id: "office-decor-88", kind: "wall-painting", x: 520, y: 818 },
  { id: "office-decor-89", kind: "wall-poster", x: 1040, y: 818 },
  { id: "office-decor-90", kind: "plant-leafy", x: 472, y: 324 },
  { id: "office-decor-91", kind: "plant-fern", x: 498, y: 800 },
  { id: "office-decor-92", kind: "plant-tall", x: 1e3, y: 660 },
  { id: "office-decor-93", kind: "plant-tall", x: 1e3, y: 360 },
  { id: "office-decor-94", kind: "lamp", x: 1010, y: 800 },
  { id: "office-decor-95", kind: "office-chair", x: 1300, y: 470 },
  { id: "office-decor-96", kind: "long-desk", x: 1300, y: 430, w: 132 },
  { id: "office-decor-97", kind: "desk-laptop", x: 1298, y: 426 },
  { id: "office-decor-98", kind: "monitor", x: 1344, y: 424 },
  { id: "office-decor-99", kind: "cabinet", x: 1300, y: 310 },
  { id: "office-decor-100", kind: "wall-painting", x: 1244, y: 306 },
  { id: "office-decor-101", kind: "wall-abstract", x: 1356, y: 306 },
  { id: "office-decor-102", kind: "cabinet", x: 1496, y: 310 },
  { id: "office-decor-103", kind: "bookshelf", x: 1530, y: 376 },
  { id: "office-decor-104", kind: "plant-tall", x: 1534, y: 310 },
  { id: "office-decor-105", kind: "rug", x: 1466, y: 470, w: 180, h: 140 },
  { id: "office-decor-106", kind: "sofa", x: 1518, y: 470, rotation: 1.5708 },
  { id: "office-decor-107", kind: "armchair", x: 1428, y: 470, rotation: -1.5708 },
  { id: "office-decor-108", kind: "coffee-table", x: 1466, y: 470 },
  { id: "office-decor-109", kind: "plant-leafy", x: 1532, y: 546 },
  { id: "office-decor-110", kind: "accent-lamp", x: 1214, y: 426 },
  { id: "office-decor-111", kind: "accent-lamp", x: 1466, y: 540 },
  { id: "office-decor-112", kind: "server-rack-5u-1", x: 88, y: 696 },
  { id: "office-decor-113", kind: "server-rack-7u-2", x: 88, y: 758 },
  { id: "office-decor-114", kind: "server-rack-5u-3", x: 392, y: 696 },
  { id: "office-decor-115", kind: "server-rack-6u-5", x: 392, y: 776 },
  { id: "office-decor-116", kind: "server-rack-4u-4", x: 132, y: 708 },
  { id: "office-decor-117", kind: "server-rack-4u-6", x: 348, y: 708 },
  { id: "office-decor-118", kind: "cooling-unit", x: 383, y: 738 },
  { id: "office-decor-119", kind: "long-desk", x: 96, y: 778, w: 72 },
  { id: "office-decor-120", kind: "computer", x: 88, y: 770 },
  { id: "office-decor-121", kind: "office-chair", x: 96, y: 806 },
  { id: "office-decor-122", kind: "cabinet", x: 383, y: 806 },
  { id: "office-decor-123", kind: "plant-tall", x: 345, y: 776 },
  { id: "office-decor-124", kind: "rug", x: 1360, y: 690, w: 150, h: 108 },
  { id: "office-decor-125", kind: "design-desk", x: 1360, y: 670 },
  { id: "office-decor-126", kind: "desk-chair-up", x: 1360, y: 706 },
  { id: "office-decor-127", kind: "armchair", x: 1502, y: 798 },
  { id: "office-decor-128", kind: "side-table", x: 1452, y: 800 },
  { id: "office-decor-129", kind: "bookshelf", x: 1520, y: 710 },
  { id: "office-decor-130", kind: "wall-poster", x: 1180, y: 604 },
  { id: "office-decor-131", kind: "plant-tall", x: 1532, y: 622 },
  { id: "office-decor-132", kind: "plant-leafy", x: 1160, y: 810 },
  { id: "office-decor-133", kind: "lamp", x: 1164, y: 610 },
  { id: "office-decor-134", kind: "rug", x: 104, y: 1e3, w: 116, h: 188 },
  { id: "office-decor-135", kind: "arcade-cabinet", x: 240, y: 1e3 },
  { id: "office-decor-136", kind: "gaming-chair", x: 320, y: 1e3 },
  { id: "office-decor-137", kind: "game-console", x: 104, y: 916 },
  { id: "office-decor-138", kind: "sofa", x: 104, y: 1e3 },
  { id: "office-decor-139", kind: "beanbag-gold", x: 112, y: 952 },
  { id: "office-decor-140", kind: "beanbag-violet", x: 112, y: 1048 },
  { id: "office-decor-141", kind: "air-hockey", x: 240, y: 1106 },
  { id: "office-decor-142", kind: "wall-poster", x: 344, y: 872 },
  { id: "office-decor-143", kind: "wall-abstract", x: 396, y: 872 },
  { id: "office-decor-144", kind: "plant-leafy", x: 76, y: 1116 },
  { id: "office-decor-145", kind: "plant-tall", x: 404, y: 1120 },
  { id: "office-decor-146", kind: "welcome-desk", x: 1080, y: 876 },
  { id: "office-decor-147", kind: "computer", x: 1080, y: 878 },
  { id: "office-decor-148", kind: "office-chair", x: 1010, y: 868 },
  { id: "office-decor-149", kind: "rug", x: 640, y: 920, w: 230, h: 96 },
  { id: "office-decor-150", kind: "sofa", x: 690, y: 926 },
  { id: "office-decor-151", kind: "armchair", x: 590, y: 924 },
  { id: "office-decor-152", kind: "coffee-table", x: 640, y: 956 },
  { id: "office-decor-153", kind: "plant-leafy", x: 720, y: 972 },
  { id: "office-decor-154", kind: "plant-leafy", x: 880, y: 972 },
  { id: "office-decor-155", kind: "rug", x: 526, y: 1110, w: 140, h: 84 },
  { id: "office-decor-156", kind: "waiting-bench", x: 520, y: 1085 },
  { id: "office-decor-157", kind: "coat-rack", x: 470, y: 1120 },
  { id: "office-decor-158", kind: "umbrella-stand", x: 504, y: 1134 },
  { id: "office-decor-159", kind: "plant-tall", x: 710, y: 1034 },
  { id: "office-decor-160", kind: "plant-tall", x: 890, y: 1034 },
  { id: "office-decor-161", kind: "wall-poster", x: 590, y: 1022 },
  { id: "office-decor-162", kind: "rest-bench", x: 1400, y: 1014, rotation: 1.5708 },
  { id: "office-decor-163", kind: "plant-tall", x: 1085, y: 1058 },
  { id: "office-decor-164", kind: "plant-fern", x: 1395, y: 1075 }
];

// src/world/level.ts
var LEVEL_DOCUMENT_VERSION = 1;
var DECOR_KINDS = [
  "long-desk",
  "desk-chair-up",
  "desk-chair-down",
  "office-chair",
  "sofa",
  "armchair",
  "coffee-table",
  "rug",
  "monitor",
  "computer",
  "bookshelf",
  "cabinet",
  "lamp",
  "whiteboard",
  "plant-leafy",
  "plant-tall",
  "plant-fern",
  "wall-painting",
  "wall-poster",
  "wall-abstract",
  "hookah",
  "game-console"
];
var WorldZoneSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
  isolated: z.boolean(),
  /** Outdoor (courtyard/garden) zones use proximity voice, not a whole-room seal (see WorldZone.openAir). */
  openAir: z.boolean().optional()
});
var WorldDoorSchema = z.object({
  id: z.string().min(1),
  zoneId: z.string().min(1),
  side: z.enum(["N", "S", "E", "W"]),
  pos: z.number(),
  width: z.number().positive()
});
var InteractiveObjectSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(["coffee", "desk", "sofa", "tv", "arcade", "jukebox"]),
  icon: z.string(),
  label: z.string(),
  x: z.number(),
  y: z.number(),
  rotation: z.number().optional(),
  effect: z.object({
    presence: z.enum(PRESENCE_FLAVORS).optional(),
    animation: z.enum(ANIMATION_STATES).optional(),
    // The 🎵 Jukebox toggles the room disco (ADR-0031) — a room-local, player-driven party.
    disco: z.boolean().optional()
  })
});
var SeatSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(["chair", "sofa", "toilet", "meeting-seat", "workstation"]),
  icon: z.string(),
  label: z.string(),
  x: z.number(),
  y: z.number(),
  facing: z.number()
});
var DecorItemSchema = z.object({
  id: z.string().min(1),
  kind: z.string().min(1),
  x: z.number(),
  y: z.number(),
  rotation: z.number().optional(),
  scale: z.number().optional(),
  w: z.number().optional(),
  h: z.number().optional()
});
var LevelDocumentSchema = z.object({
  version: z.literal(LEVEL_DOCUMENT_VERSION),
  id: z.string().min(1),
  name: z.string(),
  themeId: WorldThemeIdSchema,
  zones: z.array(WorldZoneSchema),
  doors: z.array(WorldDoorSchema),
  objects: z.array(InteractiveObjectSchema),
  seats: z.array(SeatSchema),
  // `decor` is optional on import for forward/backward-compat (older exports predate it) → defaults empty.
  decor: z.array(DecorItemSchema).default([])
});
function parseLevel(input) {
  const raw = typeof input === "string" ? JSON.parse(input) : input;
  return LevelDocumentSchema.parse(raw);
}
function serializeLevel(doc) {
  return JSON.stringify(doc, null, 2);
}
var OFFICE_DECOR = OFFICE_DECOR_DATA;
function cloneOfficeZones() {
  return OFFICE_ZONES.map((z25) => ({ ...z25 }));
}
function cloneOfficeDoors() {
  return OFFICE_DOORS.map((d) => ({ ...d }));
}
function cloneOfficeObjects() {
  return OFFICE_OBJECTS.map((o) => ({ ...o, effect: { ...o.effect } }));
}
function cloneOfficeSeats() {
  return OFFICE_SEATS.map((s) => ({ ...s }));
}
function cloneOfficeDecor() {
  return OFFICE_DECOR.map((d) => ({ ...d }));
}
function officeLevel() {
  return {
    version: LEVEL_DOCUMENT_VERSION,
    id: "hq",
    name: "Office HQ",
    themeId: "office",
    zones: cloneOfficeZones(),
    doors: cloneOfficeDoors(),
    objects: cloneOfficeObjects(),
    seats: cloneOfficeSeats(),
    decor: cloneOfficeDecor()
  };
}
function blankLevel() {
  return {
    version: LEVEL_DOCUMENT_VERSION,
    id: "untitled",
    name: "Untitled map",
    themeId: "office",
    zones: [],
    doors: [],
    objects: [],
    seats: [],
    decor: []
  };
}
function layoutFromLevel(doc) {
  return {
    zones: doc.zones,
    walls: buildWalls(doc.zones, doc.doors),
    doors: doc.doors,
    objects: doc.objects,
    decor: doc.decor
  };
}
var DEFAULT_BOUNDS = { width: SIM.WORLD_WIDTH, height: SIM.WORLD_HEIGHT };
function levelHasErrors(issues) {
  return issues.some((i) => i.level === "error");
}
function circleHitsWall(cx, cy, r, w) {
  const nx = Math.max(w.x, Math.min(cx, w.x + w.width));
  const ny = Math.max(w.y, Math.min(cy, w.y + w.height));
  const dx = cx - nx;
  const dy = cy - ny;
  return dx * dx + dy * dy < r * r;
}
function pointInZone(x, y, z25) {
  return x >= z25.x && x < z25.x + z25.width && y >= z25.y && y < z25.y + z25.height;
}
function zoneAtIn2(x, y, zones) {
  for (const z25 of zones) if (pointInZone(x, y, z25)) return z25;
  return null;
}
function overlapArea(a, b) {
  const ox = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const oy = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return ox > 0 && oy > 0 ? ox * oy : 0;
}
function duplicateIds(items) {
  const seen = /* @__PURE__ */ new Set();
  const dupes = /* @__PURE__ */ new Set();
  for (const it of items) {
    if (seen.has(it.id)) dupes.add(it.id);
    seen.add(it.id);
  }
  return [...dupes];
}
function reachableZones(zones, walls, start, bounds) {
  const GRID = 10;
  const R = SIM.DUCK_RADIUS;
  const W = bounds.width;
  const H = bounds.height;
  const cols = Math.ceil(W / GRID);
  const rows = Math.ceil(H / GRID);
  const key = (c, r) => r * cols + c;
  const center = (c, r) => ({
    x: c * GRID + GRID / 2,
    y: r * GRID + GRID / 2
  });
  const isFree = (x, y) => {
    if (x < R || y < R || x > W - R || y > H - R) return false;
    for (const w of walls) if (circleHitsWall(x, y, R, w)) return false;
    return true;
  };
  const walkable = new Uint8Array(cols * rows);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const { x, y } = center(c, r);
      walkable[key(c, r)] = isFree(x, y) ? 1 : 0;
    }
  }
  const reached = new Uint8Array(cols * rows);
  const queue = [];
  const sc0 = Math.floor(start.x / GRID);
  const sc1 = Math.ceil((start.x + start.width) / GRID);
  const sr0 = Math.floor(start.y / GRID);
  const sr1 = Math.ceil((start.y + start.height) / GRID);
  seed: for (let r = sr0; r < sr1; r++) {
    for (let c = sc0; c < sc1; c++) {
      const { x, y } = center(c, r);
      if (pointInZone(x, y, start) && walkable[key(c, r)]) {
        reached[key(c, r)] = 1;
        queue.push([c, r]);
        break seed;
      }
    }
  }
  while (queue.length) {
    const [c, r] = queue.pop();
    for (const [dc, dr] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1]
    ]) {
      const nc = c + dc;
      const nr = r + dr;
      if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) continue;
      const k = key(nc, nr);
      if (reached[k] || !walkable[k]) continue;
      reached[k] = 1;
      queue.push([nc, nr]);
    }
  }
  const reachable = /* @__PURE__ */ new Set();
  for (const z25 of zones) {
    const c0 = Math.floor(z25.x / GRID);
    const c1 = Math.ceil((z25.x + z25.width) / GRID);
    const r0 = Math.floor(z25.y / GRID);
    const r1 = Math.ceil((z25.y + z25.height) / GRID);
    for (let r = r0; r < r1; r++) {
      for (let c = c0; c < c1; c++) {
        const { x, y } = center(c, r);
        if (pointInZone(x, y, z25) && reached[key(c, r)]) {
          reachable.add(z25.id);
          break;
        }
      }
      if (reachable.has(z25.id)) break;
    }
  }
  return reachable;
}
function validateLevel(doc, bounds = DEFAULT_BOUNDS) {
  const issues = [];
  const W = bounds.width;
  const H = bounds.height;
  const { zones, doors, objects, seats } = doc;
  for (const [label, items] of [
    ["zone", zones],
    ["door", doors],
    ["object", objects],
    ["seat", seats]
  ]) {
    for (const id of duplicateIds(items)) {
      issues.push({ level: "error", code: `${label}-id-duplicate`, message: `Duplicate ${label} id "${id}".`, targetId: id });
    }
  }
  for (const z25 of zones) {
    if (z25.width <= 0 || z25.height <= 0) {
      issues.push({ level: "error", code: "zone-degenerate", message: `Room "${z25.name || z25.id}" has a non-positive size.`, targetId: z25.id });
    }
    if (z25.x < 0 || z25.y < 0 || z25.x + z25.width > W || z25.y + z25.height > H) {
      issues.push({ level: "error", code: "zone-out-of-bounds", message: `Room "${z25.name || z25.id}" extends outside the ${W}\xD7${H} world.`, targetId: z25.id });
    }
  }
  for (let i = 0; i < zones.length; i++) {
    for (let j = i + 1; j < zones.length; j++) {
      const a = zones[i];
      const b = zones[j];
      if (overlapArea(a, b) > 1) {
        issues.push({ level: "warn", code: "zone-overlap", message: `Rooms "${a.name || a.id}" and "${b.name || b.id}" overlap.`, targetId: a.id });
      }
    }
  }
  const zoneById = new Map(zones.map((z25) => [z25.id, z25]));
  for (const d of doors) {
    const z25 = zoneById.get(d.zoneId);
    if (!z25) {
      issues.push({ level: "error", code: "door-orphan", message: `Door "${d.id}" references unknown room "${d.zoneId}".`, targetId: d.id });
      continue;
    }
    const half = d.width / 2;
    const within = d.side === "N" || d.side === "S" ? d.pos - half >= z25.x && d.pos + half <= z25.x + z25.width : d.pos - half >= z25.y && d.pos + half <= z25.y + z25.height;
    if (!within) {
      issues.push({ level: "error", code: "door-off-edge", message: `Door "${d.id}" hangs off the "${z25.name || z25.id}" wall (opening not within the edge).`, targetId: d.id });
    }
  }
  if (zones.length > 0) {
    const start = zones.find((z25) => z25.id === ENTRYWAY_ZONE_ID) ?? largestZone(zones);
    const walls2 = buildWalls(zones, doors);
    const reachable = reachableZones(zones, walls2, start, bounds);
    if (!reachable.has(start.id)) {
      issues.push({ level: "error", code: "entry-unwalkable", message: `The entry room "${start.name || start.id}" has no standable floor (walls seal it shut).`, targetId: start.id });
    }
    for (const z25 of zones) {
      if (!reachable.has(z25.id)) {
        issues.push({ level: "error", code: "room-unreachable", message: `Room "${z25.name || z25.id}" can't be reached from the entry \u2014 check its doors.`, targetId: z25.id });
      }
    }
  }
  const walls = buildWalls(zones, doors);
  for (const [label, items] of [
    ["object", objects],
    ["seat", seats]
  ]) {
    for (const it of items) {
      if (it.x < 0 || it.y < 0 || it.x > W || it.y > H) {
        issues.push({ level: "error", code: `${label}-out-of-bounds`, message: `${label === "object" ? "Object" : "Seat"} "${it.label || it.id}" is outside the world.`, targetId: it.id });
        continue;
      }
      if (!zoneAtIn2(it.x, it.y, zones)) {
        issues.push({ level: "warn", code: `${label}-outside-room`, message: `${label === "object" ? "Object" : "Seat"} "${it.label || it.id}" is not inside any room.`, targetId: it.id });
      }
      if (walls.some((w) => circleHitsWall(it.x, it.y, SIM.DUCK_RADIUS, w))) {
        issues.push({ level: "warn", code: `${label}-in-wall`, message: `${label === "object" ? "Object" : "Seat"} "${it.label || it.id}" overlaps a wall \u2014 a duck can't reach it.`, targetId: it.id });
      }
    }
  }
  return issues;
}
function largestZone(zones) {
  let best = zones[0];
  let bestArea = best.width * best.height;
  for (const z25 of zones) {
    const a = z25.width * z25.height;
    if (a > bestArea) {
      bestArea = a;
      best = z25;
    }
  }
  return best;
}
var LEVEL_THEME_IDS = WORLD_THEME_IDS;
var FURNITURE_CATEGORIES = ["seating", "desks", "decor", "plants", "wall", "gaming", "kitchen", "outdoor"];
var FURNITURE_CATALOG = [
  // ── Seating ── (sit-able: a duck can sit on a placed instance)
  { id: "sofa", name: "Sofa", price: 300, category: "seating", decorKind: "sofa", icon: "\u{1F6CB}\uFE0F", sittable: true },
  { id: "armchair", name: "Armchair", price: 160, category: "seating", decorKind: "armchair", icon: "\u{1FA91}", sittable: true },
  { id: "office-chair", name: "Office chair", price: 110, category: "seating", decorKind: "office-chair", icon: "\u{1F4BA}", sittable: true },
  { id: "desk-chair-up", name: "Desk chair \u2191", price: 90, category: "seating", decorKind: "desk-chair-up", icon: "\u{1FA91}", sittable: true },
  { id: "desk-chair-down", name: "Desk chair \u2193", price: 90, category: "seating", decorKind: "desk-chair-down", icon: "\u{1FA91}", sittable: true },
  // ── Interactive props (placed → usable, like the static office objects): coffee / jukebox / TV ──
  { id: "coffee-machine", name: "Coffee machine", price: 220, category: "kitchen", decorKind: "coffee", icon: "\u2615", objectKind: "coffee" },
  { id: "jukebox", name: "Jukebox", price: 320, category: "gaming", decorKind: "jukebox", icon: "\u{1F3B5}", objectKind: "jukebox" },
  { id: "tv", name: "TV", price: 300, category: "gaming", decorKind: "tv", icon: "\u{1F4FA}", objectKind: "tv" },
  // ── Desks & work ──
  { id: "long-desk", name: "Desk", price: 250, category: "desks", decorKind: "long-desk", icon: "\u{1F5C4}\uFE0F", w: 84 },
  { id: "coffee-table", name: "Coffee table", price: 130, category: "desks", decorKind: "coffee-table", icon: "\u{1F6CE}\uFE0F" },
  { id: "monitor", name: "Monitor", price: 120, category: "desks", decorKind: "monitor", icon: "\u{1F5A5}\uFE0F" },
  { id: "computer", name: "Computer", price: 160, category: "desks", decorKind: "computer", icon: "\u{1F4BB}" },
  { id: "whiteboard", name: "Whiteboard", price: 160, category: "desks", decorKind: "whiteboard", icon: "\u{1F4CB}" },
  // ── Decor & misc ──
  { id: "bookshelf", name: "Bookshelf", price: 200, category: "decor", decorKind: "bookshelf", icon: "\u{1F4DA}" },
  { id: "cabinet", name: "Cabinet", price: 180, category: "decor", decorKind: "cabinet", icon: "\u{1F5C3}\uFE0F" },
  { id: "lamp", name: "Lamp", price: 90, category: "decor", decorKind: "lamp", icon: "\u{1F4A1}" },
  { id: "rug", name: "Rug", price: 120, category: "decor", decorKind: "rug", icon: "\u{1F7EB}", w: 160, h: 90 },
  { id: "hookah", name: "Hookah", price: 220, category: "decor", decorKind: "hookah", icon: "\u{1F4A8}" },
  { id: "game-console", name: "Game console", price: 280, category: "decor", decorKind: "game-console", icon: "\u{1F3AE}" },
  // ── Plants ──
  { id: "plant-leafy", name: "Plant", price: 100, category: "plants", decorKind: "plant-leafy", icon: "\u{1FAB4}" },
  { id: "plant-tall", name: "Tall plant", price: 140, category: "plants", decorKind: "plant-tall", icon: "\u{1F334}" },
  { id: "plant-fern", name: "Fern", price: 80, category: "plants", decorKind: "plant-fern", icon: "\u{1F33F}" },
  // ── Wall art ──
  { id: "wall-painting", name: "Painting", price: 150, category: "wall", decorKind: "wall-painting", icon: "\u{1F5BC}\uFE0F" },
  { id: "wall-poster", name: "Poster", price: 60, category: "wall", decorKind: "wall-poster", icon: "\u{1F4F0}" },
  { id: "wall-abstract", name: "Abstract art", price: 150, category: "wall", decorKind: "wall-abstract", icon: "\u{1F3A8}" },
  // ── Bespoke SCENERY pieces (the room decorators' own builders, now buyable). `decorKind` is the
  //    registered scenery kind; the web renders it via `buildScenery`. ──
  // Seating extras
  { id: "waiting-bench", name: "Waiting bench", price: 150, category: "seating", decorKind: "waiting-bench", icon: "\u{1F6CB}\uFE0F", sittable: true },
  { id: "rest-bench", name: "Rest bench", price: 110, category: "seating", decorKind: "rest-bench", icon: "\u{1F6CB}\uFE0F", sittable: true },
  { id: "side-table", name: "Side table", price: 90, category: "seating", decorKind: "side-table", icon: "\u{1F6CB}\uFE0F" },
  // Desks & work extras
  { id: "conference-table", name: "Conference table", price: 320, category: "desks", decorKind: "conference-table", icon: "\u{1F5D3}\uFE0F" },
  { id: "welcome-desk", name: "Welcome desk", price: 260, category: "desks", decorKind: "welcome-desk", icon: "\u{1F6CE}\uFE0F" },
  { id: "design-desk", name: "Design desk", price: 240, category: "desks", decorKind: "design-desk", icon: "\u{1F58D}\uFE0F" },
  { id: "desk-laptop", name: "Laptop", price: 130, category: "desks", decorKind: "desk-laptop", icon: "\u{1F4BB}" },
  { id: "coat-rack", name: "Coat rack", price: 90, category: "desks", decorKind: "coat-rack", icon: "\u{1F9E5}" },
  { id: "umbrella-stand", name: "Umbrella stand", price: 70, category: "desks", decorKind: "umbrella-stand", icon: "\u2602\uFE0F" },
  // Decor extras
  { id: "accent-lamp", name: "Accent lamp", price: 120, category: "decor", decorKind: "accent-lamp", icon: "\u{1F4A1}" },
  { id: "server-rack", name: "Server rack", price: 260, category: "decor", decorKind: "server-rack-7u-2", icon: "\u{1F5A5}\uFE0F" },
  { id: "cooling-unit", name: "Cooling unit", price: 220, category: "decor", decorKind: "cooling-unit", icon: "\u2744\uFE0F" },
  { id: "desk-mug", name: "Desk mug", price: 30, category: "decor", decorKind: "desk-mug", icon: "\u2615" },
  { id: "desk-papers", name: "Desk papers", price: 30, category: "decor", decorKind: "desk-papers", icon: "\u{1F4C4}" },
  // Plants extras
  { id: "desk-plant", name: "Desk plant", price: 70, category: "plants", decorKind: "desk-plant", icon: "\u{1FAB4}" },
  { id: "biophilic-island", name: "Biophilic island", price: 200, category: "plants", decorKind: "biophilic-island", icon: "\u{1FAB4}" },
  { id: "planter-box-wood", name: "Planter box", price: 110, category: "plants", decorKind: "planter-box-wood", icon: "\u{1FAB4}" },
  // Gaming
  { id: "arcade-cabinet", name: "Arcade cabinet", price: 320, category: "gaming", decorKind: "arcade-cabinet", icon: "\u{1F579}\uFE0F", objectKind: "arcade" },
  { id: "air-hockey", name: "Air-hockey table", price: 300, category: "gaming", decorKind: "air-hockey", icon: "\u{1F3D2}" },
  { id: "gaming-chair", name: "Gaming chair", price: 180, category: "gaming", decorKind: "gaming-chair", icon: "\u{1FA91}", sittable: true },
  { id: "beanbag-gold", name: "Beanbag (gold)", price: 130, category: "gaming", decorKind: "beanbag-gold", icon: "\u{1F6CB}\uFE0F", sittable: true },
  { id: "beanbag-violet", name: "Beanbag (violet)", price: 130, category: "gaming", decorKind: "beanbag-violet", icon: "\u{1F6CB}\uFE0F", sittable: true },
  // Kitchen
  { id: "coffee-station", name: "Coffee station", price: 200, category: "kitchen", decorKind: "coffee-station", icon: "\u2615" },
  { id: "coffee-counter", name: "Coffee counter", price: 220, category: "kitchen", decorKind: "coffee-counter", icon: "\u2615" },
  { id: "fridge-tall", name: "Fridge", price: 240, category: "kitchen", decorKind: "fridge-tall", icon: "\u{1F9CA}" },
  { id: "prep-island", name: "Prep island", price: 200, category: "kitchen", decorKind: "prep-island", icon: "\u{1F373}" },
  { id: "island-table", name: "Island table", price: 180, category: "kitchen", decorKind: "island-table", icon: "\u{1F37D}\uFE0F" },
  { id: "bar-stool", name: "Bar stool", price: 70, category: "kitchen", decorKind: "bar-stool", icon: "\u{1FA91}", sittable: true },
  { id: "water-cooler", name: "Water cooler", price: 120, category: "kitchen", decorKind: "water-cooler", icon: "\u{1F6B0}" },
  { id: "fruit-bowl", name: "Fruit bowl", price: 50, category: "kitchen", decorKind: "fruit-bowl", icon: "\u{1F34E}" },
  // Outdoor
  { id: "outdoor-sofa-patio", name: "Outdoor sofa", price: 280, category: "outdoor", decorKind: "outdoor-sofa-patio", icon: "\u{1F6CB}\uFE0F", sittable: true },
  { id: "sun-lounger-l", name: "Sun lounger (left)", price: 160, category: "outdoor", decorKind: "sun-lounger-l", icon: "\u{1F6CB}\uFE0F", sittable: true },
  { id: "sun-lounger-r", name: "Sun lounger (right)", price: 160, category: "outdoor", decorKind: "sun-lounger-r", icon: "\u{1F6CB}\uFE0F", sittable: true },
  { id: "parasol-striped", name: "Striped parasol", price: 140, category: "outdoor", decorKind: "parasol-striped", icon: "\u26F1\uFE0F" },
  // ── Default-level variants (so the office re-furnishes 100% from the store). One VISIBLE SKU per type;
  //    the exact captured variants (conference-chair facings, server-rack lanes) ride along hidden. ──
  // Board chairs (the conference table's 8 seats — each a distinct facing). One visible SKU; 7 hidden.
  { id: "board-chair", name: "Board chair", price: 110, category: "seating", decorKind: "board-chair-1", icon: "\u{1FA91}", sittable: true },
  { id: "board-chair-2", name: "Board chair", price: 110, category: "seating", decorKind: "board-chair-2", icon: "\u{1FA91}", sittable: true, hidden: true },
  { id: "board-chair-3", name: "Board chair", price: 110, category: "seating", decorKind: "board-chair-3", icon: "\u{1FA91}", sittable: true, hidden: true },
  { id: "board-chair-4", name: "Board chair", price: 110, category: "seating", decorKind: "board-chair-4", icon: "\u{1FA91}", sittable: true, hidden: true },
  { id: "board-chair-5", name: "Board chair", price: 110, category: "seating", decorKind: "board-chair-5", icon: "\u{1FA91}", sittable: true, hidden: true },
  { id: "board-chair-6", name: "Board chair", price: 110, category: "seating", decorKind: "board-chair-6", icon: "\u{1FA91}", sittable: true, hidden: true },
  { id: "board-chair-7", name: "Board chair", price: 110, category: "seating", decorKind: "board-chair-7", icon: "\u{1FA91}", sittable: true, hidden: true },
  { id: "board-chair-8", name: "Board chair", price: 110, category: "seating", decorKind: "board-chair-8", icon: "\u{1FA91}", sittable: true, hidden: true },
  // Lounge side table — renders identically to the studio "Side table"; hidden (the visible one covers it).
  { id: "lounge-side-table", name: "Side table", price: 90, category: "seating", decorKind: "lounge-side-table", icon: "\u{1F6CB}\uFE0F", hidden: true },
  // Server racks by height — the catalog already has a 7U "Server rack"; add 4U/5U/6U (visible) + lane dups (hidden).
  { id: "server-rack-4u", name: "Server rack (4U)", price: 220, category: "decor", decorKind: "server-rack-4u-4", icon: "\u{1F5A5}\uFE0F" },
  { id: "server-rack-5u", name: "Server rack (5U)", price: 240, category: "decor", decorKind: "server-rack-5u-1", icon: "\u{1F5A5}\uFE0F" },
  { id: "server-rack-6u", name: "Server rack (6U)", price: 250, category: "decor", decorKind: "server-rack-6u-5", icon: "\u{1F5A5}\uFE0F" },
  { id: "server-rack-5u-b", name: "Server rack (5U)", price: 240, category: "decor", decorKind: "server-rack-5u-3", icon: "\u{1F5A5}\uFE0F", hidden: true },
  { id: "server-rack-4u-b", name: "Server rack (4U)", price: 220, category: "decor", decorKind: "server-rack-4u-6", icon: "\u{1F5A5}\uFE0F", hidden: true }
];
var FURNITURE_BY_ID = new Map(
  FURNITURE_CATALOG.map((item) => [item.id, item])
);
function furnitureItemById(id) {
  return FURNITURE_BY_ID.get(id);
}
var OBJECT_KIND_BY_DECOR_KIND = new Map(
  FURNITURE_CATALOG.filter((i) => i.objectKind).map((i) => [i.decorKind, i.objectKind])
);
var SITTABLE_DECOR_KINDS = new Set(
  FURNITURE_CATALOG.filter((i) => i.sittable).map((i) => i.decorKind)
);
function furnitureObjectKind(decorKind) {
  return OBJECT_KIND_BY_DECOR_KIND.get(decorKind) ?? null;
}
function isSittableFurniture(decorKind) {
  return SITTABLE_DECOR_KINDS.has(decorKind);
}
var FURNITURE_INTERACT_RANGE = 90;
var MAX_FURNITURE_PER_ROOM = 700;
var FURNITURE_RATE_LIMIT = { max: 5, windowMs: 1e4 };
var DecorKindSchema = z.enum(DECOR_KINDS);
var InternalPlaceFurnitureSchema = z.object({
  workspaceId: z.string().uuid(),
  userId: z.string().uuid(),
  itemId: z.string().min(1).max(64),
  x: z.number(),
  y: z.number(),
  rotation: z.number().optional()
});
var InternalRemoveFurnitureSchema = z.object({
  workspaceId: z.string().uuid(),
  furnitureId: z.string().min(1).max(64)
});
var InternalMoveFurnitureSchema = z.object({
  workspaceId: z.string().uuid(),
  furnitureId: z.string().min(1).max(64),
  x: z.number(),
  y: z.number(),
  rotation: z.number().optional()
});

// src/realtime/vad.ts
var DEFAULT_VAD_PARAMS = {
  // Energy floors mirror the old RMS gate (mic-pipeline) so soft talkers are unaffected: gate-open ≤ the
  // self-VAD speaking floor (rms > 0.02). The high-pass upstream already strips rumble/hum.
  openThreshold: 0.015,
  closeThreshold: 7e-3,
  // Voicing floors (peak normalized CROSS-correlation, ÷√(windowed energies), so a periodic signal scores
  // ~1.0 at ANY pitch — including deep/low voices — and broadband/impulsive noise scores well under ~0.3).
  // 0.50 to OPEN (be sure it's speech), 0.30 to STAY (don't choppily drop a trailing voiced sound). Tunable.
  voicingOpen: 0.5,
  voicingClose: 0.3,
  holdMs: 220
};
function frameRms(frame) {
  let sum = 0;
  for (let i = 0; i < frame.length; i++) {
    const s = frame[i];
    sum += s * s;
  }
  return Math.sqrt(sum / Math.max(1, frame.length));
}
function voicingScore(frame, sampleRate, scratch) {
  const decim = Math.max(1, Math.round(sampleRate / 12e3));
  const n = Math.floor(frame.length / decim);
  if (n < 64) return 0;
  const x = scratch && scratch.length >= n ? scratch : new Float32Array(n);
  let mean = 0;
  for (let i = 0; i < n; i++) {
    let acc = 0;
    const base = i * decim;
    for (let j = 0; j < decim; j++) acc += frame[base + j];
    const v = acc / decim;
    x[i] = v;
    mean += v;
  }
  mean /= n;
  let e0 = 0;
  for (let i = 0; i < n; i++) {
    const v = x[i] - mean;
    x[i] = v;
    e0 += v * v;
  }
  if (e0 <= 1e-9) return 0;
  const dsr = sampleRate / decim;
  const minLag = Math.max(2, Math.floor(dsr / 400));
  const maxLag = Math.min(n - 1, Math.floor(dsr / 75));
  let best = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let s = 0;
    let ea = 0;
    let eb = 0;
    for (let i = lag; i < n; i++) {
      const a = x[i];
      const b = x[i - lag];
      s += a * b;
      ea += a * a;
      eb += b * b;
    }
    const denom = Math.sqrt(ea * eb);
    const r = denom > 1e-9 ? s / denom : 0;
    if (r > best) best = r;
  }
  return best < 0 ? 0 : best;
}
function decideVad(state, rms, voicing, nowMs, p) {
  if (state.open) {
    if (voicing >= p.voicingClose) {
      return { open: true, holdUntil: nowMs + p.holdMs };
    }
    if (nowMs >= state.holdUntil) return { open: false, holdUntil: state.holdUntil };
    return { open: true, holdUntil: state.holdUntil };
  }
  if (rms >= p.openThreshold && voicing >= p.voicingOpen) {
    return { open: true, holdUntil: nowMs + p.holdMs };
  }
  return { open: false, holdUntil: state.holdUntil };
}
var ClientMessage = {
  /** Movement intent: a (clamped) input vector + monotonic sequence number. */
  MOVE: "m",
  /** One-shot emote (wave, dance, laugh…). */
  EMOTE: "e",
  /** Interact with a world object at the player's current location. */
  INTERACT: "i",
  /** Trigger a celebration moment (quest complete, reputation given…) for the room to react to. */
  CELEBRATE: "c",
  /**
   * Give reputation to a nearby duck. The client only names the TARGET SESSION; the server owns
   * positions and the grant amount, so neither proximity nor value can be forged (see ADR-0007).
   */
  GIVE_REPUTATION: "r",
  /** Elect (or clear) the presence flavor shown over my duck — a chosen flair, never inferred (M4). */
  SET_PRESENCE: "s",
  /** Update my equipped cosmetics (M6) so everyone sees my look. Cosmetic-only; ownership is gateway-attested. */
  SET_COSMETICS: "k",
  /** Latency probe; server echoes a PONG with the same client timestamp. */
  PING: "p",
  /**
   * Block / unblock a specific citizen's voice (M3.4 — per-listener self-protection, the open-mic
   * precondition). The client names only the target SESSION; the server resolves it to a stable user
   * id and severs the audio link BOTH WAYS while active (neither hears the other). The block is
   * EPHEMERAL and NEVER LOGGED (Constitution Art. 1 — this is safety, not surveillance): it lives only
   * in room memory for the session and is never persisted, scored, or surfaced to the blocked party.
   */
  BLOCK: "b",
  UNBLOCK: "u",
  /** Say something — a short speech bubble that pops over my duck and fades (M7). */
  SAY: "y",
  /** Send a quick emoji reaction that floats up from my duck (M7). */
  REACT: "j",
  /**
   * Play a short, fun sound effect for everyone present — a soundboard moment (M7a). Like REACT, but
   * the room HEARS it together. The id is one of the closed {@link SOUND_REACTIONS} catalog and doubles
   * as the asset slug; the client plays the matching `/sounds/<id>.mp3` clip.
   */
  SOUND_REACT: "o",
  /** Challenge a NEARBY duck to Rock-Paper-Scissors (M7); the server proximity-checks + creates a match. */
  RPS_CHALLENGE: "g",
  /** Submit my choice (rock/paper/scissors) in an active match (M7). */
  RPS_CHOOSE: "h",
  /** Decline / back out of a match I was challenged to (M7). */
  RPS_DECLINE: "q",
  /**
   * BARK BATTLE (Challenge game #2 — see domain/bark.ts). Multi-char tokens like MEDIA/RPS_STARTED
   * so a single-byte collision can never swallow one in transit:
   *  - BARK_CHALLENGE — challenge a NEARBY duck (server proximity-checks + creates the match).
   *  - BARK_ACCEPT    — the INVITED duck accepts; the server stamps the shared countdown + window.
   *  - BARK_DECLINE   — either duck backs out before it resolves.
   *  - BARK_SAMPLE    — one mic-loudness reading (slot `i`, points `v`) streamed ~10×/s DURING the
   *    window only. The server clamps + dedupes every slot and owns the totals — the client never
   *    sends a score, and no audio ever rides the message (Constitution Art. 1).
   */
  BARK_CHALLENGE: "barkCh",
  BARK_ACCEPT: "barkAcc",
  BARK_DECLINE: "barkDec",
  BARK_SAMPLE: "barkSmp",
  /**
   * Sit on / stand up from an anchored SEAT (Task 2 "interactive SIT"). A toggle: naming an EMPTY seat
   * I'm next to seats me on it (the server snaps + locks me there); naming the seat I'm on (or sending
   * it again) stands me up. The client names only the seat id; the SERVER owns positions + occupancy and
   * re-checks both, so a client can't claim to be next to a seat across the map or steal a taken seat.
   */
  SIT: "S",
  /** Hold (or empty, with '') a fun prop in hand — a guitar, laptop, coffee… (Task 2 "HOLD ITEMS"). */
  SET_HELD: "L",
  /**
   * Do a beat of WORK while seated at a WORKSTATION desk (gamified-XP redesign — the small, repeatable
   * co-op office quest). The client sends nothing but the intent; the SERVER re-checks that the duck is
   * actually seated at a desk seat and enforces the {@link WORK_COOLDOWN_MS} cooldown, then attests the
   * tiny +1 XP via the gateway. Proximity/seat/cooldown can't be forged (the room owns who's seated
   * where + when they last worked), mirroring the SIT/INTERACT authority model.
   */
  WORK: "W",
  /**
   * Office RADIO control + clock-sync (ADR-0021). One channel, discriminated by an `action` field
   * (see {@link RadioControlSchema}). Most actions (load/play/pause/seek/skip/stop/enqueue/dequeue)
   * are OWNER-only and re-checked server-side from the verified JWT role — only the guild OWNER is the
   * DJ. Two actions are open to EVERYONE: `sync` (a clock probe the server echoes via RADIO_CLOCK) and
   * `react` (an emoji reaction to the current track). The wire never carries audio or a URL — only an
   * 11-char videoId — and nothing is persisted (Constitution Art. 1).
   */
  RADIO: "A",
  /**
   * MEDIA HUB control (ADR-0033) — the evolved radio: a per-ZONE shared screen with a COLLECTIVE queue,
   * screen sharing, and an extensible source model. One channel, discriminated by an `action` field
   * (see {@link MediaControlSchema}). The server derives WHICH hub from the sender's authoritative zone.
   * Anyone in the zone may `enqueue`/`react`/`sync`/`claimHost`; playback control is HOST-only, re-checked
   * server-side. The wire never carries audio/video — only an opaque `{ kind, ref }` (Constitution Art. 1).
   * Multi-char token (like RPS_STARTED) so a single-byte collision can never swallow it in transit.
   */
  MEDIA: "mediaCtl",
  /**
   * Entryway (Прихожая) front-door affordances (Floorplan v2 / ADR-0022). All three carry NO payload —
   * the SERVER owns positions + the door state, so it re-checks the pure rule (entryway.ts) before
   * acting; a client can neither forge proximity nor open a door it isn't allowed to:
   *  - RING_BELL — ring the doorbell from inside the entryway; the WHOLE office hears the chime.
   *  - KNOCK     — knock on the door from the entryway side; only ducks NEAR the door hear it.
   *  - OPEN_DOOR — open the entry door (HOST from anywhere, or anyone already INSIDE next to it). Carries
   *    an optional `open` flag; the HOST may also send `open:false` to shut it early from the doors panel.
   */
  RING_BELL: "Z",
  KNOCK: "K",
  OPEN_DOOR: "D",
  /**
   * Open/close an INTERIOR room door (Room Doors feature). Carries the {@link Doorway} id + the desired
   * `open` state. The SERVER owns positions + the door state, so it re-checks {@link canToggleDoor}
   * (anyone next to the doorway, or the HOST from anywhere) before mutating the synced closed-doors set —
   * a client can neither forge proximity nor toggle a door it isn't allowed to.
   */
  TOGGLE_DOOR: "T",
  /**
   * Broadcast my AUDIO-DEVICE state so every participant's roster row can show Discord-style 🎤/🎧
   * indicators (who can speak, who can hear). Two independent booleans: `voiceOn` (voice is live — I can
   * HEAR the room) and `micMuted` (my mic is deliberately muted — INTENT, not the per-keypress PTT gate).
   * Purely presence (Constitution Art. 1): never logged, ranked, or used to police anyone — it just lets
   * ducks understand why someone isn't answering in voice. The SERVER stores it on the sender's own
   * PlayerState (it can only set its OWN state) and Colyseus deltas it to the room.
   */
  VOICE_STATE: "V",
  /**
   * Buy & place a piece of FURNITURE in the room (Furniture Store). The client names only the catalog
   * `itemId` + a world position; the SERVER owns the price (re-derived from the catalog), the placer's
   * coin balance, the per-room cap, and a per-session rate limit — so a client can neither forge a price,
   * place without paying, nor spam past the cap (ТЗ §7/§9). Coins are charged ONLY on a confirmed place.
   */
  PLACE_FURNITURE: "F",
  /** Remove a placed furniture piece (its placer, or the room host). The client names only the piece id;
   * the server re-checks the right to remove it before deleting from the synced state (no coin refund). */
  REMOVE_FURNITURE: "f",
  /**
   * Move / rotate an already-placed furniture piece ("Take" + drop, or "Rotate" — context menu). Carries
   * the piece id + a new world position (and optional rotation). NO coins are charged (you already own the
   * piece). The server re-checks the right to move it (the placer, or the host) + that the new position is
   * inside a room, then updates the synced state for everyone.
   */
  MOVE_FURNITURE: "M"
};
var PlaceFurnitureMessageSchema = z.object({
  itemId: z.string().min(1).max(64),
  x: z.number(),
  y: z.number(),
  rotation: z.number().optional()
});
var RemoveFurnitureMessageSchema = z.object({
  furnitureId: z.string().min(1).max(64)
});
var MoveFurnitureMessageSchema = z.object({
  furnitureId: z.string().min(1).max(64),
  x: z.number(),
  y: z.number(),
  rotation: z.number().optional()
});
var WorkMessageSchema = z.object({}).strict();
var RingBellMessageSchema = z.object({}).strict();
var KnockMessageSchema = z.object({}).strict();
var OpenDoorMessageSchema = z.object({ open: z.boolean().optional() }).strict();
var ToggleDoorMessageSchema = z.object({ doorwayId: z.string().min(1).max(64), open: z.boolean() }).strict();
var MoveIntentSchema = z.object({
  seq: z.number().int().nonnegative(),
  dx: z.number().min(-1).max(1),
  dy: z.number().min(-1).max(1)
});
var EmoteMessageSchema = z.object({
  animation: AnimationSchema
});
var InteractMessageSchema = z.object({
  objectId: z.string().min(1).max(64)
});
var SitMessageSchema = z.object({
  seatId: z.string().min(1).max(64)
});
var SetHeldSchema = z.object({
  item: HeldItemValueSchema
});
var CELEBRATION_KINDS = [
  "quest_completed",
  "reputation_received",
  "release",
  "level_up",
  "welcome"
];
var CelebrationKindSchema = z.enum(CELEBRATION_KINDS);
var CelebrateMessageSchema = z.object({
  kind: CelebrationKindSchema,
  text: z.string().max(80).optional(),
  targetUserId: z.string().uuid().optional()
});
var GiveReputationIntentSchema = z.object({
  targetSessionId: z.string().min(1).max(64)
});
var BlockCitizenSchema = z.object({
  targetSessionId: z.string().min(1).max(64)
});
var VoiceStateSchema = z.object({
  voiceOn: z.boolean(),
  micMuted: z.boolean()
});
var PingMessageSchema = z.object({
  /** Client-side timestamp (ms) echoed back to compute RTT. */
  t: z.number().nonnegative()
});
var SAY_MAX_LEN = 160;
var SayMessageSchema = z.object({
  text: z.string().trim().min(1).max(SAY_MAX_LEN)
});
var REACTIONS = ["\u{1F44D}", "\u2764\uFE0F", "\u{1F602}", "\u{1F389}", "\u{1F44F}", "\u{1F914}", "\u{1F525}", "\u2728"];
var ReactionSchema = z.enum(REACTIONS);
var ReactMessageSchema = z.object({ reaction: ReactionSchema });
var SOUND_REACTIONS = [
  { id: "badum-tss", label: "\u0411\u0430-\u0434\u0443\u043C-\u0442\u0441\u0441", icon: "\u{1F941}" },
  { id: "dramatic-chipmunk", label: "\u0414\u0440\u0430\u043C\u0430\u0442\u0438\u0447\u043D\u044B\u0439 \u0431\u0443\u0440\u0443\u043D\u0434\u0443\u043A", icon: "\u{1F43F}\uFE0F" },
  { id: "hentai-moan", label: "\u0421\u0442\u043E\u043D", icon: "\u{1FAE3}" },
  { id: "hentai-yamete", label: "\u042F\u043C\u0435\u0442\u0435!", icon: "\u{1F648}" },
  { id: "lets-go", label: "\u041F\u043E\u0435\u0445\u0430\u043B\u0438!", icon: "\u{1F525}" },
  { id: "mk-choose-destiny", label: "Choose your destiny", icon: "\u2694\uFE0F" },
  { id: "mk-fatality", label: "Fatality", icon: "\u{1F480}" },
  { id: "mk-finish-him", label: "Finish him!", icon: "\u2620\uFE0F" },
  { id: "mk-round-1-fight", label: "Round 1 \u2014 Fight!", icon: "\u{1F94A}" },
  { id: "om-nom-nom", label: "\u041E\u043C-\u043D\u043E\u043C-\u043D\u043E\u043C", icon: "\u{1F60B}" },
  { id: "pikachu", label: "\u0423\u0434\u0438\u0432\u043B\u0451\u043D\u043D\u044B\u0439 \u041F\u0438\u043A\u0430\u0447\u0443", icon: "\u{1F62E}" },
  { id: "serious-moment", label: "\u0421\u0435\u0440\u044C\u0451\u0437\u043D\u044B\u0439 \u043C\u043E\u043C\u0435\u043D\u0442", icon: "\u{1F9D0}" },
  { id: "tuturu", label: "\u0422\u0443\u0442\u0443-\u0440\u0443~", icon: "\u{1F3B5}" },
  { id: "wtf", label: "What the\u2026", icon: "\u{1F92C}" },
  { id: "you-want-me", label: "\u0425\u043E\u0447\u0435\u0448\u044C \u043C\u0435\u043D\u044F?", icon: "\u{1F618}" },
  { id: "putin-vinovat", label: "\u041F\u0443\u0442\u0438\u043D \u0432\u0438\u043D\u043E\u0432\u0430\u0442", icon: "\u{1F937}" },
  { id: "a-tak-mozhno-bylo", label: "\u0410 \u0442\u0430\u043A \u043C\u043E\u0436\u043D\u043E \u0431\u044B\u043B\u043E?", icon: "\u{1F632}" },
  { id: "applodismenty", label: "\u0410\u043F\u043B\u043E\u0434\u0438\u0441\u043C\u0435\u043D\u0442\u044B", icon: "\u{1F44F}" },
  { id: "begi", label: "\u0411\u0435\u0433\u0438!", icon: "\u{1F3C3}" },
  { id: "biznes", label: "\u0411\u0438\u0437\u043D\u0435\u0441", icon: "\u{1F4BC}" },
  { id: "vot-eto-povorot", label: "\u0412\u043E\u0442 \u044D\u0442\u043E \u043F\u043E\u0432\u043E\u0440\u043E\u0442!", icon: "\u{1F300}" },
  { id: "grustno", label: "\u0413\u0440\u0443\u0441\u0442\u043D\u043E", icon: "\u{1F622}" },
  { id: "davay-po-novoy", label: "\u0414\u0430\u0432\u0430\u0439 \u043F\u043E \u043D\u043E\u0432\u043E\u0439", icon: "\u{1F501}" },
  { id: "do-svidaniya", label: "\u0414\u043E \u0441\u0432\u0438\u0434\u0430\u043D\u0438\u044F", icon: "\u{1F44B}" },
  { id: "dobroe-utro", label: "\u0414\u043E\u0431\u0440\u043E\u0435 \u0443\u0442\u0440\u043E", icon: "\u{1F305}" },
  { id: "eralash", label: "\u0415\u0440\u0430\u043B\u0430\u0448", icon: "\u{1F3AC}" },
  { id: "zakadrovyy-smeh", label: "\u0417\u0430\u043A\u0430\u0434\u0440\u043E\u0432\u044B\u0439 \u0441\u043C\u0435\u0445", icon: "\u{1F639}" },
  { id: "zarezhu", label: "\u0417\u0430\u0440\u0435\u0436\u0443", icon: "\u{1F52A}" },
  { id: "zdarova", label: "\u0417\u0434\u0430\u0440\u043E\u0432\u0430", icon: "\u{1F624}" },
  { id: "kazino", label: "\u041A\u0430\u0437\u0438\u043D\u043E", icon: "\u{1F3B0}" },
  { id: "kak-dela", label: "\u041A\u0430\u043A \u0434\u0435\u043B\u0430?", icon: "\u{1F642}" },
  { id: "kurva", label: "\u041A\u0443\u0440\u0432\u0430", icon: "\u{1F92C}" },
  { id: "lya-ty-krysa", label: "\u041B\u044F, \u0442\u044B \u043A\u0440\u044B\u0441\u0430!", icon: "\u{1F400}" },
  { id: "murlok", label: "\u041C\u0443\u0440\u043B\u043E\u043A", icon: "\u{1F41F}" },
  { id: "myaso", label: "\u041C\u044F\u0441\u043E", icon: "\u{1F969}" },
  { id: "na-nas-napali", label: "\u041D\u0430 \u043D\u0430\u0441 \u043D\u0430\u043F\u0430\u043B\u0438!", icon: "\u2694\uFE0F" },
  { id: "zachem-prishel", label: "\u0417\u0430\u0447\u0435\u043C \u044F \u043F\u0440\u0438\u0448\u0451\u043B", icon: "\u{1F629}" },
  { id: "da-net", label: "\u0414\u0430 \u043D\u0435\u0442", icon: "\u{1F645}" },
  { id: "ne-ponyala", label: "\u041D\u0435 \u043F\u043E\u043D\u044F\u043B\u0430", icon: "\u{1F615}" },
  { id: "ne-ssy", label: "\u041D\u0435 \u0434\u0440\u0435\u0439\u0444\u044C", icon: "\u{1F605}" },
  { id: "nepravda", label: "\u041D\u0435\u043F\u0440\u0430\u0432\u0434\u0430", icon: "\u{1F643}" },
  { id: "nepravilno", label: "\u041D\u0435\u043F\u0440\u0430\u0432\u0438\u043B\u044C\u043D\u043E", icon: "\u274C" },
  { id: "net-net-net", label: "\u041D\u0435\u0442-\u043D\u0435\u0442-\u043D\u0435\u0442", icon: "\u{1F645}" },
  { id: "no-delat-ne-budu", label: "\u041D\u043E \u0434\u0435\u043B\u0430\u0442\u044C \u044F \u043D\u0435 \u0431\u0443\u0434\u0443", icon: "\u{1F60F}" },
  { id: "no-eto-ne-tochno", label: "\u041D\u043E \u044D\u0442\u043E \u043D\u0435 \u0442\u043E\u0447\u043D\u043E", icon: "\u{1F914}" },
  { id: "o-chem-govoryat-vse", label: "\u041E \u0447\u0451\u043C \u0433\u043E\u0432\u043E\u0440\u044F\u0442 \u0432\u0441\u0435", icon: "\u{1F928}" },
  { id: "obidno", label: "\u041E\u0431\u0438\u0434\u043D\u043E", icon: "\u{1F61E}" },
  { id: "oy-vse", label: "\u041E\u0439 \u0432\u0441\u0451", icon: "\u{1F644}" },
  { id: "chem-to-pahnet", label: "\u0427\u0435\u043C-\u0442\u043E \u043F\u0430\u0445\u043D\u0435\u0442", icon: "\u{1F443}" },
  { id: "patsany-rebyata", label: "\u041F\u0430\u0446\u0430\u043D\u044B \u0432\u0430\u0449\u0435 \u0440\u0435\u0431\u044F\u0442\u0430", icon: "\u{1F91D}" },
  { id: "pole-chudes", label: "\u041F\u043E\u043B\u0435 \u0447\u0443\u0434\u0435\u0441", icon: "\u{1F3A1}" },
  { id: "ponyatno", label: "\u041F\u043E\u043D\u044F\u0442\u043D\u043E", icon: "\u{1F610}" },
  { id: "pohlopayu", label: "\u041F\u043E\u0445\u043B\u043E\u043F\u0430\u044E", icon: "\u{1F44F}" },
  { id: "puk", label: "\u041F\u0443\u043A", icon: "\u{1F4A8}" },
  { id: "rasskazhi", label: "\u0420\u0430\u0441\u0441\u043A\u0430\u0436\u0438", icon: "\u{1F5E3}\uFE0F" },
  { id: "sverchki", label: "\u0421\u0432\u0435\u0440\u0447\u043A\u0438", icon: "\u{1F997}" },
  { id: "skazochnyy", label: "\u0421\u043A\u0430\u0437\u043E\u0447\u043D\u044B\u0439\u2026", icon: "\u{1F921}" },
  { id: "smayliki-ne-shlesh", label: "\u0421\u043C\u0430\u0439\u043B\u0438\u043A\u0438 \u043D\u0435 \u0448\u043B\u0451\u0448\u044C", icon: "\u{1F60A}" },
  { id: "sovpadenie", label: "\u0421\u043E\u0432\u043F\u0430\u0434\u0435\u043D\u0438\u0435?", icon: "\u{1F575}\uFE0F" },
  { id: "suetu-navesti", label: "\u0421\u0443\u0435\u0442\u0443 \u043D\u0430\u0432\u0435\u0441\u0442\u0438", icon: "\u2728" },
  { id: "takoy-kakoy-est", label: "\u0422\u0430\u043A\u043E\u0439 \u043A\u0430\u043A\u043E\u0439 \u0435\u0441\u0442\u044C", icon: "\u{1F937}" },
  { id: "u-ha-ha", label: "\u0423-\u0445\u0430-\u0445\u0430!", icon: "\u{1F608}" },
  { id: "umeete-mogete", label: "\u0423\u043C\u0435\u0435\u0442\u0435 \u043C\u043E\u0433\u0451\u0442\u0435", icon: "\u{1F44D}" },
  { id: "hvatit-terpet", label: "\u0425\u0432\u0430\u0442\u0438\u0442 \u0442\u0435\u0440\u043F\u0435\u0442\u044C", icon: "\u270A" },
  { id: "chtoooo", label: "\u0427\u0442\u043E\u043E\u043E?!", icon: "\u2753" },
  { id: "shutka-smeysya", label: "\u0428\u0443\u0442\u043A\u0430, \u0441\u043C\u0435\u0439\u0441\u044F", icon: "\u{1F606}" },
  { id: "scha-mam-podozhdi", label: "\u0429\u0430, \u043C\u0430\u043C, \u043F\u043E\u0434\u043E\u0436\u0434\u0438", icon: "\u{1F644}" },
  { id: "scha-razberemsya", label: "\u0429\u0430 \u0440\u0430\u0437\u0431\u0435\u0440\u0451\u043C\u0441\u044F", icon: "\u{1F6E0}\uFE0F" },
  { id: "eto-repchik", label: "\u042D\u0442\u043E \u0440\u0435\u043F\u0447\u0438\u043A", icon: "\u{1F3A4}" },
  { id: "ya-obidelas", label: "\u042F \u043E\u0431\u0438\u0434\u0435\u043B\u0430\u0441\u044C", icon: "\u{1F624}" },
  { id: "ya-samaya-luchshaya", label: "\u042F \u0441\u0430\u043C\u0430\u044F \u043B\u0443\u0447\u0448\u0430\u044F", icon: "\u{1F308}" }
];
var SOUND_REACTION_IDS = SOUND_REACTIONS.map((s) => s.id);
var SoundReactionIdSchema = z.enum(SOUND_REACTION_IDS);
var SoundReactMessageSchema = z.object({ sound: SoundReactionIdSchema });
var RpsChallengeSchema = z.object({ targetSessionId: z.string().min(1).max(64) });
var RpsChooseSchema = z.object({
  matchId: z.string().min(1).max(64),
  choice: RpsChoiceSchema
});
var RpsDeclineSchema = z.object({ matchId: z.string().min(1).max(64) });
var BarkChallengeSchema = z.object({ targetSessionId: z.string().min(1).max(64) });
var BarkAcceptSchema = z.object({ matchId: z.string().min(1).max(64) });
var BarkDeclineSchema = z.object({ matchId: z.string().min(1).max(64) });
var BarkSampleSchema = z.object({
  matchId: z.string().min(1).max(64),
  i: z.number().int().min(0).max(BARK_MAX_SAMPLES - 1),
  v: z.number().int().min(0).max(BARK_SAMPLE_MAX)
});
var SetCosmeticsSchema = EquippedCosmeticsSchema;
var YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
var YouTubeIdSchema = z.string().regex(YOUTUBE_ID_RE);
var RADIO_QUEUE_MAX = 50;
var RadioControlSchema = z.discriminatedUnion("action", [
  /** OWNER: load a track NOW (replaces current, starts at 0, playing). */
  z.object({ action: z.literal("load"), videoId: YouTubeIdSchema }),
  /** OWNER: resume the current track. */
  z.object({ action: z.literal("play") }),
  /** OWNER: pause — the server freezes the authoritative position from its own anchor (no client value). */
  z.object({ action: z.literal("pause") }),
  /** OWNER: seek to a position (the DJ's chosen point, ms; capped at 24h to reject nonsense). */
  z.object({ action: z.literal("seek"), positionMs: z.number().int().nonnegative().max(864e5) }),
  /** OWNER: advance to the next queued track — also how the DJ's client reports ENDED / an unplayable track. */
  z.object({ action: z.literal("skip") }),
  /** OWNER: stop + clear the current track (radio goes quiet; the queue is kept). */
  z.object({ action: z.literal("stop") }),
  /** OWNER: add a track to the request queue (or start it immediately if nothing is playing). */
  z.object({ action: z.literal("enqueue"), videoId: YouTubeIdSchema }),
  /** OWNER: remove a queued track by index. */
  z.object({ action: z.literal("dequeue"), index: z.number().int().nonnegative() }),
  /** ANY listener: a clock-sync probe (`t` = client time); the server echoes RADIO_CLOCK to derive offset. */
  z.object({ action: z.literal("sync"), t: z.number().nonnegative() }),
  /** ANY listener: an emoji reaction to the CURRENT track (floats on the Now-Playing card, not over a duck). */
  z.object({ action: z.literal("react"), reaction: ReactionSchema })
]);
function parseYouTubeId(input) {
  const s = input.trim();
  if (YOUTUBE_ID_RE.test(s)) return s;
  const patterns = [
    /[?&]v=([A-Za-z0-9_-]{11})/,
    // watch?v=ID (incl. music.youtube.com)
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    // youtu.be/ID
    /\/embed\/([A-Za-z0-9_-]{11})/,
    // /embed/ID
    /\/shorts\/([A-Za-z0-9_-]{11})/,
    // /shorts/ID
    /\/v\/([A-Za-z0-9_-]{11})/
    // /v/ID
  ];
  for (const re of patterns) {
    const id = s.match(re)?.[1];
    if (id) return id;
  }
  return null;
}
var ServerMessage = {
  PONG: "p",
  /** Another player played a one-shot emote (so we can trigger the animation locally). */
  EMOTE: "e",
  /** A celebration moment to play in the world (confetti + reactions near the subject). */
  CELEBRATE: "C",
  /** Private reply to a GIVE_REPUTATION attempt: did it land, and your remaining budget / why not. */
  REPUTATION_RESULT: "R",
  /** Server rejected/clamped something — useful for debugging and anti-cheat telemetry. */
  CORRECTION: "c",
  /** Someone said something — render a speech bubble over their duck (M7). */
  SAID: "Y",
  /** Someone sent an emoji reaction — float it up from their duck (M7). */
  REACTED: "J",
  /** Someone played a sound reaction — play that clip (`/sounds/<id>.mp3`) for everyone present (M7a). */
  SOUND_REACTED: "O",
  /** You've been challenged to Rock-Paper-Scissors — show the invite (M7). */
  RPS_INVITE: "G",
  /** Your challenge was accepted into a match — start choosing (M7). Sent to the CHALLENGER so they
   * get the server-minted matchId + a choose surface (the target gets RPS_INVITE instead).
   * Multi-char token on purpose: a bare 'S' was swallowed in transit, so use an unambiguous id. */
  RPS_STARTED: "rpsStart",
  /** A match you're in resolved (or ended) — show the result (M7). Sent to both players. */
  RPS_RESULT: "H",
  /**
   * BARK BATTLE one-shots (Challenge game #2 — domain/bark.ts). Multi-char tokens on purpose:
   *  - BARK_INVITE  → the challenged duck: show the accept/decline prompt.
   *  - BARK_PENDING → the challenger: your invite is out, here's the server-minted matchId.
   *  - BARK_START   → both ducks on accept: shared countdown, then the window opens.
   *  - BARK_TICK    → both ducks ~10×/s during the window: the authoritative running totals.
   *  - BARK_RESULT  → both ducks: the ending from each one's own POV (win/lose/draw/declined/expired).
   */
  BARK_INVITE: "barkInv",
  BARK_PENDING: "barkPend",
  BARK_START: "barkGo",
  BARK_TICK: "barkTick",
  BARK_RESULT: "barkRes",
  /**
   * A cooperative OFFICE QUEST you took part in just fired (gamified-XP redesign) — show the
   * "✨ {quest} · +{xp} XP" toast. Sent PRIVATELY to each participant (the server attests one grant per
   * participant), so the client never has to infer its own reward. The matching guild-wide CELEBRATE
   * 'quest_completed' broadcast handles the world-reacts-together confetti for the headline raids.
   */
  QUEST_FIRED: "Q",
  /** Private reply to a radio `sync` probe (ADR-0021): echoes the client's `t` + the server's `serverNow`
   * so the client can derive its clock offset (RTT/2) and extrapolate the shared playback position. */
  RADIO_CLOCK: "k",
  /** Someone reacted to the current radio track (ADR-0021) — float their emoji on the Now-Playing card. */
  RADIO_REACTED: "B",
  /** Private reply to a MEDIA HUB `sync` probe (ADR-0033) — same clock-offset handshake as RADIO_CLOCK,
   * but for the per-zone hub. Echoes the client's `t` + the server's `serverNow`. */
  MEDIA_CLOCK: "mediaClk",
  /** Someone reacted on the zone's Media Hub screen (ADR-0033) — float their emoji on the player. Carries
   * the `zoneId` so a client only renders reactions for the hub it's currently watching. */
  MEDIA_REACTED: "mediaRx",
  /**
   * Entryway front-door signals (ADR-0022). One-shot events the client renders as a sound (+ a gentle
   * cue): BELL_RANG goes to EVERYONE (the chime carries across the whole office); KNOCKED goes only to
   * the sessions the server found NEAR the door (a soft, local sound). The door's open/closed state
   * itself rides the synced schema (`entryDoorOpen`), not a message. (BELL_RANG uses 'L' — 'B' is the
   * radio's RADIO_REACTED.)
   */
  BELL_RANG: "L",
  KNOCKED: "N",
  /** Private reply to a PLACE_FURNITURE attempt: did it land, the placer's new coin balance, or why not
   * (e.g. "Not enough coins", room full). The placed piece itself rides the synced schema (`furniture`). */
  FURNITURE_RESULT: "F",
  /** A duck's HP hit zero in the fire (ADR-0052) — everyone present plays the festive piñata "burst" VFX
   * at that spot. One-shot, broadcast to all; the server has already respawned the duck (synced schema).
   * Pure slapstick joy, never a metric/leaderboard (PHILOSOPHY.md). */
  DUCK_BURST: "Bx"
};
var FurnitureResultSchema = z.object({
  ok: z.boolean(),
  /** Placer's remaining coins after a successful place (so the HUD updates without a refetch). */
  coins: z.number().int().optional(),
  /** Human-readable explanation when `ok` is false. */
  reason: z.string().optional()
});
var QuestFiredSchema = z.object({
  quest: OfficeQuestIdSchema,
  xp: z.number().int().nonnegative()
});
var BellRangBroadcastSchema = z.object({
  sessionId: z.string(),
  /** The ringer's display name, so the cue can read "Ada is at the door". */
  name: z.string().max(80).optional()
});
var KnockedBroadcastSchema = z.object({
  sessionId: z.string(),
  name: z.string().max(80).optional()
});
var PongMessageSchema = z.object({ t: z.number() });
var EmoteBroadcastSchema = z.object({
  sessionId: z.string(),
  animation: AnimationSchema
});
var SaidBroadcastSchema = z.object({
  sessionId: z.string(),
  text: z.string().max(SAY_MAX_LEN)
});
var ReactedBroadcastSchema = z.object({
  sessionId: z.string(),
  reaction: ReactionSchema
});
var SoundReactedBroadcastSchema = z.object({
  sessionId: z.string(),
  sound: SoundReactionIdSchema
});
var RpsInviteSchema = z.object({
  matchId: z.string(),
  fromSessionId: z.string(),
  fromName: z.string()
});
var RpsStartedSchema = z.object({
  matchId: z.string(),
  opponentSessionId: z.string(),
  opponentName: z.string()
});
var RpsResultSchema = z.object({
  matchId: z.string(),
  outcome: RpsOutcomeSchema,
  opponentName: z.string(),
  yourChoice: RpsChoiceSchema.optional(),
  theirChoice: RpsChoiceSchema.optional()
});
var BarkInviteSchema = z.object({
  matchId: z.string(),
  fromSessionId: z.string(),
  fromName: z.string()
});
var BarkPendingSchema = z.object({
  matchId: z.string(),
  opponentSessionId: z.string(),
  opponentName: z.string()
});
var BarkStartSchema = z.object({
  matchId: z.string(),
  opponentSessionId: z.string(),
  opponentName: z.string(),
  countdownMs: z.number().int().nonnegative(),
  durationMs: z.number().int().positive()
});
var BarkTickSchema = z.object({
  matchId: z.string(),
  /** ms left in the barking window on the SERVER clock (display-only). */
  remainingMs: z.number().int().nonnegative(),
  yourScore: z.number().int().nonnegative(),
  theirScore: z.number().int().nonnegative()
});
var BarkResultSchema = z.object({
  matchId: z.string(),
  outcome: BarkOutcomeSchema,
  opponentName: z.string(),
  yourScore: z.number().int().nonnegative().optional(),
  theirScore: z.number().int().nonnegative().optional()
});
var CelebrationBroadcastSchema = z.object({
  sessionId: z.string(),
  kind: CelebrationKindSchema,
  text: z.string().optional()
});
var ReputationResultSchema = z.object({
  ok: z.boolean(),
  /** Session the giver aimed at (so the client can correlate the reply to the gesture). */
  targetSessionId: z.string(),
  /** Giver's remaining daily budget after a successful grant. */
  budgetRemaining: z.number().int().optional(),
  /** Human-readable explanation when `ok` is false. */
  reason: z.string().optional()
});
var RadioClockSchema = z.object({ t: z.number(), serverNow: z.number() });
var RadioReactedBroadcastSchema = z.object({
  sessionId: z.string(),
  reaction: ReactionSchema
});
var JoinOptionsSchema = z.object({
  accessToken: z.string().min(10),
  /**
   * The workspace whose office to join. Used as the matchmaking partition key so each tenant
   * gets its own room. The server rejects the join unless this matches the token's claim, so it
   * cannot be used to enter another company's office.
   */
  workspaceId: z.string().uuid(),
  duckColor: DuckColorSchema.default("yellow"),
  /** The citizen's chosen ANIMAL (ADR-0024). Per-player (NOT owner-gated like `theme`); defaults to the
   * original duck so a legacy/absent value still renders the beloved chick. */
  species: DuckSpeciesSchema.default("duck"),
  /** The guild's world theme (M8) — the client passes its workspace's chosen theme so the room skins
   * the world for everyone (and at boot it ALSO selects the LEVEL geometry: medieval_castle → the Castle
   * level, anything else → the default Office level). Cosmetic + guild-wide; defaults to the Office. */
  theme: WorldThemeIdSchema.default("office")
});
var MEDIA_SOURCE_KINDS = ["youtube", "screenshare"];
var MediaSourceKindSchema = z.enum(MEDIA_SOURCE_KINDS);
var QUEUEABLE_SOURCE_KINDS = ["youtube"];
var MEDIA_REF_VALIDATORS = {
  youtube: (ref) => YOUTUBE_ID_RE.test(ref) ? ref : null,
  // A screen share's ref is the host's session id; the room sets it server-side from the live socket,
  // never from client input, so any non-empty token is structurally fine here.
  screenshare: (ref) => ref.length > 0 && ref.length <= 64 ? ref : null
};
function resolveMediaInput(input) {
  const yt = parseYouTubeId(input);
  if (yt) return { kind: "youtube", ref: yt };
  return null;
}
var MEDIA_QUEUE_MAX = 50;
var MEDIA_QUEUE_MAX_PER_USER = 10;
var MEDIA_HISTORY_MAX = 20;
var MEDIA_TITLE_MAX = 200;
var SCREENSHARE_BITRATE_TIERS = [
  { maxViewers: 5, bitrate: 25e5 },
  { maxViewers: 15, bitrate: 15e5 },
  { maxViewers: 30, bitrate: 1e6 },
  { maxViewers: Number.POSITIVE_INFINITY, bitrate: 7e5 }
];
function screenshareMaxBitrate(viewerCount) {
  const n = Math.max(0, viewerCount);
  for (const tier of SCREENSHARE_BITRATE_TIERS) if (n <= tier.maxViewers) return tier.bitrate;
  return SCREENSHARE_BITRATE_TIERS[SCREENSHARE_BITRATE_TIERS.length - 1].bitrate;
}
var MAX_CONCURRENT_SCREENSHARES_PER_ROOM = 5;
var MediaEnqueueInputSchema = z.object({
  kind: z.enum(QUEUEABLE_SOURCE_KINDS),
  ref: z.string().min(1).max(256),
  title: z.string().max(MEDIA_TITLE_MAX).optional()
});
var MediaControlSchema = z.discriminatedUnion("action", [
  // ── Open to anyone in the zone ──
  /** Add an item to this zone's queue (or start it immediately if the hub is idle). */
  z.object({ action: z.literal("enqueue"), item: MediaEnqueueInputSchema }),
  /** Float an emoji reaction on the screen (not over a duck) — celebratory, ephemeral. */
  z.object({ action: z.literal("react"), reaction: ReactionSchema }),
  /** Clock-sync probe (`t` = client time); the server echoes MEDIA_CLOCK so the client derives offset. */
  z.object({ action: z.literal("sync"), t: z.number().nonnegative() }),
  /** Take the host seat — succeeds only when vacant (no present host), or always for the guild OWNER. */
  z.object({ action: z.literal("claimHost") }),
  // ── Host only ──
  /** Resume the current item. */
  z.object({ action: z.literal("play") }),
  /** Pause — the server freezes the authoritative position from its own anchor (no client value). */
  z.object({ action: z.literal("pause") }),
  /** Advance to the next queued item (also how the host's client reports the current item ENDED). */
  z.object({ action: z.literal("next") }),
  /** Seek the current (time-based) item to a position (ms, capped at 24h). */
  z.object({ action: z.literal("seek"), positionMs: z.number().int().nonnegative().max(864e5) }),
  /** Jump straight to a specific queued item by id (pulls it out of line and plays it now). */
  z.object({ action: z.literal("playNow"), itemId: z.string().min(1).max(64) }),
  /** Remove a queued item by id. */
  z.object({ action: z.literal("remove"), itemId: z.string().min(1).max(64) }),
  /** Reorder: move a queued item to a new index in the line. */
  z.object({
    action: z.literal("reorder"),
    itemId: z.string().min(1).max(64),
    toIndex: z.number().int().nonnegative().max(MEDIA_QUEUE_MAX)
  }),
  /** Clear the whole queue (the now-playing item is untouched). */
  z.object({ action: z.literal("clear") }),
  /** Release the host seat back to vacant (anyone may then claim it). */
  z.object({ action: z.literal("releaseHost") }),
  /** Begin a screen share: the host's screen becomes the now-playing item for the zone. */
  z.object({ action: z.literal("startScreenshare") }),
  /** Stop the screen share and fall back to the queue (or idle). */
  z.object({ action: z.literal("stopScreenshare") })
]);
var MediaClockSchema = z.object({ t: z.number(), serverNow: z.number() });
var MediaReactedBroadcastSchema = z.object({
  zoneId: z.string(),
  sessionId: z.string(),
  reaction: ReactionSchema
});
var MEDIA_SOURCES = ["mic", "cam", "screen"];
var MediaSourceSchema = z.enum(MEDIA_SOURCES);
var VoiceClientMessage = {
  /** A voice RPC request (see {@link VoiceRpcRequestSchema}). */
  RPC: "voice:rpc"
};
var VoiceServerMessage = {
  /** Reply to a {@link VoiceClientMessage.RPC} request, matched by `id`. */
  RPC_RESULT: "voice:result",
  /** A peer just became audible to you (in range / same zone) and is producing — go consume it. */
  NEW_PRODUCER: "voice:newProducer",
  /** A producer you were consuming is gone (peer left range/zone/disconnected) — drop the consumer. */
  PRODUCER_CLOSED: "voice:producerClosed",
  /** Server-authoritative active-speaker set (from mediasoup's AudioLevelObserver) for indicators. */
  ACTIVE_SPEAKERS: "voice:activeSpeakers"
};
var rpcBase = { id: z.string().min(1).max(64) };
var VoiceRpcRequestSchema = z.discriminatedUnion("method", [
  /** Fetch the room router's RTP capabilities (to load the client Device). */
  z.object({ ...rpcBase, method: z.literal("getRtpCapabilities") }),
  /** Ask the server to create a WebRTC transport for sending or receiving. */
  z.object({ ...rpcBase, method: z.literal("createTransport"), direction: z.enum(["send", "recv"]) }),
  /** Hand the server our local DTLS parameters to finish the ICE+DTLS connect. */
  z.object({
    ...rpcBase,
    method: z.literal("connectTransport"),
    transportId: z.string().min(1),
    dtlsParameters: z.unknown()
  }),
  /**
   * Start producing a track on our send transport; server replies with the producer id. `source` says
   * which channel it is (ADR-0030/0033): omitted ⇒ inferred (`audio`→mic, `video`→cam) so existing
   * mic/cam clients are unchanged; `'screen'` marks a SCREEN SHARE (zone-broadcast, screen-audio kept
   * off the speaking detector). A session may hold a mic + a cam + a screen-video + a screen-audio at once.
   */
  z.object({
    ...rpcBase,
    method: z.literal("produce"),
    transportId: z.string().min(1),
    kind: z.enum(["audio", "video"]),
    source: MediaSourceSchema.optional(),
    rtpParameters: z.unknown()
  }),
  /** Consume an audible peer's producer on our recv transport. */
  z.object({
    ...rpcBase,
    method: z.literal("consume"),
    producerId: z.string().min(1),
    rtpCapabilities: z.unknown()
  }),
  /** Resume a consumer (consumers are created paused, then resumed once the client is ready). */
  z.object({ ...rpcBase, method: z.literal("resumeConsumer"), consumerId: z.string().min(1) }),
  /**
   * Close one of MY OWN producers outright (e.g. turning the webcam off while staying connected). The
   * server closes the server-side producer, which fires `producerclose` on every listener's consumer —
   * so a face bubble disappears the instant the camera is switched off (ADR-0030). Distinct from
   * `setProducerPaused`, which only pauses (a paused video would freeze, not vanish).
   */
  z.object({ ...rpcBase, method: z.literal("closeProducer"), producerId: z.string().min(1) }),
  /** Mute / unmute: pause or resume our own audio producer (instant, client-driven). */
  z.object({
    ...rpcBase,
    method: z.literal("setProducerPaused"),
    producerId: z.string().min(1),
    paused: z.boolean()
  })
]);
var NewProducerSchema = z.object({
  /** The peer's room session id (so the client can spatialize this stream to that duck). */
  peerSessionId: z.string(),
  producerId: z.string(),
  kind: z.enum(["audio", "video"]),
  /** How to render it (ADR-0030/0033): 'mic' → spatial voice, 'cam' → face bubble, 'screen' → the zone's
   *  Media Hub screen (+ screen-audio to the media gain). Omitted ⇒ inferred from `kind` for old clients. */
  source: MediaSourceSchema.optional()
});
var ProducerClosedSchema = z.object({
  producerId: z.string()
});
var ActiveSpeakersSchema = z.object({
  /** Session ids currently above the speaking threshold (for the on-duck speaking indicator). */
  sessionIds: z.array(z.string())
});
function resolveEffectiveMute({ manualMuted, pttEnabled, pttTalking }) {
  return pttEnabled ? !pttTalking : manualMuted;
}
function isDeliberatelyMuted({
  manualMuted,
  pttEnabled
}) {
  return pttEnabled ? false : manualMuted;
}

// src/realtime/chat-links.ts
var TRAILING_PUNCT = /[)\].,!?;:'"»…]+$/;
var URL_CANDIDATE = /(?:https?:\/\/|www\.)[^\s]+/gi;
function safeHttpUrl(candidate) {
  const withScheme = /^www\./i.test(candidate) ? `https://${candidate}` : candidate;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return parsed.href;
  } catch {
    return null;
  }
}
function parseChatSegments(text) {
  const out = [];
  let last = 0;
  for (const match of text.matchAll(URL_CANDIDATE)) {
    const start = match.index ?? 0;
    let raw = match[0];
    let trailing = "";
    const punct = TRAILING_PUNCT.exec(raw);
    if (punct) {
      trailing = punct[0];
      raw = raw.slice(0, raw.length - trailing.length);
    }
    if (start > last) out.push({ kind: "text", value: text.slice(last, start) });
    const href = raw ? safeHttpUrl(raw) : null;
    if (href) out.push({ kind: "link", href, label: raw });
    else if (raw) out.push({ kind: "text", value: raw });
    if (trailing) out.push({ kind: "text", value: trailing });
    last = start + match[0].length;
  }
  if (last < text.length) out.push({ kind: "text", value: text.slice(last) });
  return out.length > 0 ? out : [{ kind: "text", value: text }];
}

export { ACHIEVEMENTS, ACTIVITY_STATES, ANIMATION_STATES, AcceptGuestInviteSchema, AcceptInviteSchema, AccessTokenClaimsSchema, ActiveSpeakersSchema, ActivitySchema, AnimationSchema, BARK_COUNTDOWN_MS, BARK_DURATION_MS, BARK_INVITE_TTL_MS, BARK_LATE_GRACE_MS, BARK_MAX_SAMPLES, BARK_MAX_SCORE, BARK_OUTCOMES, BARK_SAMPLE_INTERVAL_MS, BARK_SAMPLE_MAX, BARK_TICK_INTERVAL_MS, BarkAcceptSchema, BarkChallengeSchema, BarkDeclineSchema, BarkInviteSchema, BarkOutcomeSchema, BarkPendingSchema, BarkResultSchema, BarkSampleSchema, BarkStartSchema, BarkTickSchema, BellRangBroadcastSchema, BlockCitizenSchema, CELEBRATION_KINDS, COINS_DAILY_EARN_CAP, COSMETICS, COSMETIC_SLOTS, CelebrateMessageSchema, CelebrationBroadcastSchema, CelebrationKindSchema, ClientMessage, CosmeticSlotSchema, CreateInviteSchema, CreateQuestSchema, CreateWorkspaceSchema, DECOR_KINDS, DEFAULT_BOUNDS, DEFAULT_DUCK_APPEARANCE, DEFAULT_DUCK_SPECIES, DEFAULT_VAD_PARAMS, DOOR_REACH, DUCK_COLORS, DUCK_SPECIES, DecorKindSchema, DisplayNameSchema, DuckAppearanceSchema, DuckColorSchema, DuckSpeciesSchema, ENTRYWAY_ZONE_ID, ENTRY_BELL, ENTRY_BELL_REACH, ENTRY_DOORWAY_ID, ENTRY_DOOR_OPEN_MS, ENTRY_DOOR_REACH, ENTRY_DOOR_SLAB, ENTRY_DOOR_THRESHOLD, ENTRY_KNOCK_HEARING_RANGE, EmailSchema, EmoteBroadcastSchema, EmoteMessageSchema, EquipCosmeticSchema, EquippedCosmeticsSchema, FIRE_HAZARDS, FURNITURE_CATALOG, FURNITURE_CATEGORIES, FURNITURE_INTERACT_RANGE, FURNITURE_RATE_LIMIT, FingerprintSchema, FurnitureResultSchema, GATHER_DURATION_MS, GATHER_QUORUM, GiveReputationIntentSchema, GiveReputationSchema, HEALTH, HELD_ITEMS, HELD_ITEM_IDS, HeldItemIdSchema, HeldItemValueSchema, InteractMessageSchema, InternalGrantActionSchema, InternalGrantParticipationSchema, InternalGrantQuestSchema, InternalGrantReputationSchema, InternalMoveFurnitureSchema, InternalPlaceFurnitureSchema, InternalRemoveFurnitureSchema, InviteCreatedSchema, InvitePreviewSchema, JoinOptionsSchema, KnockMessageSchema, KnockedBroadcastSchema, LEADERBOARD_KINDS, LEADERBOARD_LABELS, LEGACY_FREE_BUTTON_ACTIONS, LEVELS, LEVEL_DOCUMENT_VERSION, LEVEL_THEME_IDS, LIMITS, LeaderboardKindSchema, LevelDocumentSchema, LobbyJoinSchema, LoginNameSchema, LoginSchema, MAX_CONCURRENT_SCREENSHARES_PER_ROOM, MAX_FURNITURE_PER_ROOM, MEDIA_HISTORY_MAX, MEDIA_QUEUE_MAX, MEDIA_QUEUE_MAX_PER_USER, MEDIA_REF_VALIDATORS, MEDIA_SOURCES, MEDIA_SOURCE_KINDS, MEDIA_TITLE_MAX, MEETING_DURATION_MS, MEETING_SEATS, MEETING_SEAT_COUNT, MEETING_SEAT_IDS, MEETING_TABLE, MEETING_XP_PER_SECOND, MediaClockSchema, MediaControlSchema, MediaEnqueueInputSchema, MediaReactedBroadcastSchema, MediaSourceKindSchema, MediaSourceSchema, MoveFurnitureMessageSchema, MoveIntentSchema, NEIGHBOR_PRESENCE_ALPHA, NEIGHBOR_VOICE_GAIN, NEWCOMER_WINDOW_MS, NewProducerSchema, OBJECT_INTERACT_RANGE, OFFICE_DECOR, OFFICE_DOORS, OFFICE_DOORWAYS, OFFICE_OBJECTS, OFFICE_QUESTS, OFFICE_QUEST_DAILY_XP_CAP, OFFICE_SEATS, OFFICE_WALLS, OFFICE_ZONES, OPEN_FLOOR_NAME, OfficeQuestIdSchema, OpenDoorMessageSchema, PARTICIPATION_FLUSH_MS, PARTICIPATION_XP_DAILY_CAP, PARTICIPATION_XP_PER_MINUTE, PERSONAL_VOICE_MAX, PERSONAL_VOICE_MIN, POSITIVE_ACTIONS, POSITIVE_ACTION_XP_DAILY_CAP, PRESENCE_FLAVORS, PRESENCE_LABELS, PasswordSchema, PingMessageSchema, PlaceFurnitureMessageSchema, PongMessageSchema, PositiveActionSchema, PresenceFlavorSchema, ProducerClosedSchema, PurchaseCosmeticSchema, QUEST_STATES, QUEUEABLE_SOURCE_KINDS, QuestFiredSchema, QuestStateSchema, RADIO_QUEUE_MAX, REACTIONS, REPUTATION_DAILY_BUDGET, REPUTATION_DEFAULT_GRANT, REPUTATION_PER_PAIR_DAILY_CAP, ROLE_ORDER, RPS_CHOICES, RPS_CHOICE_LABELS, RPS_OUTCOMES, RadioClockSchema, RadioControlSchema, RadioReactedBroadcastSchema, ReactMessageSchema, ReactedBroadcastSchema, ReactionSchema, RecordActionSchema, RefreshSchema, RegisterAccountSchema, RegisterWorkspaceSchema, RemoveFurnitureMessageSchema, ReputationResultSchema, RingBellMessageSchema, RoleSchema, RoomPasswordSchema, RpsChallengeSchema, RpsChoiceSchema, RpsChooseSchema, RpsDeclineSchema, RpsInviteSchema, RpsOutcomeSchema, RpsResultSchema, RpsStartedSchema, SAY_MAX_LEN, SCREENSHARE_BITRATE_TIERS, SEAT_INTERACT_RANGE, SIM, SOCIAL, SOUND_REACTIONS, SOUND_REACTION_IDS, SPECIES_LIST, SPECIES_META, STANDUP_DURATION_MS, STANDUP_QUORUM, SaidBroadcastSchema, SayMessageSchema, ServerMessage, SetCosmeticsSchema, SetHeldSchema, SetLeaderboardOptInSchema, SetPresenceSchema, SetWorldThemeSchema, ShipReleaseSchema, SitMessageSchema, SoundReactMessageSchema, SoundReactedBroadcastSchema, SoundReactionIdSchema, ToggleDoorMessageSchema, UpdateAppearanceSchema, UpdateDisplayNameSchema, UpdateWorkspaceSchema, VOICE, VoiceClientMessage, VoiceRpcRequestSchema, VoiceServerMessage, VoiceStateSchema, WALL_THICKNESS, WEATHER_KINDS, WELCOME_COINS, WORKSTATION_SEAT_IDS, WORK_COOLDOWN_MS, WORLD_EVENT_KINDS, WORLD_EVENT_LABELS, WORLD_THEMES, WORLD_THEME_IDS, WeatherSchema, WorkMessageSchema, WorkspaceSchema, WorldEventKindSchema, WorldThemeIdSchema, YOUTUBE_ID_RE, YouTubeIdSchema, barkOutcome, barkPointsFromLevel, blankLevel, buildDoorways, buildLevel, buildRoomPath, buildWalls, canHearAcrossZones, canKnockEntryDoor, canOpenEntryDoor, canRingEntryBell, canToggleDoor, clamp, clampBarkSample, clampInputVector, clampPersonalVoice, closedDoorSlabs, composeWalls, connectDoors, cosmeticById, cosmeticsBySlot, dayPhase, daylight, decideVad, doorwayById, entryPredicates, fireHazardAt, fireHazardAtIn, frameRms, furnitureItemById, furnitureObjectKind, groupOccupantsByRoom, guildLevel, heldItemById, isDeliberatelyMuted, isHeldItemValue, isInEntryway, isInFire, isSittableFurniture, isWorkstationSeat, isZoneEnclosed, layoutFromLevel, levelForTheme, levelHasErrors, mayOpenHeldDoor, nearestDoorway, nearestObject, nearestSeat, objectById, objectEffectForKind, officeLevel, officeQuestById, officeWalls, parseChatSegments, parseLevel, parseYouTubeId, personalVoiceGain, resolveEffectiveMute, resolveMediaInput, resolveWallCollision, resolveWallCollisionIn, roleAtLeast, roleRank, rpsOutcome, safeHttpUrl, screenshareMaxBitrate, seatById, serializeLevel, validateLevel, voicingScore, withinBellReach, withinDoorReach, withinInteractRange, withinKnockHearing, withinSeatRange, worldTheme, xpLevel, zoneAt, zoneAtIn, zoneVoiceGain, zoneVoiceGainIn, zonesAreNeighbors, zonesAreNeighborsIn, zonesConnectedOpen, zonesConnectedOpenIn };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map